import { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import CategoryControls from './components/CategoryControls';
import CategoryPanel from './components/CategoryPanel';
import OrdskjonnhetDraft from './components/OrdskjonnhetDraft';
import OrdskjonnhetPublic from './components/OrdskjonnhetPublic';
import NewsSection from './components/NewsSection';
import RadioSection from './components/RadioSection';
import NewCategoryForm from './components/NewCategoryForm';
import SentenceBar from './components/SentenceBar';
import TextAnalyzer from './components/TextAnalyzer';
import BackupRestoreModal from './components/BackupRestoreModal';
import WordLearningMode from './components/WordLearningMode';
import DialogRecorder from './components/DialogRecorder';
import ProfilePopover from './components/ProfilePopover';
import ElephantBadgeIcon from './components/ElephantBadgeIcon';
import { LESSONS, OVERVIEW_STEPS } from './data/lessons';
import LessonOverlay from './components/LessonOverlay';
import LessonVideo from './components/LessonVideo';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from './supabaseClient';
import {
  LEGACY_DATA_KEYS,
  STORAGE_KEYS,
  canonicalCategoryAliases,
  canonicalCategoryIds,
  defaultColors,
  fallbackDb,
  grammarIconCatalog,
  grammarIconParts,
} from './data/defaultData';
import { LEVEL_ORDER, getPhotoForLevel, buildLevels, applyWordLevel } from './data/wordLevels';

const mojibakeNames = {
  'РЎСѓР±СЉРµРєС‚': 'Субъект',
  'РњРµСЃС‚РѕРёРјРµРЅРёСЏ': 'Местоимения',
  'Р“Р»Р°РіРѕР»': 'Глагол',
  'Р”РµР№СЃС‚РІРёРµ': 'Действие',
  'РћР±СЃС‚РѕСЏС‚РµР»СЊСЃС‚РІРѕ': 'Обстоятельство',
};

const WORDS_KEY = '__words';
const ORDER_KEY = '__order';
const PATH_SEPARATOR = '\u001F';
const ANALYZER_DECK_CATEGORY = 'Из анализатора';
const MAX_PUBLIC_PHRASES_PER_DAY = 3;
const MAX_PHRASE_WORDS = 40;

// Оборачивает промис тайм-аутом, чтобы зависший запрос к Supabase
// не блокировал работу приложения (защита от белого экрана при старте).
const withTimeout = (promise, ms, fallback) => {
  return Promise.race([
    promise,
    new Promise((resolve) => window.setTimeout(() => resolve(fallback), ms)),
  ]);
};
const MAX_PHRASE_CHUNKS = 3;
const AUTHOR_MOVE_PAUSE_MS = 10 * 60 * 1000;

function pathToArray(path) {
  return path ? path.split(PATH_SEPARATOR) : [];
}

function arrayToPath(path) {
  return path.join(PATH_SEPARATOR);
}

function normalizeNode(node) {
  if (Array.isArray(node)) return { [WORDS_KEY]: node };
  return { ...(node || {}) };
}

function getNodeAtPath(categoryNode, path) {
  return pathToArray(path).reduce((node, part) => {
    const normalized = normalizeNode(node);
    return normalized[part];
  }, categoryNode);
}

function getWordsFromNode(node) {
  return Array.isArray(node) ? node : node?.[WORDS_KEY] || [];
}

function evikContentSig(word) {
  const w = word || {};
  const ord = w.ord || {};
  const ordContent = { ...ord };
  ['evik', 'evikSourceNorm', 'evikSnapshot', 'ownerNick', 'authored', 'fullnessDate', 'photoReady'].forEach((k) => { delete ordContent[k]; });
  const obj = {
    text: w.text || '',
    capitalized: !!w.capitalized,
    translations: w.translations || [],
    synonyms: w.synonyms || [],
    antonyms: w.antonyms || [],
    adjectives: w.adjectives || [],
    root: w.root || '',
    roots: w.roots || [],
    forms: w.forms || null,
    manualForms: w.manualForms || null,
    story: w.story || '',
    ord: ordContent,
  };
  return JSON.stringify(obj);
}

function forEachWordInDb(database, callback) {
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    getWordsFromNode(node).forEach((w) => callback(w));
    Object.keys(node).forEach((key) => {
      if (key === WORDS_KEY || key === ORDER_KEY) return;
      walk(node[key]);
    });
  };
  Object.keys(database || {}).forEach((cat) => walk(database[cat]));
}

function countWordsInNode(node) {
  let count = 0;
  const walk = (n) => {
    if (!n || typeof n !== 'object') return;
    count += getWordsFromNode(n).length;
    Object.keys(n).forEach((key) => {
      if (key === WORDS_KEY || key === ORDER_KEY) return;
      walk(n[key]);
    });
  };
  walk(node);
  return count;
}

function findWordLocation(database, text) {
  const target = String(text == null ? '' : text).trim().toLowerCase();
  if (!target) return null;
  let found = null;
  const cats = Object.keys(database || {});
  for (let i = 0; i < cats.length && !found; i += 1) {
    const cat = cats[i];
    const walk = (node, pathArr) => {
      if (found || !node || typeof node !== 'object') return;
      const words = getWordsFromNode(node);
      const idx = words.findIndex((w) => String((w && w.text) || '').trim().toLowerCase() === target);
      if (idx !== -1) { found = { category: cat, subPath: arrayToPath(pathArr), wordIndex: idx }; return; }
      Object.keys(node).forEach((key) => {
        if (found || key === WORDS_KEY || key === ORDER_KEY) return;
        walk(node[key], pathArr.concat(key));
      });
    };
    walk(database[cat], []);
  }
  return found;
}

// Перебор всех фото слова: зеркало w.photo и фото на каждом уровне w.levels[*].photo.
function eachWordPhoto(w, fn) {
  if (!w) return;
  if (w.photo) { const r = fn(w.photo); if (r !== undefined) w.photo = r; }
  if (w.levels && typeof w.levels === 'object') {
    Object.keys(w.levels).forEach((lv) => {
      const slot = w.levels[lv];
      if (slot && slot.photo) { const r = fn(slot.photo); if (r !== undefined) slot.photo = r; }
    });
  }
  if (w.ord && typeof w.ord === 'object' && w.ord.photo) {
    const r = fn(w.ord.photo); if (r !== undefined) w.ord.photo = r;
  }
}

function stripPhotosForCloud(database) {
  const clone = JSON.parse(JSON.stringify(database || {}));
  forEachWordInDb(clone, (w) => {
    // Выносим ВСЕ фото (зеркало и фото уровней) с photoKey: они дублируются в word_photos
    // и возвращаются при загрузке (loadWordPhotosInto). Иначе сырые base64-фото раздувают
    // строку user_databases, и сохранение крупной базы обрывается.
    eachWordPhoto(w, (ph) => {
      if (ph && ph.dataUrl && ph.photoKey) {
        return { name: ph.name || '', type: ph.type || '', photoKey: ph.photoKey, position: ph.position };
      }
      return undefined;
    });
  });
  return clone;
}

function updateNodeAtPath(categoryNode, path, updater) {
  const parts = pathToArray(path);

  if (parts.length === 0) return updater(normalizeNode(categoryNode));

  const [head, ...tail] = parts;
  const normalized = normalizeNode(categoryNode);

  return {
    ...normalized,
    [head]: updateNodeAtPath(normalized[head], arrayToPath(tail), updater),
  };
}

function setWordsAtPath(categoryNode, path, words) {
  return updateNodeAtPath(categoryNode, path, (node) => ({
    ...normalizeNode(node),
    [WORDS_KEY]: words,
  }));
}

function hasSameBaseWord(words, candidateWord) {
  const candidateText = normalizeWordText(candidateWord?.text || '');
  if (!candidateText) return false;

  return words.some((word) => normalizeWordText(word?.text || '') === candidateText);
}

function mergeCategoryNodes(currentNode, incomingNode) {
  const current = normalizeNode(currentNode);
  const incoming = normalizeNode(incomingNode);
  const currentWords = getWordsFromNode(current);
  const incomingWords = getWordsFromNode(incoming);
  const mergedWords = [
    ...currentWords,
    ...incomingWords.filter((word) => !hasSameBaseWord(currentWords, word)),
  ];
  const mergedNode = {
    ...current,
    ...(mergedWords.length > 0 ? { [WORDS_KEY]: mergedWords } : {}),
  };

  Object.entries(incoming).forEach(([key, incomingChild]) => {
    if (key === WORDS_KEY) return;
    if (key === ORDER_KEY) {
      if (Array.isArray(incomingChild)) mergedNode[key] = [...incomingChild];
      return;
    }
    mergedNode[key] = mergeCategoryNodes(current[key], incomingChild);
  });

  return mergedNode;
}

function mergeDatabases(currentDb, incomingDb) {
  return Object.entries(incomingDb).reduce((nextDb, [category, incomingNode]) => ({
    ...nextDb,
    [category]: mergeCategoryNodes(nextDb[category], incomingNode),
  }), { ...currentDb });
}

function cloneSectionNode(node) {
  return JSON.parse(JSON.stringify(normalizeNode(node)));
}

function getSubcategoryNamesFromNode(node) {
  const norm = normalizeNode(node);
  const keys = Object.keys(norm).filter((key) => key !== WORDS_KEY && key !== ORDER_KEY);
  const order = Array.isArray(norm[ORDER_KEY]) ? norm[ORDER_KEY] : null;
  if (!order) return keys;
  const present = new Set(keys);
  const ordered = [];
  order.forEach((name) => {
    if (present.has(name)) {
      ordered.push(name);
      present.delete(name);
    }
  });
  keys.forEach((name) => { if (present.has(name)) ordered.push(name); });
  return ordered;
}

function collectSectionTargetsFromDb(db) {
  const targets = [];

  const collectSubcategories = (category, node, path = []) => {
    getSubcategoryNamesFromNode(node).forEach((subcategory) => {
      const nextPath = [...path, subcategory];
      const pathValue = arrayToPath(nextPath);
      targets.push({
        category,
        path: pathValue,
        label: [category, ...nextPath].join(' / '),
      });
      collectSubcategories(category, normalizeNode(node)[subcategory], nextPath);
    });
  };

  Object.entries(db || {}).forEach(([category, node]) => {
    targets.push({ category, path: '', label: category });
    collectSubcategories(category, node);
  });

  return targets;
}

function collectConstructorSourceTargets(db, categoryOrder = Object.keys(db || {})) {
  const targets = [];

  const collectLeafSubcategories = (category, node, path = []) => {
    const normalizedNode = normalizeNode(node);
    const subcategoryNames = getSubcategoryNamesFromNode(normalizedNode);

    if (path.length > 0 && subcategoryNames.length === 0) {
      const pathValue = arrayToPath(path);
      targets.push({
        category,
        path: pathValue,
        label: [category, ...path].join(' / '),
      });
      return;
    }

    subcategoryNames.forEach((subcategory) => {
      collectLeafSubcategories(category, normalizedNode[subcategory], [...path, subcategory]);
    });
  };

  categoryOrder.forEach((category) => {
    if (!db?.[category]) return;
    targets.push({ category, path: '', label: category });
    collectLeafSubcategories(category, db[category]);
  });

  return targets;
}

function createConstructorGroup(category, path = '', label = '') {
  const cleanPath = path || '';
  const cleanLabel = label.trim() || (cleanPath ? pathToArray(cleanPath).at(-1) : category);

  return {
    id: `constructor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    label: cleanLabel,
    category,
    path: cleanPath,
  };
}

function normalizeConstructorGroups(groups, db) {
  if (!Array.isArray(groups)) return [];

  return groups
    .map((group) => ({
      id: String(group?.id || `constructor-${Math.random().toString(36).slice(2, 8)}`),
      label: String(group?.label || '').trim(),
      category: normalizeName(String(group?.category || '')),
      path: String(group?.path || ''),
    }))
    .filter((group) => group.category && db?.[group.category])
    .map((group) => ({
      ...group,
      label: group.label || (group.path ? pathToArray(group.path).at(-1) : group.category),
    }));
}

function getUniqueSectionName(parentNode, preferredName) {
  const normalizedParent = normalizeNode(parentNode);
  if (!Object.prototype.hasOwnProperty.call(normalizedParent, preferredName)) return preferredName;

  let index = 2;
  let nextName = `${preferredName} (${index})`;
  while (Object.prototype.hasOwnProperty.call(normalizedParent, nextName)) {
    index += 1;
    nextName = `${preferredName} (${index})`;
  }

  return nextName;
}

function isSameOrChildPath(path, possibleParentPath) {
  if (!possibleParentPath) return false;
  return path === possibleParentPath || path.startsWith(`${possibleParentPath}${PATH_SEPARATOR}`);
}

function normalizeName(name) {
  return mojibakeNames[name] || name;
}

function getSubcategoryMetaKey(category, subcategory) {
  return `${category}::${subcategory}`;
}

function getCategoryMetaKey(category) {
  return `${category}::__category`;
}


function contrastTextColor(bg) {
  const hex = String(bg || '').trim().replace('#', '');
  if (hex.length < 6) return '#111827';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return '#111827';
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#111827' : '#ffffff';
}

function textColorForTheme(theme, bg) {
  if (theme === 'light') return '#ffffff';
  if (theme === 'dark') return '#111827';
  return contrastTextColor(bg);
}

function getInferredCanonicalCategoryId(category) {
  const key = normalizeName(category || '').trim().toLowerCase();
  return canonicalCategoryAliases[key] || canonicalCategoryIds[category] || null;
}

function findGrammarIcon(grammarIconCatalog, iconId) {
  const groups = Object.values(grammarIconCatalog || {});
  const iconGroups = groups.flatMap((group) => Object.values(group || {}));
  return iconGroups.flat().find((icon) => icon.id === iconId) || null;
}

const verbTenseIcons = {
  past: {
    id: 'VT_PAST',
    symbol: '👈',
    label: 'прошедшее время',
  },
  present: {
    id: 'VT_PRESENT',
    symbol: '👇',
    label: 'настоящее время',
  },
  future: {
    id: 'VT_FUTURE',
    symbol: '👉',
    label: 'будущее время',
  },
};

function normalizeWordText(text) {
  return text.trim().toLowerCase();
}

// Переключатель функции "Конструктор". false = скрыт из интерфейса (но код сохранён).
// Поменять на true, чтобы вернуть конструктор пользователям.
const CONSTRUCTOR_ENABLED = false;

// Частицы/предлоги, типичные для норвежских фразовых глаголов (verb + particle).
const PHRASAL_PARTICLES = new Set([
  'om','på','av','ut','opp','ned','inn','med','til','fra','for','etter','over',
  'under','igjen','bort','fram','frem','sammen','an','i','mot','gjennom','tilbake','unna',
]);

function makeAvatarThumb(dataUrl, size = 72, quality = 0.7) {
  return new Promise((resolve) => {
    if (!dataUrl || typeof dataUrl !== 'string') { resolve(null); return; }
    try {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          const min = Math.min(img.width, img.height) || size;
          const sx = (img.width - min) / 2;
          const sy = (img.height - min) / 2;
          ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (_) { resolve(null); }
      };
      img.onerror = () => resolve(null);
      try { img.crossOrigin = 'anonymous'; } catch (_) {}
      img.src = dataUrl;
    } catch (_) { resolve(null); }
  });
}

function shrinkThumbDataUrl(dataUrl, max = 400, quality = 0.5) {
  return new Promise((resolve) => {
    if (!dataUrl || typeof dataUrl !== 'string') { resolve(null); return; }
    try {
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, max / Math.max(img.width, img.height));
          const cw = Math.max(1, Math.round(img.width * scale));
          const ch = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = cw; canvas.height = ch;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, cw, ch);
          const out = canvas.toDataURL('image/jpeg', quality);
          resolve(out && out.length < dataUrl.length ? out : null);
        } catch (_) { resolve(null); }
      };
      img.onerror = () => resolve(null);
      try { img.crossOrigin = 'anonymous'; } catch (_) {}
      img.src = dataUrl;
    } catch (_) { resolve(null); }
  });
}

function normalizeRootValue(root) {
  return normalizeWordText(root || '')
    .replace(/[^\p{L}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeRootKey(root) {
  return normalizeRootValue(root)
    .replace(/^å\s+/i, '')
    .replace(/[^\p{L}]/gu, '');
}

function normalizeWordRoots(word) {
  const roots = Array.isArray(word.roots) ? word.roots : [];
  const legacyRoot = word.root
    ? [{ root: word.root, translation: word.rootTranslation || '' }]
    : [];

  return [...legacyRoot, ...roots]
    .map((item) => ({
      root: normalizeRootValue(item.root || item.value || ''),
      translation: normalizeWordText(item.translation || item.rootTranslation || ''),
    }))
    .filter((item) => item.root)
    .filter((item, index, items) => (
      items.findIndex((candidate) => normalizeRootKey(candidate.root) === normalizeRootKey(item.root)) === index
    ));
}

function capitalizeText(text) {
  if (!text) return text;
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

function formatPhraseDisplayText(text) {
  return capitalizeText(String(text || '').trim());
}

function formatConstructedPhraseNumber(number) {
  return String(Math.min(Math.max(number, 1), 999999)).padStart(6, '0');
}

function appendPhraseEnding(text, ending) {
  const cleanText = String(text || '').trim();
  if (!cleanText) return cleanText;
  if (/(?:\.{3}|[.!?]|[!?]{2})$/.test(cleanText)) return cleanText;
  return `${cleanText}${ending}`;
}

function getLocalDayStartIso() {
  const dayStart = new Date();
  dayStart.setHours(0, 0, 0, 0);
  return dayStart.toISOString();
}

function isPhraseChunkMove(move) {
  const roleKey = normalizeName(move?.word_category || '').trim().toLowerCase();
  return ['frase', 'phrase', 'фраза', 'фразы'].includes(roleKey);
}

function shouldAutoFinishPhrase(moves) {
  const safeMoves = Array.isArray(moves) ? moves : [];
  const phraseChunks = safeMoves.filter(isPhraseChunkMove).length;
  return safeMoves.length >= MAX_PHRASE_WORDS || phraseChunks >= MAX_PHRASE_CHUNKS;
}

function normalizeExampleMatchText(text) {
  return normalizeWordText(String(text || ''))
    .replace(/[.!?]+$/g, '')
    .replace(/^å\s+/i, '')
    .replace(/^ГҐ\s+/i, '')
    .trim();
}

function collectPhotoWordTermsFromNode(node, terms) {
  const normalizedNode = normalizeNode(node);

  getWordsFromNode(normalizedNode).forEach((word) => {
    if (!word?.photo?.dataUrl) return;

    const forms = {
      ...(word.forms || {}),
      ...(word.manualForms || {}),
    };
    const candidates = [
      word.text,
      ...Object.values(forms),
      forms.perfect ? `har ${forms.perfect}` : '',
    ];

    candidates.forEach((candidate) => {
      const term = normalizeExampleMatchText(candidate);
      if (term) terms.add(term);
    });
  });

  getSubcategoryNamesFromNode(normalizedNode).forEach((subcategory) => {
    collectPhotoWordTermsFromNode(normalizedNode[subcategory], terms);
  });
}

function collectPhotoWordTerms(db) {
  const terms = new Set();
  Object.values(db || {}).forEach((node) => collectPhotoWordTermsFromNode(node, terms));
  return terms;
}

function getPhrasePhotoScore(moves, photoWordTerms) {
  if (!photoWordTerms?.size) return 0;

  return (Array.isArray(moves) ? moves : []).reduce((score, move) => {
    const term = normalizeExampleMatchText(move?.word_text);
    return term && photoWordTerms.has(term) ? score + 1 : score;
  }, 0);
}

function getWordSentenceText(word, role) {
  const forms = word.manualForms || word.forms;
  const roleKey = normalizeName(role || '').toLowerCase();
  const text = ['глагол', 'verb', 'verbs'].includes(roleKey) ? forms?.present || word.text : word.text;

  return word.capitalized ? capitalizeText(text) : text;
}

function isNounGroup(category, subcategory) {
  if (!category) return false;
  if (getInferredCanonicalCategoryId(category) === 'Substantiv') return true;
  if (!subcategory) return false;
  const subcategoryName = pathToArray(subcategory).at(-1) || subcategory;
  const categoryKey = normalizeName(category).toLowerCase();
  const subcategoryKey = normalizeName(subcategoryName).toLowerCase();

  const nounCategoryNames = [
    'субъект',
    'объект',
    'предмет',
    'предметы',
    'person',
    'personer',
    'persons',
    'people',
  ];
  const nonNounSubcategoryNames = ['местоимения', 'pronouns'];
  const nounSubcategoryNames = [
    'люди',
    'предметы',
    'предмет',
    'объекты',
    'объект',
    'person',
    'personer',
    'persons',
    'people',
    'mennesker',
  ];

  return (
    nounCategoryNames.includes(categoryKey) && !nonNounSubcategoryNames.includes(subcategoryKey)
  ) || nounSubcategoryNames.includes(subcategoryKey);
}

function isVerbGroup(category, subcategory) {
  const categoryKey = normalizeName(category || '').toLowerCase();
  if (getInferredCanonicalCategoryId(category) === 'Verb') return true;
  return ['глагол', 'verb', 'verbs'].includes(categoryKey);
}

function isAdjectiveGroup(category, subcategory) {
  const categoryKey = normalizeName(category || '').toLowerCase();
  if (getInferredCanonicalCategoryId(category) === 'Adjektiv') return true;
  return ['прилагательное', 'adjective', 'adjectives'].includes(categoryKey);
}

function isAdverbGroup(category, subcategory) {
  const categoryKey = normalizeName(category || '').toLowerCase();
  if (getInferredCanonicalCategoryId(category) === 'Adverb') return true;
  return ['наречие', 'наречия', 'adverb', 'adverbs'].includes(categoryKey);
}

function shouldTryDictionaryForms(category, subcategory) {
  return Boolean(category);
}

function getNounSearchCandidates(text) {
  const base = text.trim()
    .toLowerCase()
    .replace(/^en\s+/i, '')
    .replace(/^ei\s+/i, '')
    .replace(/^et\s+/i, '');
  const candidates = [base];
  const addStemCandidate = (endingLength) => {
    const stem = base.slice(0, -endingLength);
    if (stem.length >= 3) candidates.push(stem);
  };

  if (base.endsWith('ene')) addStemCandidate(3);
  if (base.endsWith('er')) addStemCandidate(2);
  if (base.endsWith('en')) addStemCandidate(2);
  if (base.endsWith('et')) addStemCandidate(2);

  return [...new Set(candidates.filter(Boolean))];
}

function createVerbForms(text) {
  const base = text.trim().toLowerCase();
  const infinitive = base.replace(/^å\s+/i, '');

  const knownForms = {
    'være': {
      present: 'er',
      past: 'var',
      perfect: 'vært',
      imperative: 'vær',
    },
    er: {
      present: 'er',
      past: 'var',
      perfect: 'vært',
      imperative: 'vær',
    },
    err: {
      present: 'er',
      past: 'var',
      perfect: 'vært',
      imperative: 'vær',
    },
    komme: {
      present: 'kommer',
      past: 'kom',
      perfect: 'kommet',
      imperative: 'kom',
    },
    snakke: {
      present: 'snakker',
      past: 'snakket',
      perfect: 'snakket',
      imperative: 'snakk',
    },
    bo: {
      present: 'bor',
      past: 'bodde',
      perfect: 'bodd',
      imperative: 'bo',
    },
    jobbe: {
      present: 'jobber',
      past: 'jobbet',
      perfect: 'jobbet',
      imperative: 'jobb',
    },
  };

  if (knownForms[infinitive]) return knownForms[infinitive];

  return {
    present: infinitive.endsWith('e') ? `${infinitive}r` : `${infinitive}r`,
    past: infinitive.endsWith('e') ? `${infinitive}t` : `${infinitive}et`,
    perfect: infinitive.endsWith('e') ? `${infinitive}t` : `${infinitive}et`,
    imperative: infinitive.endsWith('e') ? infinitive.slice(0, -1) : infinitive,
  };
}

function isGeneratedVerbFallback(word) {
  const text = normalizeWordText(word.text || '');
  if (!text || !word.forms) return false;

  const fallbackForms = createVerbForms(text);
  return (
    word.forms.present === fallbackForms.present
    && word.forms.past === fallbackForms.past
    && word.forms.perfect === fallbackForms.perfect
    && word.forms.imperative === fallbackForms.imperative
    && !['være', 'er', 'err', 'komme', 'snakke', 'bo', 'jobbe'].includes(text)
  );
}

function findInflection(inflections, tags) {
  const form = inflections.find((item) => (
    tags.every((tag) => (
      (item.tags || []).some((itemTag) => itemTag.replace(/[<>]/g, '') === tag)
    ))
  ));

  return form?.word_form || '';
}

function findPreferredInflection(inflections, tags, preferredTags = [], rejectedTags = []) {
  const forms = inflections.filter((item) => (
    tags.every((tag) => (
      (item.tags || []).some((itemTag) => itemTag.replace(/[<>]/g, '') === tag)
    ))
  ));

  const scoreForm = (item) => {
    const cleanTags = (item.tags || []).map((tag) => tag.replace(/[<>]/g, ''));
    const preferredScore = preferredTags.reduce((score, tag) => (
      cleanTags.includes(tag) ? score + 8 : score
    ), 0);
    const rejectedScore = rejectedTags.reduce((score, tag) => (
      cleanTags.includes(tag) ? score - 10 : score
    ), 0);
    const commonMasculineEndingScore = item.word_form?.endsWith('en') ? 3 : 0;

    return preferredScore + rejectedScore + commonMasculineEndingScore;
  };

  return forms
    .slice()
    .sort((left, right) => scoreForm(right) - scoreForm(left))[0]?.word_form || '';
}

function extractNounFormsFromArticle(article, requestedWord) {
  const cleanRequestedWord = requestedWord.toLowerCase();
  const nounLemma = (article.lemmas || []).find((lemma) => {
    const isNoun = (lemma.paradigm_info || []).some((paradigm) => (
      (paradigm.tags || []).includes('NOUN')
    ));

    return isNoun && lemma.lemma.toLowerCase() === cleanRequestedWord;
  }) || (article.lemmas || []).find((lemma) => (
    (lemma.paradigm_info || []).some((paradigm) => (
      (paradigm.tags || []).includes('NOUN')
    ))
  ));

  const paradigm = nounLemma?.paradigm_info?.find((item) => (
    (item.tags || []).includes('NOUN')
  ));

  if (!paradigm?.inflection) return null;

  const singularIndefinite = findInflection(paradigm.inflection, ['Sing', 'Ind']);
  const singularDefinite = findPreferredInflection(
    paradigm.inflection,
    ['Sing', 'Def'],
    ['Masc', 'Masc/Fem'],
    ['Fem'],
  );
  const pluralIndefinite = findInflection(paradigm.inflection, ['Plur', 'Ind']);
  const pluralDefinite = findInflection(paradigm.inflection, ['Plur', 'Def']);

  if (!singularIndefinite || !singularDefinite || !pluralIndefinite || !pluralDefinite) {
    return null;
  }

  const genderTags = [];
  (nounLemma?.paradigm_info || []).forEach((pi) => {
    genderTags.push(...(pi.tags || []));
    (pi.inflection || []).forEach((inf) => genderTags.push(...(inf.tags || [])));
  });
  const cleanGenderTags = genderTags.map((t) => String(t).replace(/[<>]/g, ''));
  const gender = cleanGenderTags.some((t) => t.includes('Neuter'))
    ? 'et'
    : cleanGenderTags.some((t) => t.includes('Fem'))
      ? 'ei'
      : cleanGenderTags.some((t) => t.includes('Masc'))
        ? 'en'
        : null;

  return {
    base: singularIndefinite,
    gender,
    forms: {
      singularDefinite,
      pluralIndefinite,
      pluralDefinite,
    },
  };
}

async function fetchOfficialNounForms(text) {
  const candidates = getNounSearchCandidates(text);

  for (const base of candidates) {
    const params = new URLSearchParams({ w: base, dict: 'bm', scope: 'e', wc: 'NOUN' });
    const articlesResponse = await fetch(`https://ord.uib.no/api/articles?${params.toString()}`);

    if (!articlesResponse.ok) continue;

    const articlesData = await articlesResponse.json();
    const articleIds = articlesData?.articles?.bm || [];

    for (const articleId of articleIds.slice(0, 5)) {
      const articleResponse = await fetch(`https://ord.uib.no/bm/article/${articleId}.json`);
      if (!articleResponse.ok) continue;

      const article = await articleResponse.json();
      const forms = extractNounFormsFromArticle(article, base);
      if (forms) return forms;
    }
  }

  return null;
}

function extractVerbFormsFromArticle(article, requestedWord) {
  const cleanRequestedWord = requestedWord.toLowerCase();
  const verbLemma = (article.lemmas || []).find((lemma) => {
    const isVerb = (lemma.paradigm_info || []).some((paradigm) => (
      (paradigm.tags || []).includes('VERB')
    ));

    return isVerb && lemma.lemma.toLowerCase() === cleanRequestedWord;
  }) || (article.lemmas || []).find((lemma) => (
    (lemma.paradigm_info || []).some((paradigm) => (
      (paradigm.tags || []).includes('VERB')
    ))
  ));

  const paradigm = verbLemma?.paradigm_info?.find((item) => (
    (item.tags || []).includes('VERB')
  ));

  if (!paradigm?.inflection) return null;

  const infinitive = findInflection(paradigm.inflection, ['Inf']) || verbLemma?.lemma || requestedWord;
  const present = findInflection(paradigm.inflection, ['Pres']);
  const past = findInflection(paradigm.inflection, ['Pret']) || findInflection(paradigm.inflection, ['Past']);
  const perfect = findInflection(paradigm.inflection, ['PerfPart']) || findInflection(paradigm.inflection, ['Part']);
  const imperative = findInflection(paradigm.inflection, ['Imp']);

  if (!infinitive || !present || !past || !perfect) return null;

  return {
    base: infinitive,
    forms: {
      present,
      past,
      perfect,
      imperative: imperative || infinitive,
    },
  };
}

async function fetchOfficialVerbForms(text) {
  const base = text.trim().toLowerCase().replace(/^å\s+/i, '');
  const knownBaseByForm = {
    er: 'være',
    var: 'være',
    vært: 'være',
  };

  if (knownBaseByForm[base]) {
    const infinitive = knownBaseByForm[base];

    return {
      base: infinitive,
      forms: createVerbForms(infinitive),
    };
  }

  const params = new URLSearchParams({ w: base, dict: 'bm', scope: 'e', wc: 'VERB' });
  const articlesResponse = await fetch(`https://ord.uib.no/api/articles?${params.toString()}`);

  if (!articlesResponse.ok) return null;

  const articlesData = await articlesResponse.json();
  const articleIds = articlesData?.articles?.bm || [];

  for (const articleId of articleIds.slice(0, 5)) {
    const articleResponse = await fetch(`https://ord.uib.no/bm/article/${articleId}.json`);
    if (!articleResponse.ok) continue;

    const article = await articleResponse.json();
    const forms = extractVerbFormsFromArticle(article, base);
    if (forms) return forms;
  }

  return null;
}

function extractAdjectiveFormsFromArticle(article, requestedWord) {
  const cleanRequestedWord = requestedWord.toLowerCase();
  const adjectiveLemma = (article.lemmas || []).find((lemma) => {
    const isAdjective = (lemma.paradigm_info || []).some((paradigm) => (
      (paradigm.tags || []).includes('ADJ')
    ));

    return isAdjective && lemma.lemma.toLowerCase() === cleanRequestedWord;
  }) || (article.lemmas || []).find((lemma) => (
    (lemma.paradigm_info || []).some((paradigm) => (
      (paradigm.tags || []).includes('ADJ')
    ))
  ));

  const paradigm = adjectiveLemma?.paradigm_info?.find((item) => (
    (item.tags || []).includes('ADJ')
  ));

  if (!paradigm?.inflection) return null;

  const positive = findInflection(paradigm.inflection, ['Pos', 'Masc/Fem', 'Ind', 'Sing'])
    || adjectiveLemma?.lemma
    || requestedWord;
  const neuter = findInflection(paradigm.inflection, ['Pos', 'Neuter', 'Ind', 'Sing']);
  const plural = findInflection(paradigm.inflection, ['Pos', 'Plur']);
  const definite = findInflection(paradigm.inflection, ['Pos', 'Def', 'Sing']);
  const comparative = findInflection(paradigm.inflection, ['Cmp']);
  const superlativeIndefinite = findInflection(paradigm.inflection, ['Sup', 'Ind']);
  const superlativeDefinite = findInflection(paradigm.inflection, ['Sup', 'Def']);

  const forms = {
    positive,
    neuter,
    plural,
    definite,
    comparative,
    superlativeIndefinite,
    superlativeDefinite,
  };

  if (!Object.values(forms).some(Boolean)) return null;

  return {
    base: positive,
    forms,
  };
}

async function fetchOfficialAdjectiveForms(text) {
  const base = text.trim().toLowerCase();
  const params = new URLSearchParams({ w: base, dict: 'bm', scope: 'e', wc: 'ADJ' });
  const articlesResponse = await fetch(`https://ord.uib.no/api/articles?${params.toString()}`);

  if (!articlesResponse.ok) return null;

  const articlesData = await articlesResponse.json();
  const articleIds = articlesData?.articles?.bm || [];

  for (const articleId of articleIds.slice(0, 5)) {
    const articleResponse = await fetch(`https://ord.uib.no/bm/article/${articleId}.json`);
    if (!articleResponse.ok) continue;

    const article = await articleResponse.json();
    const forms = extractAdjectiveFormsFromArticle(article, base);
    if (forms) return forms;
  }

  return null;
}

function normalizeNounWord(word) {
  const oldNounTexts = {
    studenten: 'student',
    'lГ¦reren': 'lærer',
    læreren: 'lærer',
  };
  const text = normalizeWordText(oldNounTexts[word.text] || word.text);

  return {
    ...word,
    text,
    root: normalizeWordText(word.root || ''),
    rootTranslation: normalizeWordText(word.rootTranslation || ''),
    roots: normalizeWordRoots(word),
    capitalized: Boolean(word.capitalized),
    type: 'noun',
    gender: word.gender || null,
    forms: word.forms || null,
    manualForms: word.manualForms || (word.formsSource === 'manual' ? word.forms : null),
    formsSource: word.formsSource || (word.forms ? 'dictionary' : null),
    translations: (word.translations || []).map(normalizeWordText),
    synonyms: (word.synonyms || []).map(normalizeWordText),
    antonyms: (word.antonyms || []).map(normalizeWordText),
    adjectives: (word.adjectives || []).map(normalizeWordText),
    photo: word.photo || null,
  };
}

function normalizeVerbWord(word) {
  const oldVerbTexts = {
    er: 'være',
    err: 'være',
    kommer: 'komme',
    snakker: 'snakke',
    bor: 'bo',
    jobber: 'jobbe',
  };
  const originalText = normalizeWordText(word.text || '');
  const text = normalizeWordText(oldVerbTexts[originalText] || originalText);
  const shouldRebuildForms = Boolean(oldVerbTexts[originalText]);

  return {
    ...word,
    text,
    root: normalizeWordText(word.root || ''),
    rootTranslation: normalizeWordText(word.rootTranslation || ''),
    roots: normalizeWordRoots(word),
    capitalized: Boolean(word.capitalized),
    type: 'verb',
    forms: shouldRebuildForms
      ? createVerbForms(text)
      : word.formsSource === 'manual' ? word.manualForms || word.forms || null
        : isGeneratedVerbFallback({ ...word, text }) ? null : word.forms || null,
    manualForms: word.manualForms || (word.formsSource === 'manual' ? word.forms : null),
    formsSource: shouldRebuildForms
      ? 'dictionary'
      : word.formsSource || (word.forms && !isGeneratedVerbFallback({ ...word, text }) ? 'dictionary' : null),
    translations: (word.translations || []).map(normalizeWordText),
    synonyms: (word.synonyms || []).map(normalizeWordText),
    antonyms: (word.antonyms || []).map(normalizeWordText),
    adjectives: (word.adjectives || []).map(normalizeWordText),
    photo: word.photo || null,
  };
}

function normalizeAdjectiveWord(word) {
  return {
    ...word,
    text: normalizeWordText(word.text || ''),
    root: normalizeWordText(word.root || ''),
    rootTranslation: normalizeWordText(word.rootTranslation || ''),
    roots: normalizeWordRoots(word),
    capitalized: Boolean(word.capitalized),
    type: 'adjective',
    forms: word.forms || null,
    manualForms: word.manualForms || (word.formsSource === 'manual' ? word.forms : null),
    formsSource: word.formsSource || (word.forms ? 'dictionary' : null),
    translations: (word.translations || []).map(normalizeWordText),
    synonyms: (word.synonyms || []).map(normalizeWordText),
    antonyms: (word.antonyms || []).map(normalizeWordText),
    adjectives: (word.adjectives || []).map(normalizeWordText),
    photo: word.photo || null,
  };
}

function normalizeAdverbWord(word) {
  return {
    ...word,
    text: normalizeWordText(word.text || ''),
    root: normalizeWordText(word.root || ''),
    rootTranslation: normalizeWordText(word.rootTranslation || ''),
    roots: normalizeWordRoots(word),
    capitalized: Boolean(word.capitalized),
    type: 'adverb',
    forms: word.forms || null,
    manualForms: word.manualForms || (word.formsSource === 'manual' ? word.forms : null),
    formsSource: word.formsSource || (word.forms ? 'manual' : null),
    translations: (word.translations || []).map(normalizeWordText),
    synonyms: (word.synonyms || []).map(normalizeWordText),
    antonyms: (word.antonyms || []).map(normalizeWordText),
    adjectives: (word.adjectives || []).map(normalizeWordText),
    photo: word.photo || null,
  };
}

function normalizeWord(word) {
  return {
    ...word,
    text: normalizeWordText(word.text || ''),
    root: normalizeWordText(word.root || ''),
    rootTranslation: normalizeWordText(word.rootTranslation || ''),
    roots: normalizeWordRoots(word),
    capitalized: Boolean(word.capitalized),
    translations: (word.translations || []).map(normalizeWordText),
    synonyms: (word.synonyms || []).map(normalizeWordText),
    antonyms: (word.antonyms || []).map(normalizeWordText),
    adjectives: (word.adjectives || []).map(normalizeWordText),
    photo: word.photo || null,
  };
}

function normalizeWordsForGroup(category, subcategoryPath, words) {
  return words.map((word) => {
    if (isNounGroup(category, subcategoryPath)) return normalizeNounWord(word);
    if (isVerbGroup(category, subcategoryPath)) return normalizeVerbWord(word);
    if (isAdjectiveGroup(category, subcategoryPath)) return normalizeAdjectiveWord(word);
    if (isAdverbGroup(category, subcategoryPath)) return normalizeAdverbWord(word);
    return normalizeWord(word);
  });
}

function mapWordsInNode(node, mapper, path = []) {
  const normalizedNode = normalizeNode(node);
  const nextNode = { ...normalizedNode };

  if (Array.isArray(nextNode[WORDS_KEY])) {
    nextNode[WORDS_KEY] = nextNode[WORDS_KEY].map((word, index) => mapper(word, path, index));
  }

  Object.entries(nextNode).forEach(([subcategoryName, childNode]) => {
    if (subcategoryName === WORDS_KEY || subcategoryName === ORDER_KEY) return;
    nextNode[subcategoryName] = mapWordsInNode(childNode, mapper, [...path, subcategoryName]);
  });

  return nextNode;
}

function collectWordsFromDb(db) {
  const entries = [];

  const collectFromNode = (category, node, path = []) => {
    const normalizedNode = normalizeNode(node);
    getWordsFromNode(normalizedNode).forEach((word) => {
      entries.push({
        category,
        path,
        word,
        text: normalizeWordText(word.text || ''),
      });
    });

    Object.entries(normalizedNode).forEach(([subcategoryName, childNode]) => {
      if (subcategoryName === WORDS_KEY || subcategoryName === ORDER_KEY) return;
      collectFromNode(category, childNode, [...path, subcategoryName]);
    });
  };

  Object.entries(db || {}).forEach(([category, categoryNode]) => {
    collectFromNode(category, categoryNode);
  });

  return entries.filter((entry) => entry.text);
}

function syncReciprocalRelations(db) {
  const entries = collectWordsFromDb(db);
  if (entries.length === 0) return db;

  const buildRelationMap = (relationKey) => entries.reduce((map, entry) => {
    const relationValues = (entry.word[relationKey] || []).map(normalizeWordText).filter(Boolean);

    relationValues.forEach((relationValue) => {
      const current = map.get(relationValue) || [];
      if (!current.includes(entry.text)) map.set(relationValue, [...current, entry.text]);
    });

    return map;
  }, new Map());
  const relationMaps = {
    synonyms: buildRelationMap('synonyms'),
    antonyms: buildRelationMap('antonyms'),
  };

  if (relationMaps.synonyms.size === 0 && relationMaps.antonyms.size === 0) return db;

  let changed = false;
  const nextDb = Object.entries(db).reduce((next, [category, categoryNode]) => {
    next[category] = mapWordsInNode(categoryNode, (word) => {
      const text = normalizeWordText(word.text || '');
      let nextWord = word;

      ['synonyms', 'antonyms'].forEach((relationKey) => {
        const reciprocalValues = (relationMaps[relationKey].get(text) || []).filter((value) => value !== text);
        if (reciprocalValues.length === 0) return;

        const currentValues = (nextWord[relationKey] || []).map(normalizeWordText).filter(Boolean);
        const mergedValues = [...currentValues];

        reciprocalValues.forEach((value) => {
          if (mergedValues.length >= 3) return;
          if (!mergedValues.includes(value)) mergedValues.push(value);
        });

        if (mergedValues.length === currentValues.length) return;

        changed = true;
        nextWord = {
          ...nextWord,
          [relationKey]: mergedValues,
        };
      });

      if (nextWord !== word && nextWord.ord && nextWord.ord.evik && nextWord.ord.evikSnapshot) {
        nextWord = { ...nextWord, ord: { ...nextWord.ord, evikSnapshot: evikContentSig(nextWord) } };
      }
      return nextWord;
    });

    return next;
  }, {});

  return changed ? nextDb : db;
}

function normalizeCategoryNode(category, node, path = []) {
  if (Array.isArray(node)) {
    return normalizeWordsForGroup(category, arrayToPath(path), node);
  }

  return Object.entries(node || {}).reduce((nextNode, [subcategory, childNode]) => {
    if (subcategory === WORDS_KEY) {
      return {
        ...nextNode,
        [WORDS_KEY]: normalizeWordsForGroup(category, arrayToPath(path), childNode || []),
      };
    }
    if (subcategory === ORDER_KEY) {
      return Array.isArray(childNode) ? { ...nextNode, [ORDER_KEY]: [...childNode] } : nextNode;
    }

    const cleanSubcategory = normalizeName(subcategory);

    return {
      ...nextNode,
      [cleanSubcategory]: normalizeCategoryNode(category, childNode, [...path, cleanSubcategory]),
    };
  }, {});
}

function normalizeDb(rawDb) {
  return Object.entries(rawDb).reduce((nextDb, [category, subcategories]) => {
    const cleanCategory = normalizeName(category);

    nextDb[cleanCategory] = {
      ...(nextDb[cleanCategory] || {}),
      ...normalizeCategoryNode(cleanCategory, subcategories),
    };

    return nextDb;
  }, {});
}

function normalizeColors(rawColors) {
  const cleanColors = Object.entries(rawColors || {}).reduce((nextColors, [category, color]) => ({
    ...nextColors,
    [normalizeName(category)]: color,
  }), {});

  return { ...defaultColors, ...cleanColors };
}

function normalizeCategoryOrder(rawOrder, db) {
  const categories = Object.keys(db || {});
  const order = Array.isArray(rawOrder)
    ? rawOrder.map((category) => normalizeName(category)).filter(Boolean)
    : [];
  const uniqueOrder = order.filter((category, index, items) => (
    categories.includes(category) && items.indexOf(category) === index
  ));
  const missingCategories = categories.filter((category) => !uniqueOrder.includes(category));

  return [...uniqueOrder, ...missingCategories];
}

function readJsonStorage(key) {
  try {
    const value = localStorage.getItem(key);
    if (!value) return null;
    return JSON.parse(value);
  } catch {
    // Хранилище может быть недоступно (приватный режим, встроенный браузер) — не роняем приложение.
    return null;
  }
}

function writeJsonStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`Не удалось сохранить ${key} в localStorage`, error);
    return false;
  }
}

function createEmptyAnalysisLibrary() {
  return {
    batch: 1,
    nextNumber: 1,
    texts: [],
    archives: [],
  };
}

function normalizeAnalysisLibrary(rawLibrary) {
  const emptyLibrary = createEmptyAnalysisLibrary();
  if (!rawLibrary || typeof rawLibrary !== 'object') return emptyLibrary;

  const texts = Array.isArray(rawLibrary.texts) ? rawLibrary.texts : [];
  const archives = Array.isArray(rawLibrary.archives) ? rawLibrary.archives : [];
  const nextNumber = Number(rawLibrary.nextNumber) || texts.length + 1;
  const batch = Number(rawLibrary.batch) || archives.length + 1 || 1;

  return {
    batch,
    nextNumber: Math.min(Math.max(nextNumber, 1), 1000),
    texts,
    archives,
  };
}

function normalizeLearningProgress(rawProgress) {
  if (!rawProgress || typeof rawProgress !== 'object') {
    return { learned: {}, difficult: {} };
  }

  return {
    learned: rawProgress.learned && typeof rawProgress.learned === 'object'
      ? rawProgress.learned
      : {},
    difficult: rawProgress.difficult && typeof rawProgress.difficult === 'object'
      ? rawProgress.difficult
      : {},
    reviewQueue: rawProgress.reviewQueue && typeof rawProgress.reviewQueue === 'object'
      ? rawProgress.reviewQueue
      : {},
    totalSeen: Number(rawProgress.totalSeen) || 0,
  };
}

function normalizeUserProfile(rawProfile) {
  if (!rawProfile || typeof rawProfile !== 'object') {
    return { id: '', email: '', nickname: '', photo: null, registeredAt: '', role: 'user', status: 'active', badges: [] };
  }

  return {
    id: typeof rawProfile.id === 'string' ? rawProfile.id : '',
    email: typeof rawProfile.email === 'string' ? rawProfile.email.trim() : '',
    nickname: typeof rawProfile.nickname === 'string' ? rawProfile.nickname.trim() : '',
    photo: rawProfile.photo && typeof rawProfile.photo === 'object' ? rawProfile.photo : null,
    role: ['user', 'moderator', 'admin', 'teacher'].includes(rawProfile.role) ? rawProfile.role : 'user',
    status: ['active', 'blocked', 'pending'].includes(rawProfile.status) ? rawProfile.status : 'pending',
    registeredAt: rawProfile.registered_at || rawProfile.registeredAt || '',
    badges: Array.isArray(rawProfile.badges) ? rawProfile.badges.filter((b) => typeof b === 'string') : [],
  };
}

function isProfileBlocked(profile) {
  if(profile?.status==='blocked')return true;
  if(profile?.status==='pending'){
    const reg=new Date(profile.registeredAt);
    if(!isNaN(reg)&&(Date.now()-reg.getTime())/3600000>=48)return true;
  }
  return false;
}

function isValidEmail(email) {
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email.trim());
}

const APP_PUBLIC_URL = 'https://norge-app.vercel.app';

function getPasswordResetRedirectUrl() {
  const { origin, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return APP_PUBLIC_URL;
  }
  return origin;
}

function getProfileInitial(profile) {
  return (profile.nickname || profile.email || 'N').trim().charAt(0).toUpperCase();
}

function sanitizeRegistrationEmail(value) {
  return value.replace(/[^A-Za-z0-9._%+-@]/g, '').slice(0, 80);
}

function sanitizeRegistrationNickname(value) {
  return value.replace(/[^\p{L}\p{N}_ -]/gu, '').replace(/\s{2,}/g, ' ').slice(0, 32);
}

function sanitizeRegistrationPassword(value) {
  return value.slice(0, 20);
}

function normalizeNicknameForRegistry(value) {
  return sanitizeRegistrationNickname(value).trim().replace(/\s+/g, ' ').toLowerCase();
}

function translateSupabaseError(message) {
  const cleanMessage = String(message || '').trim();
  const lowerMessage = cleanMessage.toLowerCase();

  if (!cleanMessage) return 'Не удалось выполнить действие. Попробуйте еще раз.';
  if (lowerMessage.includes('email rate limit exceeded')) {
    return 'Слишком много попыток регистрации. Подождите несколько минут и попробуйте снова.';
  }
  if (lowerMessage.includes('invalid login credentials')) {
    return 'Неверный email или пароль.';
  }
  if (lowerMessage.includes('user already registered') || lowerMessage.includes('already registered')) {
    return 'Этот email уже зарегистрирован. Попробуйте войти.';
  }
  if (lowerMessage.includes('duplicate key') || lowerMessage.includes('already exists') || lowerMessage.includes('unique')) {
    return 'Email или ник уже занят. Попробуйте войти, выбрать другой ник или добавить цифры.';
  }
  if (lowerMessage.includes('permission denied')) {
    return 'Это действие доступно только автору. Изменять можно только свои слова и ходы.';
  }
  if (lowerMessage.includes('email not confirmed')) {
    return 'Email еще не подтвержден. Проверьте почту и перейдите по ссылке подтверждения.';
  }
  if (lowerMessage.includes('password should be')) {
    return 'Пароль не подходит по требованиям. Используйте 6-20 символов.';
  }
  if (lowerMessage.includes('profile blocked')) {
    return 'Профиль заблокирован модератором. Доступ к программе закрыт.';
  }

  return cleanMessage;
}

function formatAnalysisTextNumber(number) {
  return String(number).padStart(3, '0');
}

function downloadTextFile(text, filename) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function loadInitialDb() {
  const saved = readJsonStorage(STORAGE_KEYS.data);
  if (saved) return syncReciprocalRelations(normalizeDb(saved));

  for (const legacyKey of LEGACY_DATA_KEYS) {
    const legacyData = readJsonStorage(legacyKey);
    if (legacyData) return syncReciprocalRelations(normalizeDb(legacyData));
  }

  return syncReciprocalRelations(fallbackDb);
}

function createEmptyCloudDataPayload() {
  const categoryMeta = Object.keys(fallbackDb).reduce((meta, category) => ({
    ...meta,
    [getCategoryMetaKey(category)]: {
      canonicalId: getInferredCanonicalCategoryId(category),
    },
  }), {});

  return {
    db: syncReciprocalRelations(normalizeDb(fallbackDb)),
    categoryColors: normalizeColors(null),
    categoryMeta,
    categoryOrder: Object.keys(fallbackDb),
    constructorGroups: [],
    analysisLibrary: createEmptyAnalysisLibrary(),
    learningProgress: normalizeLearningProgress(null),
  };
}

function phraseMovesToSentenceItems(moves = []) {
  return moves.map((move) => ({
    text: move.word_text,
    role: move.word_category,
    sourceCategory: move.word_category,
    sourceSubcategory: move.word_path || null,
  }));
}

function aiCardToPublicData(card) {
  if (!card || typeof card !== 'object') return null;
  const arr = (v) => (Array.isArray(v) ? v : []);
  const f = (card.forms && typeof card.forms === 'object') ? card.forms : {};
  const type = String(card.type || '').toLowerCase();
  const data = {
    text: card.text || '',
    type: type,
    gender: card.gender || null,
    catColor: null,
    translations: arr(card.translations).map((t) => String(t)),
    synonyms: arr(card.synonyms).map((t) => String(t)),
    antonyms: arr(card.antonyms).map((t) => String(t)),
    root: card.root || '',
    cognates: arr(card.rootWords).map((w) => (w && w.text ? String(w.text) : String(w || ''))).filter(Boolean),
    phonetic: arr(card.phoneticSimilar).map((t) => (t && typeof t === 'object') ? String(t.text || t.word || '') : String(t)).filter(Boolean),
    story: card.story || '',
    forms: [],
    aiGenerated: true,
  };
  const ph = (card.phrases && typeof card.phrases === 'object') ? card.phrases : {};
  if (ph.main && ph.main.no) { data.phrase = ph.main.no; data.phraseTranslation = ph.main.ru || ''; }
  const ex = []; const exTr = [];
  ['life', 'everyday', 'rule'].forEach((k) => { if (ph[k] && ph[k].no) { ex.push(ph[k].no); exTr.push(ph[k].ru || ''); } });
  if (ex.length) { data.examples = ex; data.exampleTranslations = exTr; }
  if (type === 'noun') {
    data.forms = [f.singularIndefinite, f.singularDefinite, f.pluralIndefinite, f.pluralDefinite].filter(Boolean);
  } else if (type === 'verb') {
    data.forms = [f.infinitive, f.present, f.past, f.perfect, f.imperative].filter(Boolean);
  } else if (type === 'adjective') {
    data.grad = { positiv: f.positive || '', komparativ: f.comparative || '', superlativ: f.superlative || '' };
  } else {
    data.forms = Object.values(f).filter((x) => typeof x === 'string' && x.trim());
    if (type === 'phrase' && f.infinitive) {
      data.forms = [f.present, f.past, f.perfect].filter(Boolean);
    }
  }
  if (card.image) { data.photo = { dataUrl: card.image }; }
  return data;
}

function resizePhotoFile(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let w = img.naturalWidth || img.width;
      let h = img.naturalHeight || img.height;
      if (w > maxDim || h > maxDim) {
        if (w >= h) { h = Math.round((h * maxDim) / w); w = maxDim; }
        else { w = Math.round((w * maxDim) / h); h = maxDim; }
      }
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}

const UI_STRINGS = {
  ru: {
    nick: 'Ник', password: 'Пароль',
    nickPh: 'Как вас показывать в программе', passPh: '6-20 символов',
    forgot: 'Забыли пароль?', wait: 'Подождите...',
    login: 'Войти', register: 'Зарегистрироваться',
    haveAcc: 'Я уже зарегистрирован', createAcc: 'Создать новый аккаунт',
    backHome: '← Вернуться на главную', fillEmailPass: 'Заполните email и пароль.', fillAll: 'Заполните email, ник и пароль.', regCreated: 'Регистрация создана. Проверьте email, подтвердите аккаунт и затем нажмите «Я уже зарегистрирован».', langLabel: 'Язык / Language',
    guestTagline: 'Программа по изучению норвежского языка',
    nativeLangLabel: 'Ваш родной язык:',
    guestSearchPh: 'Поиск слова по-норвежски…',
    notFound: 'Слово «{word}» ещё не занесено в наш публичный словарь, ты можешь быть первым, кто это сделает. Для этого войди на сайт.',
    notFoundAuth: 'Слово «{word}» ещё не занесено в наш публичный словарь, ты можешь быть первым, кто это сделает.',
    searching: 'Поиск…',
    hintEmail: 'Напиши свой email, на него придёт письмо для подтверждения. Убедись, что email написан верно.',
    hintNick: 'Имя, под которым ты будешь выступать в программе. 2–32 символа. Если желаемый ник уже занят, придётся придумать другой.',
    hintPass: 'Придумай пароль от 6 до 20 символов. Запиши его, чтобы не забыть.',
    lessonOn: 'Пройти обучение', lessonOff: 'Выключить обучение',
    news: 'Новости', newsLoading: 'Загружаем новости…', newsEmpty: 'Новостей пока нет', newsRead: 'Читать оригинал',
    radio: 'Радио', radioLoading: 'Загружаем песни…', radioEmpty: 'Песен пока нет', radioPlay: 'Играть', radioPause: 'Пауза', radioDownload: 'Скачать', radioNext: 'Следующая через', radioHint: 'Песни из базы играют одна за другой — можно слушать фоном.',
  },
  en: {
    nick: 'Nickname', password: 'Password',
    nickPh: 'How others will see you', passPh: '6-20 characters',
    forgot: 'Forgot password?', wait: 'Please wait...',
    login: 'Log in', register: 'Sign up',
    haveAcc: 'I already have an account', createAcc: 'Create a new account',
    backHome: '← Back to home', fillEmailPass: 'Fill in email and password.', fillAll: 'Fill in email, nickname and password.', regCreated: 'Registration created. Check your email, confirm your account, then tap "I am already registered".', langLabel: 'Язык / Language',
    guestTagline: 'A program for learning Norwegian',
    nativeLangLabel: 'Your native language:',
    guestSearchPh: 'Search a Norwegian word…',
    notFound: 'The word "{word}" is not yet in our public dictionary — you can be the first to add it. Just sign in to the site.',
    notFoundAuth: 'The word "{word}" is not yet in our public dictionary — you can be the first to add it.',
    searching: 'Searching…',
    hintEmail: 'Enter your email, a confirmation letter will be sent to it. Make sure the email is correct.',
    hintNick: 'The name you will go by in the program. 2–32 characters. If the desired nickname is already taken, you will need to pick another.',
    hintPass: 'Create a password of 6 to 20 characters. Write it down so you do not forget it.',
    lessonOn: 'Start the tutorial', lessonOff: 'Turn off the tutorial',
    news: 'News', newsLoading: 'Loading news…', newsEmpty: 'No news yet', newsRead: 'Read the original',
    radio: 'Radio', radioLoading: 'Loading songs…', radioEmpty: 'No songs yet', radioPlay: 'Play', radioPause: 'Pause', radioDownload: 'Download', radioNext: 'Next in', radioHint: 'Songs from the library play one after another — nice as background.',
  },
  uk: {
    nick: 'Нік', password: 'Пароль',
    nickPh: 'Як показувати вас у програмі', passPh: '6-20 символів',
    forgot: 'Забули пароль?', wait: 'Зачекайте...',
    login: 'Увійти', register: 'Зареєструватися',
    haveAcc: 'Я вже зареєстрований', createAcc: 'Створити новий акаунт',
    backHome: '← На головну', fillEmailPass: 'Заповніть email і пароль.', fillAll: 'Заповніть email, нік і пароль.', regCreated: 'Реєстрацію створено. Перевір пошту, підтверди акаунт, а потім натисни «Я вже зареєстрований».', langLabel: 'Мова / Language',
    guestTagline: 'Програма для вивчення норвезької мови',
    nativeLangLabel: 'Ваша рідна мова:',
    guestSearchPh: 'Пошук норвезького слова…',
    notFound: 'Слова «{word}» ще немає в нашому публічному словнику — ти можеш бути першим, хто це зробить. Для цього увійди на сайт.',
    notFoundAuth: 'Слова «{word}» ще немає в нашому публічному словнику — ти можеш бути першим, хто це зробить.',
    searching: 'Пошук…',
    hintEmail: 'Напиши свій email, на нього прийде лист для підтвердження. Переконайся, що email введено правильно.',
    hintNick: 'Ім\'я, під яким ти виступатимеш у програмі. 2–32 символи. Якщо бажаний нік уже зайнятий, доведеться придумати інший.',
    hintPass: 'Придумай пароль від 6 до 20 символів. Запиши його, щоб не забути.',
    lessonOn: 'Пройти навчання', lessonOff: 'Вимкнути навчання',
    news: 'Новини', newsLoading: 'Завантажуємо новини…', newsEmpty: 'Новин поки немає', newsRead: 'Читати оригінал',
    radio: 'Радіо', radioLoading: 'Завантажуємо пісні…', radioEmpty: 'Пісень поки немає', radioPlay: 'Грати', radioPause: 'Пауза', radioDownload: 'Завантажити', radioNext: 'Наступна за', radioHint: 'Пісні з бази грають одна за одною — можна слухати фоном.',
  },
  de: {
    nick: 'Nickname', password: 'Passwort',
    nickPh: 'Wie andere Sie sehen', passPh: '6-20 Zeichen',
    forgot: 'Passwort vergessen?', wait: 'Bitte warten...',
    login: 'Anmelden', register: 'Registrieren',
    haveAcc: 'Ich habe bereits ein Konto', createAcc: 'Neues Konto erstellen',
    backHome: '← Zur Startseite', fillEmailPass: 'E-Mail und Passwort ausfüllen.', fillAll: 'E-Mail, Nick und Passwort ausfüllen.', regCreated: 'Registrierung erstellt. Prüfe deine E-Mail, bestätige dein Konto und tippe dann auf „Ich bin bereits registriert“.', langLabel: 'Sprache / Language',
    guestTagline: 'Ein Programm zum Erlernen der norwegischen Sprache',
    nativeLangLabel: 'Ihre Muttersprache:',
    guestSearchPh: 'Norwegisches Wort suchen…',
  },
  es: {
    nick: 'Apodo', password: 'Contraseña',
    nickPh: 'Cómo te verán los demás', passPh: '6-20 caracteres',
    forgot: '¿Olvidaste tu contraseña?', wait: 'Espera...',
    login: 'Iniciar sesión', register: 'Registrarse',
    haveAcc: 'Ya tengo una cuenta', createAcc: 'Crear una cuenta nueva',
    backHome: '← Al inicio', fillEmailPass: 'Completa el email y la contraseña.', fillAll: 'Completa el email, el apodo y la contraseña.', regCreated: 'Registro creado. Revisa tu correo, confirma tu cuenta y luego pulsa "Ya estoy registrado".', langLabel: 'Idioma / Language',
    guestTagline: 'Un programa para aprender noruego',
    nativeLangLabel: 'Tu idioma nativo:',
    guestSearchPh: 'Buscar una palabra noruega…',
  },
  fr: {
    nick: 'Pseudo', password: 'Mot de passe',
    nickPh: 'Comment les autres vous voient', passPh: '6-20 caractères',
    forgot: 'Mot de passe oublié ?', wait: 'Patientez...',
    login: 'Se connecter', register: "S'inscrire",
    haveAcc: "J'ai déjà un compte", createAcc: 'Créer un nouveau compte',
    backHome: '← Accueil', fillEmailPass: "Remplis l'email et le mot de passe.", fillAll: "Remplis l'email, le pseudo et le mot de passe.", regCreated: "Inscription créée. Vérifie ton e-mail, confirme ton compte, puis clique sur « Je suis déjà inscrit ».", langLabel: 'Langue / Language',
    guestTagline: 'Un programme pour apprendre le norvégien',
    nativeLangLabel: 'Votre langue maternelle :',
    guestSearchPh: 'Rechercher un mot norvégien…',
  },
  no: {
    nick: 'Kallenavn', password: 'Passord',
    nickPh: 'Hvordan andre ser deg', passPh: '6-20 tegn',
    forgot: 'Glemt passord?', wait: 'Vent litt...',
    login: 'Logg inn', register: 'Registrer deg',
    haveAcc: 'Jeg har allerede en konto', createAcc: 'Opprett en ny konto',
    backHome: '← Til forsiden', fillEmailPass: 'Fyll inn e-post og passord.', fillAll: 'Fyll inn e-post, kallenavn og passord.', regCreated: 'Registrering opprettet. Sjekk e-posten din, bekreft kontoen, og trykk deretter «Jeg er allerede registrert».', langLabel: 'Språk / Language',
    guestTagline: 'Et program for å lære norsk',
    nativeLangLabel: 'Ditt morsmål:',
    guestSearchPh: 'Søk etter et norsk ord…',
    notFound: 'Ordet «{word}» er ennå ikke lagt til i vår offentlige ordbok — du kan være den første som gjør det. Logg inn på siden for å gjøre det.',
    notFoundAuth: 'Ordet «{word}» er ennå ikke lagt til i vår offentlige ordbok — du kan være den første som gjør det.',
    searching: 'Søker…',
    hintEmail: 'Skriv inn e-posten din, en bekreftelses-e-post sendes dit. Sjekk at e-posten er riktig.',
    hintNick: 'Navnet du bruker i programmet. 2–32 tegn. Hvis ønsket kallenavn allerede er tatt, må du velge et annet.',
    hintPass: 'Lag et passord på 6 til 20 tegn. Skriv det ned så du ikke glemmer det.',
    lessonOn: 'Start opplæringen', lessonOff: 'Slå av opplæringen',
    news: 'Nyheter', newsLoading: 'Laster nyheter…', newsEmpty: 'Ingen nyheter ennå', newsRead: 'Les originalen',
    radio: 'Radio', radioLoading: 'Laster sanger…', radioEmpty: 'Ingen sanger ennå', radioPlay: 'Spill', radioPause: 'Pause', radioDownload: 'Last ned', radioNext: 'Neste om', radioHint: 'Sanger fra basen spilles etter hverandre — fint som bakgrunn.',
  },
};

const LANGS = [
  { code: 'ru', flag: '🇷🇺', name: 'Русский' },
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'no', flag: '🇳🇴', name: 'Norsk' },
  { code: 'uk', flag: '🇺🇦', name: 'Українська' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
];

export default function App() {
  const [db, setDb] = useState(loadInitialDb);
  const [ordDraft, setOrdDraft] = useState(null);
  const [publicPage, setPublicPage] = useState(() => {
    try {
      const o = new URLSearchParams(window.location.search).get('ord');
      if (o && o.trim()) return { wordNorm: o.trim().toLowerCase(), wordText: o.trim(), openedAt: Date.now(), deepLink: true };
    } catch (e) {}
    return null;
  });
  const [evik, setEvik] = useState(null);
  const [photoModePick, setPhotoModePick] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogResult, setDialogResult] = useState(null);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [voiceSetupOpen, setVoiceSetupOpen] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState({ has: false, name: '' });
  const [aiLock, setAiLock] = useState(false);
  const aiLockTimerRef = useRef(null);
  const aiBtnBusy = aiLock || (evik && evik.stage === 'loading');
  const photoInputRef = useRef(null);
  const evikTimerRef = useRef(null);
  const evikLongRef = useRef(false);
  const photoModeRef = useRef('objects');
  const [pubMenuWord, setPubMenuWord] = useState(null);
  const [showAuthScreen, setShowAuthScreen] = useState(false);
  const [isNewsOpen, setIsNewsOpen] = useState(false);
  const [isRadioOpen, setIsRadioOpen] = useState(false);
  const [radioMounted, setRadioMounted] = useState(false);
  const [evikMenuOpen, setEvikMenuOpen] = useState(false);
  const [uiLang, setUiLang] = useState(() => { try { return localStorage.getItem('norsk-ui-lang') || 'no'; } catch (e) { return 'no'; } });
  const [langChosen, setLangChosen] = useState(() => { try { return localStorage.getItem('norsk-lang-chosen') === '1'; } catch (e) { return false; } });
  const changeUiLang = (l) => { setUiLang(l); setLangChosen(true); try { localStorage.setItem('norsk-ui-lang', l); localStorage.setItem('norsk-lang-chosen', '1'); } catch (e) {} };
  const t = (k) => (UI_STRINGS[uiLang] && UI_STRINGS[uiLang][k]) || UI_STRINGS.en[k] || UI_STRINGS.ru[k] || k;
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const curLang = LANGS.find((l) => l.code === uiLang) || LANGS[0];
  const renderLangPicker = (extraClass) => (
    <div className={'lang-picker' + (extraClass ? ' ' + extraClass : '')}>
      <button type="button" className="lang-picker-btn" onClick={() => setLangMenuOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={langMenuOpen}>
        <span className="lang-flag">{curLang.flag}</span>
        <span className="lang-name">{curLang.name}</span>
        <span className="lang-caret">▾</span>
      </button>
      {langMenuOpen && (
        <>
          <div className="lang-menu-backdrop" onClick={() => setLangMenuOpen(false)} />
          <div className="lang-menu" role="listbox">
            {LANGS.map((l) => (
              <button type="button" key={l.code} role="option" aria-selected={l.code === uiLang} className={'lang-option' + (l.code === uiLang ? ' active' : '')} onClick={() => { changeUiLang(l.code); setLangMenuOpen(false); }}>
                <span className="lang-flag">{l.flag}</span>
                <span className="lang-name">{l.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
  const [guestQuery, setGuestQuery] = useState('');
  const [guestResults, setGuestResults] = useState([]);
  const [guestSearching, setGuestSearching] = useState(false);
  const [categoryColors, setCategoryColors] = useState(() => (
    normalizeColors(readJsonStorage(STORAGE_KEYS.colors))
  ));
  const [categoryMeta, setCategoryMeta] = useState(() => (
    readJsonStorage(STORAGE_KEYS.categoryMeta) || {}
  ));
  const [categoryOrder, setCategoryOrder] = useState(() => (
    readJsonStorage(STORAGE_KEYS.categoryOrder) || []
  ));
  const [hiddenSkeletonCanonicalIds, setHiddenSkeletonCanonicalIds] = useState(() => (
    readJsonStorage(STORAGE_KEYS.hiddenSkeleton) || []
  ));
  const [deckSubcategories, setDeckSubcategories] = useState(() => (
    readJsonStorage(STORAGE_KEYS.deckSubcategories) || []
  ));
  const [analyzerAliases, setAnalyzerAliases] = useState(() => (
    readJsonStorage(STORAGE_KEYS.analyzerAliases) || {}
  ));
  const [constructorGroups, setConstructorGroups] = useState(() => (
    readJsonStorage(STORAGE_KEYS.constructorGroups) || []
  ));
  const [analysisLibrary, setAnalysisLibrary] = useState(() => (
    normalizeAnalysisLibrary(readJsonStorage(STORAGE_KEYS.analysisLibrary))
  ));
  const [learningProgress, setLearningProgress] = useState(() => (
    normalizeLearningProgress(readJsonStorage(STORAGE_KEYS.learningProgress))
  ));
  const [userProfile, setUserProfile] = useState(() => (
    normalizeUserProfile(readJsonStorage(STORAGE_KEYS.userProfile))
  ));
  const [registrationDraft, setRegistrationDraft] = useState(() => (
    normalizeUserProfile(readJsonStorage(STORAGE_KEYS.userProfile))
  ));
  const [userRegistry, setUserRegistry] = useState(() => {
    const savedRegistry = readJsonStorage(STORAGE_KEYS.userRegistry);
    return Array.isArray(savedRegistry) ? savedRegistry : [];
  });
  const [registrationError, setRegistrationError] = useState('');
  const [authMode, setAuthMode] = useState('register');
  const [lessonActive, setLessonActive] = useState(false);
  const [focusedAuthField, setFocusedAuthField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [lessonStep, setLessonStep] = useState(0);
  const [showElephantBadge, setShowElephantBadge] = useState(false);
  const [activeLesson, setActiveLesson] = useState(null);
  const [lastBadgeKey, setLastBadgeKey] = useState('');

  useEffect(() => {
    const du = userProfile && userProfile.photo && userProfile.photo.dataUrl;
    if (!du || (userProfile.photo && userProfile.photo.thumb)) return;
    let cancelled = false;
    makeAvatarThumb(du, 64, 0.7).then((thumb) => {
      if (cancelled || !thumb) return;
      setUserProfile((cur) => (
        cur && cur.photo && cur.photo.dataUrl === du && !cur.photo.thumb
          ? { ...cur, photo: { ...cur.photo, thumb } }
          : cur
      ));
    });
    return () => { cancelled = true; };
  }, [userProfile.photo && userProfile.photo.dataUrl]);

  useEffect(() => {
    const badges = Array.isArray(userProfile?.badges) ? userProfile.badges : [];
    const uid = userProfile?.id;
    if (!uid) return;
    const count = badges.filter((b) => b === 'pink_elephant').length;
    if (count <= 0) return;
    const seenKey = 'seen_pink_elephant_count_' + uid;
    let seen = 0;
    try { seen = parseInt(localStorage.getItem(seenKey) || '0', 10) || 0; } catch (_) {}
    if (count <= seen) return;
    try { localStorage.setItem(seenKey, String(count)); } catch (_) {}
    setLastBadgeKey('pink_elephant');
    setShowElephantBadge(true);
  }, [userProfile]);
  const [forceOpenSection, setForceOpenSection] = useState(null);
  const [authStatus, setAuthStatus] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isBackupModalOpen, setIsBackupModalOpen] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isPasswordResetRequestOpen, setIsPasswordResetRequestOpen] = useState(false);
  const [shouldPulseResetEmail, setShouldPulseResetEmail] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [shouldPulseRecoveryPassword, setShouldPulseRecoveryPassword] = useState(false);
  const [isProfileStatsOpen, setIsProfileStatsOpen] = useState(false);
  const cloudLoadedRef = useRef(false);
  const rootMigrationDoneRef = useRef(false);
  const rootMigrationCountRef = useRef(0);
  const authoredMigrationDoneRef = useRef(false);
  const authoredMigrationCountRef = useRef(0);
  const thumbShrinkDoneRef = useRef(false);
  const dbRef = useRef(db);
  useEffect(() => { dbRef.current = db; }, [db]);
  const [sentence, setSentence] = useState([]);
  const [positionHistory, setPositionHistory] = useState(['START']);
  // Снимок места работы, сохранённый перед перезагрузкой логотипом (читаем один раз).
  const navRestoreRef = useRef(undefined);
  if (navRestoreRef.current === undefined) {
    let snap = null;
    try {
      const raw = localStorage.getItem('norsk_nav_restore_v1');
      if (raw) snap = JSON.parse(raw);
    } catch (_) {}
    navRestoreRef.current = (snap && typeof snap === 'object') ? snap : null;
  }
  const navRestore = navRestoreRef.current;
  const navSnapshotRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState(navRestore && ('activeCategory' in navRestore) ? (navRestore.activeCategory ?? null) : null);
  const [activeSubcategory, setActiveSubcategory] = useState(navRestore && ('activeSubcategory' in navRestore) ? (navRestore.activeSubcategory ?? null) : null);
  const [analyzerText, setAnalyzerText] = useState(navRestore && typeof navRestore.analyzerText === 'string' ? navRestore.analyzerText : '');
  const [analyzerMaxChars, setAnalyzerMaxChars] = useState(5000);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [newWordCapitalized, setNewWordCapitalized] = useState(false);
  const [databaseSearch, setDatabaseSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchHistory, setSearchHistory] = useState(() => { try { return JSON.parse(localStorage.getItem('norsk_search_history') || '[]'); } catch (e) { return []; } });
  const [evikHighlight, setEvikHighlight] = useState('');
  const [wordRatings, setWordRatings] = useState([]);
  const [showRatings, setShowRatings] = useState(false);
  const [dbPublicResults, setDbPublicResults] = useState([]);
  const [searchHighlight, setSearchHighlight] = useState(null);
  const [isLearningMode, setIsLearningMode] = useState(navRestore && typeof navRestore.isLearningMode === 'boolean' ? navRestore.isLearningMode : false);
  const [isStudyActive, setIsStudyActive] = useState(false);
  const [isConstructorOpen, setIsConstructorOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem('norskTheme') === 'light' ? 'light' : 'dark'; } catch (e) { return 'dark'; }
  });
  const applyThemeAttr = (v) => {
    try {
      if (v === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
      else document.documentElement.removeAttribute('data-theme');
    } catch (e) {}
  };
  const toggleTheme = () => {
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('norskTheme', next); } catch (e) {}
      applyThemeAttr(next);
      return next;
    });
  };
  useEffect(() => { applyThemeAttr(theme); }, []);
  const [isLogoSpinning, setIsLogoSpinning] = useState(false);
  const [subcategoryDeleteConfirm, setSubcategoryDeleteConfirm] = useState(null);
  const [addRowModal, setAddRowModal] = useState(null);
  const [renameRowModal, setRenameRowModal] = useState(null);
  const [wordLinkModal, setWordLinkModal] = useState(null);
  const [storyModal, setStoryModal] = useState(null);
  const [editWordModal, setEditWordModal] = useState(null);
  const [deleteRowModal, setDeleteRowModal] = useState(null);
  const [deleteAccountModal, setDeleteAccountModal] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const requestConfirm = (message, options = {}) => new Promise((resolve) => setConfirmDialog({ message, resolve, ...options }));
  const resolveConfirm = (value) => { if (confirmDialog && confirmDialog.resolve) confirmDialog.resolve(value); setConfirmDialog(null); };
  const notify = (message, options = {}) => new Promise((resolve) => setConfirmDialog({ message, resolve, alert: true, ...options }));
  const requestChoice = (message, options = {}) => new Promise((resolve) => setConfirmDialog({ message, resolve, ...options }));
  const [activeMobileSection, setActiveMobileSection] = useState(navRestore && navRestore.activeMobileSection ? navRestore.activeMobileSection : 'dictionary');
  const [mobileSearchMode, setMobileSearchMode] = useState(true);
  const [deskDict, setDeskDict] = useState(navRestore && typeof navRestore.deskDict === 'boolean' ? navRestore.deskDict : false);
  const [deskAnalyzer, setDeskAnalyzer] = useState(navRestore && typeof navRestore.deskAnalyzer === 'boolean' ? navRestore.deskAnalyzer : false);
  const [isPartsMenuOpen, setIsPartsMenuOpen] = useState(false);
  const [phraseGames, setPhraseGames] = useState([]);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState({ subscribers_count: 0, starred_count: 0 });
  const [phraseDatabasesCache, setPhraseDatabasesCache] = useState(null);
  const phraseDatabasesForPanel = useMemo(() => {
    const others = (phraseDatabasesCache || []).filter((r) => r.user_id !== userProfile.id);
    if (!userProfile.id) return others;
    const myEntry = { user_id: userProfile.id, nickname: userProfile.nickname || "", data: { db } };
    return [myEntry, ...others];
  }, [phraseDatabasesCache, db, userProfile.id, userProfile.nickname]);

  const [finishedPhraseTexts, setFinishedPhraseTexts] = useState([]);
  const [phraseGamesError, setPhraseGamesError] = useState('');
  const [activePhraseGameId, setActivePhraseGameId] = useState(null);
  const [isWatchingPhrase, setIsWatchingPhrase] = useState(false);


  useEffect(() => {
    const raw = guestQuery.trim();
    if (!raw) { setGuestResults([]); setGuestSearching(false); return undefined; }
    const safe = raw.replace(/[,()%*]/g, ' ').trim();
    if (!safe) { setGuestResults([]); return undefined; }
    let cancelled = false;
    setGuestSearching(true);
    const t = setTimeout(() => {
      supabase.from('ordskjonnhet_public')
        .select('word_norm,word_text,owner_nick,completeness,owner_id,avatar:data->>ownerAvatar')
        .eq('authored', true)
        .or('word_text.ilike.%' + safe + '%,word_norm.ilike.%' + safe + '%')
        .order('completeness', { ascending: false })
        .limit(40)
        .then(({ data }) => { if (!cancelled) { setGuestResults(Array.isArray(data) ? data : []); setGuestSearching(false); } }, () => { if (!cancelled) { setGuestResults([]); setGuestSearching(false); } });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [guestQuery, supabase]);

  const guestGrouped = useMemo(() => {
    if (!guestResults.length) return [];
    const ranked = [...guestResults].sort((a, b) => (String(a.owner_nick||'').trim().toLowerCase()==='evik'?0:1) - (String(b.owner_nick||'').trim().toLowerCase()==='evik'?0:1));
    const byWord = new Map();
    for (const r of ranked) {
      const wn = String(r.word_norm || '').trim().toLowerCase();
      if (!byWord.has(wn)) byWord.set(wn, { ...r, authors: [] });
      byWord.get(wn).authors.push(r);
    }
    return [...byWord.values()];
  }, [guestResults]);

  const guestHasExact = useMemo(() => {
    const gq = guestQuery.trim().toLowerCase();
    if (!gq) return false;
    return guestGrouped.some((r) => String(r.word_norm || '').trim().toLowerCase() === gq);
  }, [guestGrouped, guestQuery]);
  const [isComposingPhrase, setIsComposingPhrase] = useState(false);
  const [isFinishMenuOpen, setIsFinishMenuOpen] = useState(false);
  const [isFinishedPhrasesOpen, setIsFinishedPhrasesOpen] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [phraseClockTick, setPhraseClockTick] = useState(Date.now());
  const [moderationTarget, setModerationTarget] = useState('');
  const [constructorGroupDraft, setConstructorGroupDraft] = useState({
    label: '',
    sourceKey: '',
  });

  const getCloudDataPayload = () => ({
    db: stripPhotosForCloud(db),
    categoryColors,
    categoryMeta,
    categoryOrder: normalizeCategoryOrder(categoryOrder, db),
    constructorGroups: normalizeConstructorGroups(constructorGroups, db),
    analysisLibrary,
    learningProgress,
    deckSubcategories,
    analyzerAliases,
  });

  const applyCloudDataPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return;
    if (payload.db) {
      const normalizedDb = syncReciprocalRelations(normalizeDb(payload.db));
      forEachWordInDb(normalizedDb, (w) => {
        if (w && w.ord && w.ord.evik && w.ord.evikSnapshot) w.ord.evikSnapshot = evikContentSig(w);
      });
      const incomingEmpty = Object.keys(normalizedDb || {}).length === 0;
      const haveData = cloudLoadedRef.current && db && Object.keys(db).length > 0;
      if (incomingEmpty && haveData) {
        // Защита: не затирать уже загруженную непустую базу подозрительной пустой загрузкой.
      } else {
        setDb(normalizedDb);
        setCategoryOrder(normalizeCategoryOrder(payload.categoryOrder || Object.keys(normalizedDb), normalizedDb));
      }
    } else if (payload.categoryOrder) {
      setCategoryOrder((currentOrder) => normalizeCategoryOrder(payload.categoryOrder || currentOrder, db));
    }
    if (payload.categoryColors) setCategoryColors(normalizeColors(payload.categoryColors));
    if (payload.categoryMeta) setCategoryMeta(payload.categoryMeta || {});
    if (payload.constructorGroups) {
      setConstructorGroups(normalizeConstructorGroups(payload.constructorGroups, payload.db ? syncReciprocalRelations(normalizeDb(payload.db)) : db));
    }
    if (payload.analysisLibrary) setAnalysisLibrary(normalizeAnalysisLibrary(payload.analysisLibrary));
    if (payload.learningProgress) setLearningProgress(normalizeLearningProgress(payload.learningProgress));
    if (Array.isArray(payload.deckSubcategories)) setDeckSubcategories(payload.deckSubcategories);
    if (payload.analyzerAliases && typeof payload.analyzerAliases === 'object' && Object.keys(payload.analyzerAliases).length > 0) setAnalyzerAliases((prev) => ({ ...prev, ...payload.analyzerAliases }));
  };

  const migrateInlinePhotos = (userId) => {
    if (!userId) return;
    setDb((currentDb) => {
      const clone = JSON.parse(JSON.stringify(currentDb || {}));
      let changed = false;
      forEachWordInDb(clone, (w) => {
        eachWordPhoto(w, (ph) => {
          if (ph.dataUrl && !ph.photoKey) {
            const key = 'ph_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
            changed = true;
            const withKey = { ...ph, photoKey: key };
            supabase.from('word_photos').upsert({
              user_id: userId,
              photo_key: key,
              data: withKey,
              updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id,photo_key' });
            uploadPhotoToStorage(userId, key, withKey.dataUrl);
            return withKey;
          }
          return undefined;
        });
      });
      return changed ? clone : currentDb;
    });
  };

  const photoLoadingRef = useRef(new Set());
  const [foreignPhotosCache, setForeignPhotosCache] = useState({});
  const [ownPublicThumbs, setOwnPublicThumbs] = useState({});
  const [evikAnalyzerWords, setEvikAnalyzerWords] = useState([]);
  const foreignPhotoLoadingRef = useRef(new Set());
  const loadForeignPhoto = async (photoKey, ownerId) => {
    if (!photoKey || !ownerId) return;
    const cacheKey = ownerId + ':' + photoKey;
    if (foreignPhotoLoadingRef.current.has(cacheKey)) return;
    if (foreignPhotosCache[cacheKey]) return;
    foreignPhotoLoadingRef.current.add(cacheKey);
    try {
      const { data, error } = await supabase
        .from('word_photos')
        .select('data')
        .eq('user_id', ownerId)
        .eq('photo_key', photoKey)
        .maybeSingle();
      if (error || !data || !data.data || !data.data.dataUrl) return;
      setForeignPhotosCache((prev) => ({ ...prev, [cacheKey]: data.data }));
    } catch (_) {}
  };

  const loadOnePhotoLazy = async (photoKey, userId) => {
    if (!photoKey || !userId) return;
    if (photoLoadingRef.current.has(photoKey)) return;
    photoLoadingRef.current.add(photoKey);
    try {
      const { data, error } = await supabase
        .from('word_photos')
        .select('data')
        .eq('user_id', userId)
        .eq('photo_key', photoKey)
        .maybeSingle();
      if (error || !data || !data.data || !data.data.dataUrl) return;
      const photo = data.data;
      setDb((currentDb) => {
        const clone = JSON.parse(JSON.stringify(currentDb || {}));
        let changed = false;
        forEachWordInDb(clone, (w) => {
          eachWordPhoto(w, (ph) => {
            if (ph.photoKey === photoKey && !ph.dataUrl) { changed = true; return { ...ph, dataUrl: photo.dataUrl, name: photo.name }; }
            return undefined;
          });
        });
        return changed ? clone : currentDb;
      });
    } catch (e) {}
  };


  // Загружает миниатюры СВОИХ опубликованных слов из ordskjonnhet_public.
  // Это запасной источник фото для словаря: если у слова локально нет dataUrl,
  // но оно опубликовано (фото видно на публичной странице) — показываем миниатюру.
  const loadOwnPublicThumbs = async (ownerId) => {
    if (!ownerId) return;
    try {
      const { data, error } = await supabase
        .from('ordskjonnhet_public')
        .select('word_norm,data')
        .eq('owner_id', ownerId);
      if (error || !Array.isArray(data)) return;
      const map = {};
      data.forEach((row) => {
        const thumb = row && row.data && row.data.photo && row.data.photo.thumb;
        if (row.word_norm && thumb) map[row.word_norm] = thumb;
      });
      if (Object.keys(map).length) setOwnPublicThumbs(map);
    } catch (_) {}
  };

  const loadEvikAnalyzerWords = async () => {
    try {
      const { data, error } = await supabase
        .from('ordskjonnhet_public')
        .select('word_text,data->type,data->aiPos,data->translations,data->gender,data->root,data->forms,data->photo')
        .eq('owner_nick', 'Evik');
      if (error || !Array.isArray(data)) return;
      const list = data.map((row) => {
        const d = row || {};
        const forms = Array.isArray(d.forms) ? d.forms.filter((f) => typeof f === 'string') : [];
        return {
          text: (row && row.word_text) || d.text || '',
          pos: String(d.type || d.aiPos || '').toLowerCase().trim(),
          translations: Array.isArray(d.translations) ? d.translations.filter(Boolean) : [],
          gender: d.gender || null,
          root: d.root || '',
          forms,
          photoUrl: (() => { const ph = d.photo && d.photo.dataUrl ? String(d.photo.dataUrl) : ''; return ph ? (ph.indexOf('/api/ai/img/') === 0 ? ph + '?w=220' : ph) : ''; })(),
        };
      }).filter((w) => w.text);
      setEvikAnalyzerWords(list);
    } catch (_) {}
  };

  useEffect(() => { loadEvikAnalyzerWords(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWordRatings = async () => {
    try {
      const r = await fetch('/api/ai/ratings');
      const j = await r.json().catch(() => null);
      if (j && j.ok && Array.isArray(j.ratings)) setWordRatings(j.ratings);
    } catch (_) {}
  };
  useEffect(() => { loadWordRatings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWordPhotosInto = async (userId) => {
    if (!userId) return;
    const missing = new Set();
    forEachWordInDb(dbRef.current || {}, (w) => {
      eachWordPhoto(w, (ph) => { if (ph && ph.photoKey && !ph.dataUrl) missing.add(ph.photoKey); return undefined; });
    });
    if (missing.size === 0) return;
    const keys = Array.from(missing);
    let q = supabase.from('word_photos').select('photo_key,data').eq('user_id', userId);
    if (keys.length <= 200) q = q.in('photo_key', keys);
    const { data, error } = await q;
    if (error || !Array.isArray(data) || data.length === 0) return;
    const byKey = {};
    data.forEach((row) => { if (row.photo_key) byKey[row.photo_key] = row.data; });
    setDb((currentDb) => {
      const clone = JSON.parse(JSON.stringify(currentDb || {}));
      let changed = false;
      forEachWordInDb(clone, (w) => {
        eachWordPhoto(w, (ph) => {
          const key = ph.photoKey;
          if (key && byKey[key] && byKey[key].dataUrl && !ph.dataUrl) {
            changed = true;
            return { ...byKey[key], position: ph.position, photoKey: key };
          }
          return undefined;
        });
      });
      return changed ? clone : currentDb;
    });
  };


  // Создаёт ежедневный снапшот пользовательской базы, если последний старше 23 часов.
  const maybeCreateDailyBackup = async (userId) => {
    const ckey = 'lastBackupCheck:' + (userId || 'me');
    try {
      const last = Number(localStorage.getItem(ckey) || 0);
      if (last && Date.now() - last < 23 * 3600000) return;
    } catch (_) {}
    try {
      const { data: list, error: listErr } = await supabase.rpc('list_user_backups');
      if (listErr) return;
      try { localStorage.setItem(ckey, String(Date.now())); } catch (_) {}
      const arr = Array.isArray(list) ? list : [];
      if (arr.length > 0) {
        const ageHours = (Date.now() - new Date(arr[0].backup_created_at).getTime())/3600000;
        if (ageHours < 23) return;
      }
      await supabase.rpc('create_user_backup');
    } catch (_) {}
  };

  const repairEvikLinks = async (userId) => {
    try { if (localStorage.getItem('evik_relink_v2') === '1') return; } catch (_) {}
    try {
      const { data: rows, error } = await supabase
        .from('ordskjonnhet_public')
        .select('word_norm')
        .eq('owner_nick', 'Evik');
      if (error) return;
      const evikSet = new Set((rows || []).map((r) => String(r.word_norm || '').trim().toLowerCase()));
      if (evikSet.size === 0) return;
      const repair = (w) => {
        const norm = String((w && w.text) || '').trim().toLowerCase();
        if (!norm || !evikSet.has(norm)) return w;
        if (w.ord && (w.ord.authored || w.ord.evik)) return w;
        const ord = { ...(w.ord || {}), evik: true, evikSourceNorm: norm, ownerNick: 'Evik' };
        const nw = { ...w, ord };
        nw.ord.evikSnapshot = evikContentSig(nw);
        return nw;
      };
      const walk = (node) => {
        if (Array.isArray(node)) return node.map(repair);
        if (!node || typeof node !== 'object') return node;
        const out = {};
        Object.keys(node).forEach((k) => {
          if (k === WORDS_KEY) out[k] = (node[k] || []).map(repair);
          else if (k === ORDER_KEY) out[k] = node[k];
          else out[k] = walk(node[k]);
        });
        return out;
      };
      setDb((cur) => {
        const next = {};
        Object.keys(cur || {}).forEach((cat) => { next[cat] = walk(cur[cat]); });
        return next;
      });
      try { localStorage.setItem('evik_relink_v2', '1'); } catch (_) {}
    } catch (_) {}
  };

  const loadCloudProfile = async (user, shouldLoadDatabase = true) => {
    if (!user?.id) return null;

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (profileError) throw profileError;

    if (profile) {
      const nextProfile = normalizeUserProfile({
        id: profile.id,
        email: profile.email || user.email || '',
        nickname: profile.nickname || '',
        photo: profile.avatar || null,
        registeredAt: profile.created_at || '',
        role: profile.role || 'user',
        status: profile.status || 'active',
        badges: profile.badges || [],
      });
      if (isProfileBlocked(nextProfile)) {
        await supabase.auth.signOut();
        throw new Error('profile blocked');
      }
      setUserProfile(nextProfile);
      setRegistrationDraft((current) => ({ ...nextProfile, password: current.password || '' }));
      awardRegistrationBadge();
      // Обновляем last_seen при каждом входе
      supabase.from('profiles').update({last_seen: new Date().toISOString()}).eq('id', user.id);
    } else {
      let pending = null;
      try {
        const raw = localStorage.getItem(STORAGE_KEYS.pendingRegistration);
        if (raw) pending = JSON.parse(raw);
      } catch (_) {}
      const pendingMatches = pending && pending.email && pending.email.toLowerCase() === (user.email || '').toLowerCase();
      if (pendingMatches) {
        const newProfile = {
          id: user.id,
          email: user.email || pending.email,
          nickname: pending.nickname,
          photo: pending.photo || null,
          registeredAt: new Date().toISOString(),
        };
        try {
          await supabase.from('profiles').insert({
            id: newProfile.id,
            email: newProfile.email,
            nickname: newProfile.nickname,
            avatar: newProfile.photo,
          });
          await supabase.from('user_databases').upsert({
            user_id: newProfile.id,
            data: createEmptyCloudDataPayload(),
          }, { onConflict: 'user_id' });
        } catch (_) {}
        setUserProfile(newProfile);
        applyCloudDataPayload(createEmptyCloudDataPayload());
        setRegistrationDraft((current) => ({ ...newProfile, password: '' }));
        try { localStorage.removeItem(STORAGE_KEYS.pendingRegistration); } catch (_) {}
        awardRegistrationBadge();
      } else if (shouldLoadDatabase) {
        // Только при первичной загрузке считаем отсутствие профиля настоящим.
        // На повторных вызовах (обновление токена) временный пустой ответ игнорируем.
        await supabase.auth.signOut();
        setUserProfile(normalizeUserProfile(null));
        setAuthStatus('Аккаунт найден. Войди по email и паролю, чтобы продолжить.');
      }
    }

    if (shouldLoadDatabase) {
      const { data: databaseRow, error: databaseError } = await supabase
        .from('user_databases')
        .select('data')
        .eq('user_id', user.id)
        .maybeSingle();

      if (databaseError) throw databaseError;
      if (databaseRow?.data) applyCloudDataPayload(databaseRow.data);
      cloudLoadedRef.current = true;
      // Подгружаем все фото пользователя (включая фото уровней) сразу,
      // чтобы они были на месте и в карточках, и в контекстном меню.
      const ordDeepLink = (() => { try { return /[?&]ord=/.test(window.location.search); } catch (e) { return false; } })();
      if (!ordDeepLink) {
        window.setTimeout(() => { loadWordPhotosInto(user.id); }, 300);
        // Миниатюры своих опубликованных слов — запасной источник фото для словаря.
        window.setTimeout(() => { loadOwnPublicThumbs(user.id); }, 800);
        window.setTimeout(() => { repairEvikLinks(user.id); }, 1200);
        // Выносим сырые встроенные фото (зеркало и уровни) в word_photos, чтобы база оставалась лёгкой.
        window.setTimeout(() => { migrateInlinePhotos(user.id); }, 1500);
        // Фоновое создание ежедневного снапшота (после загрузки UI).
        window.setTimeout(() => { maybeCreateDailyBackup(user.id); }, 3000);
      }
    }

    return profile;
  };

  useEffect(() => {
    writeJsonStorage(STORAGE_KEYS.data, db);
    writeJsonStorage(STORAGE_KEYS.colors, categoryColors);
    writeJsonStorage(STORAGE_KEYS.categoryMeta, categoryMeta);
    writeJsonStorage(STORAGE_KEYS.categoryOrder, categoryOrder);
    writeJsonStorage(STORAGE_KEYS.constructorGroups, constructorGroups);
    writeJsonStorage(STORAGE_KEYS.analysisLibrary, analysisLibrary);
    writeJsonStorage(STORAGE_KEYS.learningProgress, learningProgress);
    writeJsonStorage(STORAGE_KEYS.deckSubcategories, deckSubcategories);
    writeJsonStorage(STORAGE_KEYS.analyzerAliases, analyzerAliases);
    writeJsonStorage(STORAGE_KEYS.userProfile, userProfile);
    writeJsonStorage(STORAGE_KEYS.userRegistry, userRegistry);
    writeJsonStorage(STORAGE_KEYS.hiddenSkeleton, hiddenSkeletonCanonicalIds);
  }, [db, categoryColors, categoryMeta, categoryOrder, constructorGroups, analysisLibrary, learningProgress, deckSubcategories, analyzerAliases, userProfile, userRegistry, hiddenSkeletonCanonicalIds]);

  useEffect(() => {
    setCategoryOrder((currentOrder) => normalizeCategoryOrder(currentOrder, db));
    setConstructorGroups((currentGroups) => normalizeConstructorGroups(currentGroups, db));
  }, [db]);

  useEffect(() => {
    let isMounted = true;

    withTimeout(supabase.auth.getSession(), 8000, { data: { session: null } }).then(async ({ data }) => {
      const user = data?.session?.user;
      if (!isMounted || !user) return;

      try {
        await withTimeout(loadCloudProfile(user, true), 12000, null);
      } catch (error) {
        setRegistrationError(translateSupabaseError(error.message || 'Не удалось загрузить профиль из Supabase.'));
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') { setSessionExpired(false); }
      if (event === 'SIGNED_OUT') { setSessionExpired(true); }
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        setIsPasswordResetRequestOpen(false);
        setShouldPulseResetEmail(false);
        setShouldPulseRecoveryPassword(false);
        setAuthMode('login');
        setAuthStatus('Введите новый пароль для аккаунта.');
        setRegistrationError('');
      }

      const user = session?.user;
      if (!user) return;
      const shouldLoadDb = (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && !cloudLoadedRef.current;
      loadCloudProfile(user, shouldLoadDb).catch((error) => {
        setRegistrationError(translateSupabaseError(error.message || 'Не удалось обновить профиль Supabase.'));
      });
    });

    // Периодическая проверка сессии каждые 30 секунд
    const sessionCheck = setInterval(async () => {
      try {
        const hasLocalAuth = Object.keys(localStorage).some((k) => /sb-.*-auth-token/.test(k));
        if (!hasLocalAuth) return;
      } catch (_) {}
      let alive = false;
      try {
        const g = await supabase.auth.getSession();
        if (g && g.data && g.data.session) alive = true;
        if (!alive) {
          const r = await supabase.auth.refreshSession();
          if (r && r.data && r.data.session) alive = true;
          else if (!(r && r.error)) alive = true;
        }
      } catch (_) { alive = true; }
      if (!alive) setSessionExpired(true);
    }, 30000);

    return () => {
      isMounted = false;
      listener.subscription.unsubscribe();
      clearInterval(sessionCheck);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!userProfile.id) return undefined;
    if (!cloudLoadedRef.current) return undefined;

    const saveOnce = async () => {
      const { error } = await supabase
        .from('user_databases')
        .upsert({
          user_id: userProfile.id,
          data: getCloudDataPayload(),
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });
      return error;
    };
    const timeoutId = window.setTimeout(async () => {
      // Фоновая проверка: жива ли сессия Supabase перед сохранением.
      try {
        const g = await supabase.auth.getSession();
        if (!g || !g.data || !g.data.session) {
          let dead = false;
          try {
            const r = await supabase.auth.refreshSession();
            if (!(r && r.data && r.data.session) && (r && r.error)) dead = true;
          } catch (_) { dead = false; }
          if (dead) { setSessionExpired(true); return; }
        }
      } catch (_) {
        // ignore, попробуем сохранить и поймаем ошибку ниже
      }
      let error = await saveOnce();
      if (error) {
        await new Promise((r) => window.setTimeout(r, 2000));
        error = await saveOnce();
      }
      if (error) {
        const code = error.code || error.status || '';
        const msg = (error.message || '').toLowerCase();
        if (code === 401 || code === 403 || code === '401' || code === '403' || msg.includes('jwt') || msg.includes('auth')) {
          setSessionExpired(true);
        }
      }
    }, 1200);


    return () => window.clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisLibrary, categoryColors, categoryMeta, categoryOrder, constructorGroups, db, learningProgress, deckSubcategories, analyzerAliases, userProfile.id]);

  // Миграция корней: из старого поля word.root и из опубликованных слов в массив word.roots.
  useEffect(() => {
    if (rootMigrationDoneRef.current) return undefined;
    if (!userProfile.id) return undefined;
    if (!cloudLoadedRef.current) return undefined;
    try {
      if (localStorage.getItem('ord_root_migration_v2') === '1') { rootMigrationDoneRef.current = true; return undefined; }
    } catch (_) {}
    rootMigrationDoneRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('ordskjonnhet_public')
          .select('word_norm, data')
          .eq('owner_id', userProfile.id);
        if (cancelled) return;
        if (error) { rootMigrationDoneRef.current = false; return; }
        const rootByNorm = {};
        (Array.isArray(data) ? data : []).forEach((row) => {
          const r = normalizeRootValue((row && row.data && row.data.root) || '');
          const wn = normalizeWordText(String((row && row.word_norm) || '')).replace(/^å\s+/i, '');
          if (wn && r) rootByNorm[wn] = r;
        });
        const foundCount = Object.keys(rootByNorm).length;
        rootMigrationCountRef.current = 0;
        setDb((currentDb) => {
          const clone = JSON.parse(JSON.stringify(currentDb || {}));
          let added = 0;
          let touched = 0;
          forEachWordInDb(clone, (w) => {
            if (!w || !w.text) return;
            const existing = normalizeWordRoots(w);
            const haveKeys = new Set(existing.map((it) => normalizeRootKey(it.root)));
            let merged = existing;
            const wn = normalizeWordText(w.text).replace(/^å\s+/i, '');
            const pub = rootByNorm[wn] ? normalizeRootValue(rootByNorm[wn]) : '';
            if (pub && !haveKeys.has(normalizeRootKey(pub))) {
              merged = [...existing, { root: pub, translation: '' }];
              added += 1;
            }
            const hadLegacy = !!(w.root && String(w.root).trim());
            const curLen = Array.isArray(w.roots) ? w.roots.length : 0;
            if (merged.length > 0 && (merged.length !== curLen || hadLegacy)) {
              w.roots = merged.map((it) => ({ root: it.root, translation: it.translation || '' }));
              w.root = '';
              w.rootTranslation = '';
              touched += 1;
            }
          });
          rootMigrationCountRef.current = added;
          return touched > 0 ? clone : currentDb;
        });
        try { localStorage.setItem('ord_root_migration_v2', '1'); } catch (_) {}
        window.setTimeout(() => {
          notify(`Корни: найдено ${foundCount}, добавлено ${rootMigrationCountRef.current}`);
        }, 500);
      } catch (_) { rootMigrationDoneRef.current = false; }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile.id, db]);

  // Миграция публичного статуса: проставить ord.authored словам, опубликованным в облаке.
  // С авто-повтором: пытается, пока облако хоть раз не ответит (на случай нестабильности Supabase).
  useEffect(() => {
    if (authoredMigrationDoneRef.current) return undefined;
    if (!userProfile.id) return undefined;
    const migKey = 'ord_authored_migration_v2_' + userProfile.id;
    try {
      if (localStorage.getItem(migKey) === '1') { authoredMigrationDoneRef.current = true; return undefined; }
    } catch (_) {}
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const schedule = (ms) => { if (!cancelled) timer = window.setTimeout(run, ms); };
    const run = async () => {
      if (cancelled || authoredMigrationDoneRef.current) return;
      attempts += 1;
      if (attempts > 40) return;
      if (!userProfile.id) { schedule(20000); return; }
      if (!cloudLoadedRef.current) { schedule(8000); return; }
      try {
        const { data, error } = await supabase
          .from('ordskjonnhet_public')
          .select('word_norm, fullness_date')
          .eq('owner_id', userProfile.id)
          .eq('authored', true);
        if (cancelled) return;
        if (error || !Array.isArray(data)) { schedule(20000); return; }
        const pub = {};
        data.forEach((row) => {
          const wn = normalizeWordText(String((row && row.word_norm) || ''));
          if (wn) pub[wn] = (row && row.fullness_date) || null;
        });
        const foundCount = Object.keys(pub).length;
        authoredMigrationCountRef.current = 0;
        setDb((currentDb) => {
          const clone = JSON.parse(JSON.stringify(currentDb || {}));
          let changed = 0;
          forEachWordInDb(clone, (w) => {
            if (!w || !w.text) return;
            const wn = normalizeWordText(w.text);
            if (!(wn in pub)) return;
            if (w.ord && w.ord.authored) return;
            w.ord = { ...(w.ord || {}), authored: true };
            if (!w.ord.fullnessDate && pub[wn]) w.ord.fullnessDate = pub[wn];
            changed += 1;
          });
          authoredMigrationCountRef.current = changed;
          return changed > 0 ? clone : currentDb;
        });
        authoredMigrationDoneRef.current = true;
        try { localStorage.setItem(migKey, '1'); } catch (_) {}
      } catch (_) {
        schedule(20000);
      }
    };
    run();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile.id]);

  // Разовое ужимание превью существующих публичных слов (из имеющегося thumb, без скачивания полных фото).
  useEffect(() => {
    if (thumbShrinkDoneRef.current) return undefined;
    if (!userProfile.id) return undefined;
    try {
      if (localStorage.getItem('ord_thumb_shrink_v1') === '1') { thumbShrinkDoneRef.current = true; return undefined; }
    } catch (_) {}
    let cancelled = false;
    const run = async () => {
      if (cancelled || thumbShrinkDoneRef.current) return;
      try {
        const { data, error } = await supabase
          .from('ordskjonnhet_public')
          .select('id, data')
          .eq('owner_id', userProfile.id);
        if (cancelled) return;
        if (error || !Array.isArray(data)) return;
        let changed = 0;
        for (const row of data) {
          if (cancelled) return;
          const d = (row && row.data) || {};
          const thumb = d.photo && d.photo.thumb;
          if (!thumb || typeof thumb !== 'string') continue;
          const smaller = await shrinkThumbDataUrl(thumb, 400, 0.5);
          if (!smaller) continue;
          const nextData = { ...d, photo: { ...d.photo, thumb: smaller } };
          const { error: upErr } = await supabase
            .from('ordskjonnhet_public')
            .update({ data: nextData })
            .eq('id', row.id);
          if (!upErr) changed += 1;
        }
        thumbShrinkDoneRef.current = true;
        try { localStorage.setItem('ord_thumb_shrink_v1', '1'); } catch (_) {}
        if (changed > 0) {
          window.setTimeout(() => { notify(`Превью ужаты: ${changed}`); }, 500);
        }
      } catch (_) { /* повторим при следующем входе */ }
    };
    const t = window.setTimeout(run, 4000);
    return () => { cancelled = true; window.clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile.id]);

  const loadPhraseGames = async () => {
    if (!userProfile.id) {
      setPhraseGames([]);
      setFinishedPhraseTexts([]);
      return;
    }

    const { data: games, error: gamesError } = await supabase
      .from('phrase_games')
      .select('id, author_id, status, created_at, updated_at')
      .in('status', ['active', 'finished'])
      .order('updated_at', { ascending: false })
      .limit(50);

    if (gamesError) {
      setPhraseGamesError(translateSupabaseError(gamesError.message));
      return;
    }

    const gameIds = (games || []).map((game) => game.id);
    if (gameIds.length === 0) {
      setPhraseGames([]);
      setFinishedPhraseTexts([]);
      setPhraseGamesError('');
      return;
    }

    const { data: moves, error: movesError } = await supabase
      .from('phrase_moves')
      .select('id, game_id, user_id, move_index, word_text, word_category, word_path, word_level, created_at')
      .in('game_id', gameIds)
      .order('move_index', { ascending: true });

    if (movesError) {
      setPhraseGamesError(translateSupabaseError(movesError.message));
      return;
    }

    const participantIds = [
      ...(games || []).map((game) => game.author_id),
      ...(moves || []).map((move) => move.user_id),
    ].filter(Boolean);
    const profileIds = [...new Set(participantIds)];
    const { data: profiles } = profileIds.length > 0
      ? await supabase.rpc('get_public_nicknames', { ids: profileIds })
      : { data: [] };
    const profileMap = (profiles || []).reduce((map, profile) => ({
      ...map,
      [profile.id]: profile.nickname,
    }), {});

    const movesByGame = (moves || []).reduce((map, move) => {
      const currentMoves = map[move.game_id] || [];
      return {
        ...map,
        [move.game_id]: [...currentMoves, move],
      };
    }, {});

    const enrichedGames = (games || []).map((game) => {
      const gameMoves = movesByGame[game.id] || [];
      const lastGameMove = gameMoves[gameMoves.length - 1];
      const lastMoveAuthorName = lastGameMove
        ? (lastGameMove.user_id === userProfile.id
          ? userProfile.nickname
          : profileMap[lastGameMove.user_id] || 'Участник')
        : '';
      return {
        lastMoveAuthorName,
        ...game,
        authorName: game.author_id === userProfile.id
          ? userProfile.nickname
          : profileMap[game.author_id] || 'Участник',
        moves: gameMoves,
      };
    });

    const nextGames = enrichedGames.filter((game) => game.status === 'active').sort((left, right) => {
      const leftOwn = left.author_id === userProfile.id ? 0 : 1;
      const rightOwn = right.author_id === userProfile.id ? 0 : 1;
      return leftOwn - rightOwn || new Date(right.updated_at) - new Date(left.updated_at);
    });
    const nextFinishedPhraseTexts = enrichedGames
      .filter((game) => game.status === 'finished')
      .sort((left, right) => new Date(left.created_at) - new Date(right.created_at))
      .map((game, index) => {
        const participantNames = [...new Set((game.moves || []).map((move) => (
          move.user_id === userProfile.id
            ? userProfile.nickname
            : profileMap[move.user_id] || 'Участник'
        )))];

        return {
          id: `phrase ${formatConstructedPhraseNumber(index + 1)}`,
          source: 'phrase-game',
          gameId: game.id,
          text: formatPhraseDisplayText(game.moves.map((move) => move.word_text).filter(Boolean).join(' ')),
          authorName: game.authorName,
          participants: participantNames,
          moveCount: game.moves.length,
          moves: game.moves,
          createdAt: game.created_at,
          updatedAt: game.updated_at,
        };
      })
      .filter((entry) => entry.text);

    setPhraseGames(nextGames);
    setFinishedPhraseTexts(nextFinishedPhraseTexts);
    setPhraseGamesError('');
  };

  const loadMySubscriptions = async () => {
    if (!userProfile.id) { setMySubscriptions([]); return; }
    const { data, error } = await supabase
      .from('subscriptions')
      .select('target_id, starred')
      .eq('subscriber_id', userProfile.id);
    if (error) { setMySubscriptions([]); return; }
    setMySubscriptions(data || []);
  };

  const toggleSubscription = async (targetId, starred = false) => {
    if (!userProfile.id || !targetId || targetId === userProfile.id) return;
    const existing = mySubscriptions.find((s) => s.target_id === targetId);
    let opError = null;
    if (existing) {
      const r = await supabase.from('subscriptions').delete().eq('subscriber_id', userProfile.id).eq('target_id', targetId);
      opError = r.error;
    } else {
      const r = await supabase.from('subscriptions').insert({ subscriber_id: userProfile.id, target_id: targetId, starred });
      opError = r.error;
    }
    if (opError) { console.error('subscription toggle error:', opError); return; }
    await loadMySubscriptions();
    await loadPhraseDatabases();
  };

  const toggleSubscriptionStar = async (targetId) => {
    if (!userProfile.id || !targetId || targetId === userProfile.id) return;
    const existing = mySubscriptions.find((s) => s.target_id === targetId);
    if (!existing) {
      await supabase.from('subscriptions').insert({ subscriber_id: userProfile.id, target_id: targetId, starred: true });
    } else if (existing.starred) {
      await supabase.from('subscriptions').update({ starred: false })
        .eq('subscriber_id', userProfile.id).eq('target_id', targetId);
    } else {
      await supabase.from('subscriptions').update({ starred: true })
        .eq('subscriber_id', userProfile.id).eq('target_id', targetId);
    }
    await loadMySubscriptions();
    await loadPhraseDatabases();
  };


  const loadSubscriptionStats = async () => {
    if (!userProfile.id) return;
    const { data, error } = await supabase.rpc('get_subscription_stats', { p_user_id: userProfile.id });
    if (error || !data || data.length === 0) return;
    setSubscriptionStats({
      subscribers_count: data[0].subscribers_count || 0,
      starred_count: data[0].starred_count || 0,
    });
  };

  const loadPhraseDatabases = async () => {
    if (!userProfile.id) return;
    const { data, error } = await supabase.rpc('get_phrase_databases_for_me');
    if (error) { setPhraseDatabasesCache([]); return; }
    setPhraseDatabasesCache(data || []);
  };


  useEffect(() => {
    if (!userProfile.id) return undefined;
    if (!CONSTRUCTOR_ENABLED) return undefined;

    loadPhraseGames();
    loadMySubscriptions();
    loadPhraseDatabases();
    loadSubscriptionStats();
    const channel = supabase
      .channel('phrase-games-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'phrase_games' }, loadPhraseGames)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'phrase_moves' }, loadPhraseGames)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile.id]);

  useEffect(() => {
    const timerId = window.setInterval(() => setPhraseClockTick(Date.now()), 30000);
    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    if (!userProfile.id) {
      setOnlineUserIds([]);
      return undefined;
    }

    const channel = supabase.channel('city-108-online-users', {
      config: {
        presence: {
          key: userProfile.id,
        },
      },
    });

    const syncPresence = () => {
      const presenceState = channel.presenceState();
      const nextUserIds = Object.entries(presenceState)
        .flatMap(([key, presences]) => (
          Array.isArray(presences)
            ? presences.map((presence) => presence.user_id || key)
            : [key]
        ))
        .filter(Boolean);

      setOnlineUserIds([...new Set(nextUserIds)]);
    };

    channel
      .on('presence', { event: 'sync' }, syncPresence)
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            user_id: userProfile.id,
            nickname: userProfile.nickname,
            online_at: new Date().toISOString(),
          });
        }
      });

    return () => {
      channel.untrack();
      supabase.removeChannel(channel);
    };
  }, [userProfile.id, userProfile.nickname]);

  // Индекс историй: токен -> слова-владельцы; и тексты историй по слову.
  const storyOwnersIndex = useMemo(() => {
    const index = {};
    const stories = {};
    collectWordsFromDb(db).forEach(({ word }) => {
      const story = (word && word.story) ? String(word.story) : '';
      if (!story.trim()) return;
      const ownerText = (word.text || '').trim();
      if (!ownerText) return;
      if (!stories[ownerText]) stories[ownerText] = story;
      const tokens = new Set(story.toLowerCase().split(/[^\p{L}]+/u).filter(Boolean));
      tokens.forEach((tok) => {
        if (!index[tok]) index[tok] = [];
        if (!index[tok].includes(ownerText)) index[tok].push(ownerText);
      });
    });
    return { index, stories };
  }, [db]);

  // Для слова: до 5 слов-владельцев историй, где оно встречается (с формами),
  // и в каждой — предложения, содержащие это слово.
  const getWordTexts = (word) => {
    if (!word) return [];
    const forms = { ...(word.forms || {}), ...(word.manualForms || {}) };
    const variants = new Set();
    const addVar = (s) => {
      const v = (s || '').toLowerCase().replace(/^å\s+/, '').trim();
      if (v) v.split(/[^\p{L}]+/u).filter(Boolean).forEach((t) => variants.add(t));
    };
    addVar(word.text);
    Object.values(forms).forEach(addVar);
    const ownText = (word.text || '').trim().toLowerCase();
    const { index, stories } = storyOwnersIndex;
    const ownerTexts = [];
    variants.forEach((tok) => {
      (index[tok] || []).forEach((ownerText) => {
        if (ownerText.toLowerCase() === ownText) return;
        if (!ownerTexts.includes(ownerText)) ownerTexts.push(ownerText);
      });
    });
    const hasVariant = (sentence) =>
      sentence.toLowerCase().split(/[^\p{L}]+/u).filter(Boolean).some((t) => variants.has(t));
    const result = [];
    ownerTexts.slice(0, 5).forEach((ownerText) => {
      const story = stories[ownerText] || '';
      const sentences = (story.match(/[^.!?\u2026]+[.!?\u2026]*/g) || [])
        .map((s) => s.trim()).filter(Boolean);
      const matched = sentences.filter(hasVariant);
      result.push({ text: ownerText, sentences: matched.length ? matched : [story.trim()] });
    });
    return result;
  };

  const analyzerMap = useMemo(() => {
    const map = {};
    const wordEntries = [];
    const entriesByTerm = {};
    const splitPhrases = [];
    const skeletonKeySet = new Set(Object.keys(fallbackDb));

    const addAnalyzerEntry = (term, entry, isBase) => {
      const key = normalizeWordText(term || '').replace(/^å\s+/i, '');
      if (!key) return;

      const tagged = { ...entry, isBaseMatch: !!isBase };
      if (!map[key]) map[key] = tagged;

      const currentEntries = entriesByTerm[key] || [];
      const alreadyExists = currentEntries.some((candidate) => (
        candidate.text === entry.text
        && candidate.category === entry.category
        && candidate.subcategory === entry.subcategory
        && candidate.type === entry.type
        && candidate.tense === entry.tense
      ));

      if (!alreadyExists) entriesByTerm[key] = [...currentEntries, tagged];
    };

    const addWordsFromNode = (category, node, path = []) => {
      const normalizedNode = normalizeNode(node);
      const subcategory = arrayToPath(path);
      const words = getWordsFromNode(normalizedNode);

      if (subcategory && words.length > 0) {
        const meta = categoryMeta[getSubcategoryMetaKey(category, subcategory)];
        const icon = meta?.iconId ? findGrammarIcon(grammarIconCatalog, meta.iconId) : null;
        const entryBase = {
          category,
          subcategory,
          color: categoryColors[category],
          icon,
          isSkeleton: skeletonKeySet.has(categoryMeta[getCategoryMetaKey(category)]?.canonicalId),
        };

        words.forEach((word) => {
          const forms = {
            ...(word.forms || {}),
            ...(word.manualForms || {}),
          };
          const wordEntry = {
            ...entryBase,
            text: word.text,
            type: word.type || '',
            gender: word.gender || null,
            root: normalizeWordText(word.root || ''),
            rootTranslation: normalizeWordText(word.rootTranslation || ''),
            roots: normalizeWordRoots(word),
            translations: (word.translations || []).filter(Boolean),
            photo: (() => {
              const lvl = LEVEL_ORDER.includes(word.lvl) ? word.lvl : 'A1';
              const pl = getPhotoForLevel(word, lvl);
              if (pl && pl.dataUrl) return pl;
              const lvls = buildLevels(word);
              for (let i = 0; i < LEVEL_ORDER.length; i += 1) {
                const s = lvls[LEVEL_ORDER[i]];
                if (s && s.photo && s.photo.dataUrl) return s.photo;
              }
              if (word.photo && word.photo.dataUrl) return word.photo;
              if (word.ord && word.ord.photo && word.ord.photo.dataUrl) return word.ord.photo;
              const norm = normalizeWordText(word.text || '');
              if (norm && ownPublicThumbs && ownPublicThumbs[norm]) return { dataUrl: ownPublicThumbs[norm], name: '', type: 'image/jpeg' };
              return null;
            })(),
          };
          wordEntries.push(wordEntry);

          addAnalyzerEntry(word.text, wordEntry, true);

          // Индекс разрывных фразовых глаголов: "verb particle" (2 слова, второе — частица).
          (() => {
            const wt = normalizeWordText(word.text || '').replace(/^å\s+/i, '');
            const parts = wt.split(/\s+/).filter(Boolean);
            if (parts.length !== 2) return;
            const first = parts[0];
            const particle = parts[1];
            if (!PHRASAL_PARTICLES.has(particle)) return;
            const stem = first.endsWith('e') ? first.slice(0, -1) : first;
            const firstForms = new Set([first]);
            Object.values(forms).forEach((f) => {
              const fw = normalizeWordText(String(f || '')).replace(/^å\s+/i, '').split(/\s+/)[0];
              if (fw) firstForms.add(fw);
            });
            splitPhrases.push({ first, stem, particle, firstForms, entry: { ...wordEntry } });
          })();

          Object.entries(forms).forEach(([formKey, form]) => {
            if (form) addAnalyzerEntry(form, { ...wordEntry, formKey }, false);
          });

          if (word.type === 'verb') {
            const verbEntryBase = {
              ...wordEntry,
              icon: null,
            };
            const infinitive = (forms.infinitive || word.text || '').toLowerCase().replace(/^å\s+/i, '');
            const present = forms.present?.toLowerCase();
            const past = forms.past?.toLowerCase();
            const tenseEntries = [
              [present, verbTenseIcons.present],
              [past, verbTenseIcons.past],
              [infinitive ? `skal ${infinitive}` : '', verbTenseIcons.future],
              [infinitive ? `vil ${infinitive}` : '', verbTenseIcons.future],
              [infinitive ? `kommer til å ${infinitive}` : '', verbTenseIcons.future],
            ];

            tenseEntries.forEach(([phrase, tenseIcon]) => {
              if (!phrase) return;
              addAnalyzerEntry(phrase, {
                ...verbEntryBase,
                icon: tenseIcon,
                tense: tenseIcon.id,
              }, false);
            });
          }
        });
      }

      Object.entries(normalizedNode).forEach(([subcategoryName, childNode]) => {
        if (subcategoryName === WORDS_KEY || subcategoryName === ORDER_KEY) return;
        addWordsFromNode(category, childNode, [...path, subcategoryName]);
      });
    };

    Object.entries(db).forEach(([category, categoryNode]) => {
      addWordsFromNode(category, categoryNode);
    });

    const evikCanonColor = {};
    Object.keys(db).forEach((cat) => {
      const cid = (categoryMeta[getCategoryMetaKey(cat)] || {}).canonicalId || getInferredCanonicalCategoryId(cat);
      if (cid && !(cid in evikCanonColor) && categoryColors[cat]) evikCanonColor[cid] = categoryColors[cat];
    });
    (evikAnalyzerWords || []).forEach((ew) => {
      const text = String(ew.text || '').trim();
      if (!text) return;
      let canon = canonicalCategoryAliases[String(ew.pos || '').toLowerCase()] || null;
      // Глагольные выражения (фраза со спряжением) красим и относим как глаголы.
      if (canon === 'Frase' && Array.isArray(ew.forms) && ew.forms.length > 0) canon = 'Verb';
      const color = (canon && (evikCanonColor[canon] || defaultColors[canon])) || '#7c3aed';
      const entry = {
        category: canon || 'Evik',
        subcategory: '',
        color,
        icon: null,
        isSkeleton: false,
        text,
        type: ew.pos || '',
        gender: ew.gender || null,
        root: normalizeWordText(ew.root || ''),
        rootTranslation: '',
        roots: [],
        translations: (ew.translations || []).filter(Boolean),
        photo: ew.photoUrl ? { dataUrl: ew.photoUrl, position: 50 } : null,
        isEvikPublic: true,
      };
      if (entry.root) {
        wordEntries.push({ ...entry, roots: [{ root: entry.root, translation: entry.rootTranslation || '' }] });
      }
      const addEvikTerm = (term, isBase) => {
        const k = normalizeWordText(term || '').replace(/^å\s+/i, '');
        if (!k || map[k]) return;
        addAnalyzerEntry(term, entry, isBase);
      };
      addEvikTerm(text, true);
      (ew.forms || []).forEach((f) => addEvikTerm(f, false));
      // split-индекс для публичных слов Эвика: verb + particle
      (() => {
        const wt = normalizeWordText(text).replace(/^\u00e5\s+/i, '');
        const pp = wt.split(/\s+/).filter(Boolean);
        if (pp.length !== 2) return;
        const first = pp[0];
        const particle = pp[1];
        if (!PHRASAL_PARTICLES.has(particle)) return;
        const stem = first.endsWith('e') ? first.slice(0, -1) : first;
        const firstForms = new Set([first]);
        (ew.forms || []).forEach((f) => {
          const fw = normalizeWordText(String(f || '')).replace(/^\u00e5\s+/i, '').split(/\s+/)[0];
          if (fw) firstForms.add(fw);
        });
        splitPhrases.push({ first, stem, particle, firstForms, entry: { ...entry } });
      })();
    });

    Object.defineProperty(map, '__wordEntries', {
      value: wordEntries,
      enumerable: false,
    });
    Object.defineProperty(map, '__entriesByTerm', {
      value: entriesByTerm,
      enumerable: false,
    });
    Object.defineProperty(map, '__splitPhrases', {
      value: splitPhrases,
      enumerable: false,
    });

    return map;
  }, [db, categoryColors, categoryMeta, ownPublicThumbs, evikAnalyzerWords]);

  const analyzerRootEntries = useMemo(() => {
    const entries = (analyzerMap.__wordEntries || Object.values(analyzerMap))
      .filter((entry) => entry.text)
      .filter((entry, index, items) => (
        items.findIndex((item) => (
          item.text === entry.text
          && item.category === entry.category
          && item.subcategory === entry.subcategory
        )) === index
      ));
    const manualRoots = entries
      .flatMap((entry) => entry.roots || [])
      .filter((item, index, items) => (
        items.findIndex((candidate) => normalizeRootKey(candidate.root) === normalizeRootKey(item.root)) === index
      ));

    if (manualRoots.length === 0) return [];

    const manualRootKeys = manualRoots
      .map(({ root }) => normalizeRootKey(root))
      .filter((key) => key.length >= 2);

    return manualRoots.flatMap(({ root, translation }) => {
      const rootKey = normalizeRootKey(root);
      if (rootKey.length < 2) return [];
      return entries
        .filter((entry) => {
          const ownRoots = (entry.roots || [])
            .map((item) => normalizeRootKey(item.root))
            .filter(Boolean);
          if (ownRoots.includes(rootKey)) return true;
          if (ownRoots.length > 0) return false;
          const textKey = normalizeRootKey(entry.text);
          if (!textKey.startsWith(rootKey)) return false;
          const hasLongerRoot = manualRootKeys.some(
            (key) => key.length > rootKey.length && textKey.startsWith(key),
          );
          return !hasLongerRoot;
        })
        .map((entry) => ({ ...entry, root, rootTranslation: translation }));
    });
  }, [analyzerMap]);

  const analyzerMaxPhraseLength = useMemo(() => (
    Math.max(
      1,
      ...Object.keys(analyzerMap).map((word) => word.split(/\s+/).filter(Boolean).length),
    )
  ), [analyzerMap]);
  const databaseSearchResults = useMemo(() => {
    const query = normalizeWordText(databaseSearch);
    if (!query) return [];
    const cleanQuery = query.replace(/^å\s+/i, '');
    const canSearchByRoot = cleanQuery.length >= 3;

    const results = [];
    const addMatchesFromNode = (category, node, path = []) => {
      const normalizedNode = normalizeNode(node);
      const subcategory = arrayToPath(path);
      const words = getWordsFromNode(normalizedNode);

      words.forEach((word) => {
        const forms = {
          ...(word.forms || {}),
          ...(word.manualForms || {}),
        };
        const terms = [
          word.text,
          ...Object.values(forms),
          word.type === 'verb' ? `å ${word.text}` : '',
        ]
          .filter(Boolean)
          .map((term) => normalizeWordText(term).replace(/^å\s+/i, ''));
        const translationTerms = (word.translations || [])
          .filter(Boolean)
          .map((translation) => normalizeWordText(translation));
        const rootTerms = normalizeWordRoots(word).map((item) => normalizeRootKey(item.root)).filter(Boolean);
        const matchingOwnRoot = canSearchByRoot && rootTerms.some((root) => (
          root.includes(cleanQuery) || cleanQuery.includes(root)
        ));
        const exactOwnRoot = rootTerms.includes(normalizeRootKey(cleanQuery));
        const hasExactMatch = terms.includes(cleanQuery);
        const translationMatch = translationTerms.find((translation) => (
          translation === query || (query.length >= 3 && translation.includes(query))
        ));
        const hasRootMatch = canSearchByRoot && [
          ...terms,
          ...rootTerms,
        ].some((term) => term.includes(cleanQuery) || cleanQuery.includes(term));

        const shortText = normalizeWordText(word.text || '').replace(/^å\s+/i, '');
        const isShortMatch = !!cleanQuery && shortText.length <= 3 && shortText.startsWith(cleanQuery);

        if (!hasExactMatch && !translationMatch && !hasRootMatch && !isShortMatch) return;

        results.push({
          word: word.text,
          level: word.lvl,
          category,
          subcategory,
          pathLabel: [category, ...path].join(' / '),
          matchType: hasExactMatch ? 'word' : isShortMatch ? 'word' : translationMatch ? 'translation' : 'root',
          isShortMatch,
          matchedTranslation: translationMatch || '',
          hasMatchingOwnRoot: matchingOwnRoot,
          hasExactOwnRoot: exactOwnRoot,
          authored: !!(word.ord && (word.ord.authored || word.ord.evik)),
        });
      });

      Object.entries(normalizedNode).forEach(([subcategoryName, childNode]) => {
        if (subcategoryName === WORDS_KEY || subcategoryName === ORDER_KEY) return;
        addMatchesFromNode(category, childNode, [...path, subcategoryName]);
      });
    };

    Object.entries(db).forEach(([category, categoryNode]) => {
      if (category === ANALYZER_DECK_CATEGORY) return;
      addMatchesFromNode(category, categoryNode);
    });

    return results.filter((result, index, items) => (
      items.findIndex((item) => (
        item.word === result.word
        && item.category === result.category
        && item.subcategory === result.subcategory
      )) === index
    )).sort((left, right) => {
      const getPriority = (result) => {
        if (result.isShortMatch) return -1;
        if (result.hasMatchingOwnRoot) return 0;
        if (result.matchType === 'word') return 1;
        if (result.matchType === 'translation') return 2;
        return 3;
      };

      return getPriority(left) - getPriority(right)
        || ((left.isShortMatch && right.isShortMatch) ? ((left.word || '').length - (right.word || '').length) : 0)
        || left.word.localeCompare(right.word)
        || left.pathLabel.localeCompare(right.pathLabel);
    });
  }, [databaseSearch, db]);

  useEffect(() => {
    const raw = (databaseSearch || '').trim();
    if (!raw) { setDbPublicResults([]); return undefined; }
    const safe = raw.replace(/[,()%*]/g, ' ').trim().replace(/^å\s+/i, '');
    if (!safe || safe.length < 2) { setDbPublicResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      const enc = encodeURIComponent('*' + safe + '*');
      const url = SUPABASE_URL + '/rest/v1/ordskjonnhet_public?select=word_norm,word_text,owner_nick,completeness,owner_id,pos,avatar:data->>ownerAvatar&authored=eq.true&or=(word_text.ilike.' + enc + ',word_norm.ilike.' + enc + ')&order=completeness.desc&limit=20';
      fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Accept: 'application/json' }, cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => { if (!cancelled) setDbPublicResults(Array.isArray(data) ? data : []); })
        .catch(() => { if (!cancelled) setDbPublicResults([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [databaseSearch, supabase]);

  const posRu = (pp) => ({ noun: 'сущ.', verb: 'глаг.', adjective: 'прил.', adverb: 'нареч.', preposition: 'предл.', pronoun: 'мест.', conjunction: 'союз', numeral: 'числ.', interjection: 'межд.', phrase: 'фраза' }[String(pp || '').toLowerCase()] || '');
  const dbPublicExtra = useMemo(() => {
    if (!dbPublicResults.length) return [];
    const ranked = [...dbPublicResults].sort((a, b) => (String(a.owner_nick||'').trim().toLowerCase()==='evik'?0:1) - (String(b.owner_nick||'').trim().toLowerCase()==='evik'?0:1));
    const byWord = new Map();
    for (const r of ranked) {
      const wn = String(r.word_norm || '').trim().toLowerCase();
      const key = wn + '|' + String(r.pos || 'noun');
      if (!byWord.has(key)) byWord.set(key, { ...r, authors: [] });
      byWord.get(key).authors.push(r);
    }
    const out = [];
    for (const entry of byWord.values()) {
      out.push(entry);
    }
    return out;
  }, [dbPublicResults, databaseSearchResults, userProfile]);

  const newsKnownWords = useMemo(() => {
    const s = new Set();
    try {
      collectWordsFromDb(db).forEach((e) => {
        const w = String((e.word && e.word.text) || '').trim().toLowerCase().replace(/^(en|ei|et|å)\s+/i, '');
        if (w) s.add(w);
      });
    } catch (err) {}
    return s;
  }, [db]);

  const profileStats = useMemo(() => {
    const wordEntries = collectWordsFromDb(db);
    const uniqueWords = new Set(wordEntries.map((entry) => normalizeWordText(entry.word?.text || '')).filter(Boolean));
    const subcategoryKeys = new Set();

    const collectSubcategories = (category, node, path = []) => {
      const normalizedNode = normalizeNode(node);
      Object.entries(normalizedNode).forEach(([subcategoryName, childNode]) => {
        if (subcategoryName === WORDS_KEY || subcategoryName === ORDER_KEY) return;
        const nextPath = [...path, subcategoryName];
        subcategoryKeys.add(`${category}${PATH_SEPARATOR}${arrayToPath(nextPath)}`);
        collectSubcategories(category, childNode, nextPath);
      });
    };

    Object.entries(db).forEach(([category, categoryNode]) => {
      collectSubcategories(category, categoryNode);
    });

    const countListItems = (key) => wordEntries.reduce((sum, entry) => (
      sum + (Array.isArray(entry.word?.[key]) ? entry.word[key].length : 0)
    ), 0);

    return {
      categories: Object.keys(db).length,
      subcategories: subcategoryKeys.size,
      words: uniqueWords.size,
      wordCards: wordEntries.length,
      translations: countListItems('translations'),
      synonyms: countListItems('synonyms'),
      antonyms: countListItems('antonyms'),
      roots: wordEntries.reduce((sum, entry) => sum + normalizeWordRoots(entry.word).length, 0),
      photos: wordEntries.filter((entry) => entry.word?.photo?.dataUrl).length,
      savedTexts: analysisLibrary.texts.length,
      archives: analysisLibrary.archives.length,
      learned: Object.keys(learningProgress.learned || {}).length,
      difficult: Object.keys(learningProgress.difficult || {}).length,
      totalSeen: learningProgress.totalSeen || 0,
    };
  }, [analysisLibrary, db, learningProgress]);

  const onlineUserCount = Math.max(onlineUserIds.length, userProfile.id ? 1 : 0);

  const phraseGameRows = useMemo(() => phraseGames.map((game) => {
    const moves = game.moves || [];
    const firstMove = moves[0];
    const lastMove = moves[moves.length - 1];
    const lastMoveByCurrentUser = lastMove?.user_id === userProfile.id;
    const isAuthor = game.author_id === userProfile.id;
    const moreThanTwoOnline = onlineUserCount > 2;
    const authorPauseMs = isAuthor && moreThanTwoOnline && lastMove && !lastMoveByCurrentUser
      ? Math.max(0, AUTHOR_MOVE_PAUSE_MS - (phraseClockTick - new Date(lastMove.created_at).getTime()))
      : 0;
    const canMove = game.status === 'active'
      && moves.length > 0
      && !lastMoveByCurrentUser
      && authorPauseMs === 0;
    const moveStatusText = canMove
      ? 'можно ходить'
      : authorPauseMs > 0
        ? `пауза ${Math.ceil(authorPauseMs / 60000)} мин`
        : 'ждем ход';
    const moveBlockReason = authorPauseMs > 0
      ? 'Сейчас пауза автора. Если больше двух участников онлайн, автор сможет ходить через 10 минут после чужого хода.'
      : 'Сейчас ход другого участника. Выберите зеленую фразу или начните новую.';

    return {
      ...game,
      canMove,
      moveStatusText,
      moveBlockReason,
      authorPauseMs,
      startText: firstMove?.word_text || 'Пустая фраза',
      fullText: moves.map((move) => move.word_text).filter(Boolean).join(' '),
      lastText: lastMove?.word_text || '',
      moveCount: moves.length,
    };
  }), [onlineUserCount, phraseClockTick, phraseGames, userProfile.id]);

  const availablePhraseCount = phraseGameRows.filter((game) => game.canMove).length;
  const activePhraseGame = phraseGameRows.find((game) => game.id === activePhraseGameId) || null;
  const canCancelSentence = (() => {
    if (!activePhraseGameId) return true;
    if (isWatchingPhrase) return false;
    if (!activePhraseGame) return false;
    const moves = activePhraseGame.moves || [];
    const lastMove = moves.at(-1);
    if (!lastMove) return false;
    return lastMove.user_id === userProfile.id;
  })();
  const scoredFinishedPhraseTexts = useMemo(() => {
    const photoWordTerms = collectPhotoWordTerms(db);
    return finishedPhraseTexts.map((phrase) => ({
      ...phrase,
      photoScore: getPhrasePhotoScore(phrase.moves, photoWordTerms),
    }));
  }, [db, finishedPhraseTexts]);
  const exampleTexts = useMemo(
    () => [...analysisLibrary.texts, ...scoredFinishedPhraseTexts],
    [analysisLibrary.texts, scoredFinishedPhraseTexts],
  );
  const activePhraseMoveSignature = activePhraseGame?.moves
    ?.map((move) => `${move.id}:${move.word_text}:${move.word_category}`)
    .join('|') || '';
  const constructorSentence = activePhraseGame
    ? phraseMovesToSentenceItems(activePhraseGame.moves || [])
    : sentence;
  const shouldShowNewPhraseButton = isConstructorOpen;
  const shouldShowCheckMovesButton = Boolean(phraseGameRows.length > 0 || activePhraseGame || phraseGamesError);
  const shouldShowPhraseToolbar = Boolean(
    shouldShowNewPhraseButton
    || shouldShowCheckMovesButton
    || activePhraseGame?.author_id === userProfile.id,
  );
  const orderedCategoriesAll = useMemo(() => normalizeCategoryOrder(categoryOrder, db), [categoryOrder, db]);
  const orderedCategories = useMemo(() => orderedCategoriesAll.filter((cat) => {
    if (cat === ANALYZER_DECK_CATEGORY) return false;
    const cid = categoryMeta[getCategoryMetaKey(cat)]?.canonicalId || getInferredCanonicalCategoryId(cat);
    if (!cid) return true;
    if (!Object.keys(fallbackDb).includes(cid)) return true;
    return !hiddenSkeletonCanonicalIds.includes(cid);
  }), [orderedCategoriesAll, categoryMeta, hiddenSkeletonCanonicalIds]);
  const normalizedConstructorGroups = useMemo(() => normalizeConstructorGroups(constructorGroups, db), [constructorGroups, db]);
  const visibleConstructorGroups = normalizedConstructorGroups;
  const constructorGroupById = useMemo(() => visibleConstructorGroups.reduce((map, group) => ({
    ...map,
    [group.id]: group,
  }), {}), [visibleConstructorGroups]);
  const constructorGroupLabels = useMemo(() => visibleConstructorGroups.reduce((map, group) => ({
    ...map,
    [group.id]: group.label,
  }), {}), [visibleConstructorGroups]);
  const constructorSourceTargets = useMemo(
    () => collectConstructorSourceTargets(db, orderedCategories),
    [db, orderedCategories],
  );
  const sectionTargets = useMemo(() => collectSectionTargetsFromDb(
    Object.fromEntries(orderedCategories.map((category) => [category, db[category]])),
  ), [db, orderedCategories]);
  const categoryCanonicalMap = useMemo(() => Object.keys(db).reduce((map, category) => {
    const metaCanonicalId = categoryMeta[getCategoryMetaKey(category)]?.canonicalId;
    return {
      ...map,
      [category]: metaCanonicalId || getInferredCanonicalCategoryId(category) || category,
    };
  }, {}), [db, categoryMeta]);
  const categoryColorByRole = useMemo(() => Object.entries(db).reduce((colors, [category]) => {
    const canonicalId = categoryCanonicalMap[category] || category;
    return {
      ...colors,
      [category]: categoryColors[category] || defaultColors[canonicalId],
      [canonicalId]: categoryColors[category] || defaultColors[canonicalId] || categoryColors[canonicalId],
    };
  }, { ...defaultColors, ...categoryColors }), [categoryCanonicalMap, categoryColors, db]);

  const categoryTextThemeByRole = useMemo(() => {
    const out = {};
    Object.keys(db).forEach((category) => {
      const meta = categoryMeta[getCategoryMetaKey(category)] || {};
      const theme = meta.textTheme || null;
      out[category] = theme;
      const cid = categoryCanonicalMap[category] || getInferredCanonicalCategoryId(category);
      if (cid && !(cid in out)) out[cid] = theme;
    });
    return out;
  }, [db, categoryMeta, categoryCanonicalMap]);

  const categoryTextColorByRole = useMemo(() => {
    const out = {};
    Object.keys(categoryColorByRole).forEach((role) => {
      out[role] = textColorForTheme(categoryTextThemeByRole[role], categoryColorByRole[role]);
    });
    return out;
  }, [categoryColorByRole, categoryTextThemeByRole]);

  const getCanonicalCategoryForRole = (role) => categoryCanonicalMap[role]
    || getInferredCanonicalCategoryId(role)
    || role;

  const skeletonCategoryKeys = Object.keys(fallbackDb);

  const CANONICAL_TO_TYPE = { Substantiv: 'noun', Verb: 'verb', Adjektiv: 'adjective', Adverb: 'adverb' };
  const getTypeForCategory = (role) => {
    const cid = (categoryMeta[getCategoryMetaKey(role)] && categoryMeta[getCategoryMetaKey(role)].canonicalId) || getInferredCanonicalCategoryId(role);
    return CANONICAL_TO_TYPE[cid] || null;
  };
  const isSkeletonCategory = (role) => {
    const meta = categoryMeta[getCategoryMetaKey(role)];
    const canonicalId = meta && meta.canonicalId;
    return skeletonCategoryKeys.includes(canonicalId);
  };

  const isCategoryDuplicate = (role) => {
    const cid = categoryCanonicalMap[role] || getInferredCanonicalCategoryId(role);
    if (!cid) return false;
    let count = 0;
    Object.keys(db).forEach((c) => {
      const cc = categoryCanonicalMap[c] || getInferredCanonicalCategoryId(c);
      if (cc === cid) count += 1;
    });
    return count > 1;
  };

  const constructorGroupColors = useMemo(() => visibleConstructorGroups.reduce((colors, group) => ({
    ...colors,
    [group.id]: categoryColorByRole[group.category]
      || categoryColorByRole[categoryCanonicalMap[group.category] || getInferredCanonicalCategoryId(group.category)]
      || '#a78bfa',
  }), {}), [categoryCanonicalMap, categoryColorByRole, visibleConstructorGroups]);

  useEffect(() => {
    if (!activePhraseGameId) return;
    if (!phraseGameRows.some((game) => game.id === activePhraseGameId)) {
      setActivePhraseGameId(null);
    }
  }, [activePhraseGameId, phraseGameRows]);

  const nextAnalysisTextNumber = formatAnalysisTextNumber(
    analysisLibrary.nextNumber > 999 ? 1 : analysisLibrary.nextNumber,
  );

  const saveAnalysisText = (text, existingId = null, name = '') => {
    const cleanText = text.trim();
    if (!cleanText) return null;

    if (existingId) {
      const updatedEntry = {
        id: existingId,
        text: cleanText,
        updatedAt: new Date().toISOString(),
      };

      setAnalysisLibrary((currentLibrary) => {
        const nextLibrary = normalizeAnalysisLibrary(currentLibrary);
        const existingText = nextLibrary.texts.find((item) => item.id === existingId);

        if (!existingText) return nextLibrary;

        return {
          ...nextLibrary,
          texts: nextLibrary.texts.map((item) => (
            item.id === existingId
              ? { ...item, ...updatedEntry, createdAt: item.createdAt }
              : item
          )),
        };
      });

      return updatedEntry;
    }

    setAnalysisLibrary((currentLibrary) => {
      let nextLibrary = normalizeAnalysisLibrary(currentLibrary);
      let texts = [...nextLibrary.texts];
      let archives = [...nextLibrary.archives];
      let nextNumber = nextLibrary.nextNumber;
      let batch = nextLibrary.batch;

      if (nextNumber > 999 || texts.length >= 999) {
        if (texts.length > 0) {
          archives = [
            ...archives,
            {
              id: formatAnalysisTextNumber(batch),
              createdAt: new Date().toISOString(),
              texts,
            },
          ];
        }

        texts = [];
        nextNumber = 1;
        batch += 1;
      }

      const savedEntry = {
        id: formatAnalysisTextNumber(nextNumber),
        name: String(name || '').slice(0, 5),
        text: cleanText,
        createdAt: new Date().toISOString(),
      };

      return {
        batch,
        nextNumber: nextNumber + 1,
        texts: [...texts, savedEntry],
        archives,
      };
    });

    return {
      id: nextAnalysisTextNumber,
      name: String(name || '').slice(0, 5),
      text: cleanText,
      createdAt: new Date().toISOString(),
    };
  };

  const currentPosition = positionHistory[positionHistory.length - 1];
  const canonicalCurrentPosition = currentPosition === 'START'
    ? 'START'
    : getCanonicalCategoryForRole(currentPosition);
  const sentenceWarning = canonicalCurrentPosition === 'Preposisjon'
    ? 'Предлог требует продолжения: добавьте объект, местоимение или фразу, чтобы связка получила смысл.'
    : '';

  const addNode = async () => {
    const value = normalizeWordText(newWord);
    if (!value || !activeCategory) return;

    setNewWord('');

    const isNounTarget = isNounGroup(activeCategory, activeSubcategory);
    const isVerbTarget = isVerbGroup(activeCategory, activeSubcategory);
    const isAdjectiveTarget = isAdjectiveGroup(activeCategory, activeSubcategory);
    const isAdverbTarget = isAdverbGroup(activeCategory, activeSubcategory);
    let officialNounData = null;
    let officialVerbData = null;
    let officialAdjectiveData = null;
    if (isNounTarget && shouldTryDictionaryForms(activeCategory, activeSubcategory)) {
      try {
        officialNounData = await fetchOfficialNounForms(value);
      } catch {
        officialNounData = null;
      }
    }
    if (isVerbTarget && shouldTryDictionaryForms(activeCategory, activeSubcategory)) {
      try {
        officialVerbData = await fetchOfficialVerbForms(value);
      } catch {
        officialVerbData = null;
      }
    }
    if (isAdjectiveTarget && shouldTryDictionaryForms(activeCategory, activeSubcategory)) {
      try {
        officialAdjectiveData = await fetchOfficialAdjectiveForms(value);
      } catch {
        officialAdjectiveData = null;
      }
    }

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };

      if (activeSubcategory) {
        const word = isNounTarget
          ? {
            text: officialNounData?.base || value,
            root: '',
            rootTranslation: '',
            roots: [],
            capitalized: newWordCapitalized,
            lvl: 'A1',
            type: 'noun',
            forms: officialNounData?.forms || null,
            gender: officialNounData?.gender || null,
            manualForms: null,
            formsSource: officialNounData?.forms ? 'dictionary' : null,
            translations: [],
            synonyms: [],
            antonyms: [],
            adjectives: [],
            photo: null,
          }
          : isVerbTarget
            ? {
              text: officialVerbData?.base || value.toLowerCase().replace(/^å\s+/i, ''),
              root: '',
              rootTranslation: '',
              roots: [],
              capitalized: newWordCapitalized,
              lvl: 'A1',
              type: 'verb',
              forms: officialVerbData?.forms || null,
              manualForms: null,
              formsSource: officialVerbData?.forms ? 'dictionary' : null,
              translations: [],
              synonyms: [],
              antonyms: [],
              adjectives: [],
              photo: null,
            }
          : isAdjectiveTarget
            ? {
              text: officialAdjectiveData?.base || value,
              root: '',
              rootTranslation: '',
              roots: [],
              capitalized: newWordCapitalized,
              lvl: 'A1',
              type: 'adjective',
              forms: officialAdjectiveData?.forms || null,
              manualForms: null,
              formsSource: officialAdjectiveData?.forms ? 'dictionary' : null,
              translations: [],
              synonyms: [],
              antonyms: [],
              adjectives: [],
              photo: null,
            }
          : isAdverbTarget
            ? {
              text: value,
              root: '',
              rootTranslation: '',
              roots: [],
              capitalized: newWordCapitalized,
              lvl: 'A1',
              type: 'adverb',
              forms: null,
              manualForms: null,
              formsSource: null,
              translations: [],
              synonyms: [],
              antonyms: [],
              adjectives: [],
              photo: null,
            }
          : {
            text: value,
            root: '',
            rootTranslation: '',
            roots: [],
            capitalized: newWordCapitalized,
            lvl: 'A1',
            manualForms: null,
            translations: [],
            synonyms: [],
            antonyms: [],
            adjectives: [],
            photo: null,
          };

        const words = getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory));
        const isDuplicateWord = words.some((existingWord) => {
          const existingForms = {
            ...(existingWord.forms || {}),
            ...(existingWord.manualForms || {}),
          };
          const existingTerms = [
            existingWord.text,
            ...Object.values(existingForms),
          ].filter(Boolean).map((term) => normalizeWordText(term));
          const newForms = {
            ...(word.forms || {}),
            ...(word.manualForms || {}),
          };
          const newTerms = [
            word.text,
            ...Object.values(newForms),
          ].filter(Boolean).map((term) => normalizeWordText(term));

          return newTerms.some((term) => existingTerms.includes(term));
        });

        if (isDuplicateWord) return currentDb;

        currentCategory = setWordsAtPath(currentCategory, activeSubcategory, [...words, word]);
      } else {
        currentCategory[value] = currentCategory[value] || [];
      }

      nextDb[activeCategory] = currentCategory;
      return syncReciprocalRelations(nextDb);
    });
  };

  const deleteWord = (wordIndex) => {
    if (!activeCategory) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory));

      currentCategory = setWordsAtPath(
        currentCategory,
        activeSubcategory,
        words.filter((_, index) => index !== wordIndex),
      );

      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const changeWordLevel = (wordIndex, level) => {
    if (!activeCategory) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];

      if (!word) return currentDb;

      const levels = buildLevels(word);
      words[wordIndex] = applyWordLevel(word, levels, level);
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });
  };

  const setWordLevelShared = (wordIndex, level, shared) => {
    if (!activeCategory) return;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const levels = buildLevels(word);
      if (!levels[level]) levels[level] = { phrase: '', photo: null };
      levels[level] = { ...levels[level], shared: !!shared };
      const cur = LEVEL_ORDER.includes(word.lvl) ? word.lvl : level;
      words[wordIndex] = applyWordLevel(word, levels, cur);
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const setWordGender = (wordIndex, gender, expectedText) => {
    if (!activeCategory) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];

      if (!word) return currentDb;
      if (expectedText && word.text !== expectedText) return currentDb;

      {
        const nw = { ...word, gender: gender || null };
        if (nw.ord && nw.ord.evik && nw.ord.evikSnapshot) nw.ord = { ...nw.ord, evikSnapshot: evikContentSig(nw) };
        words[wordIndex] = nw;
      }
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });
  };

  const setWordRow = (wordIndex, rowName) => {
    if (!activeCategory) return;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory || ''))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const next = { ...word };
      if (rowName) next.row = rowName;
      else delete next.row;
      words[wordIndex] = next;
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory || '', words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const normalizeYouTubeLink = (raw) => {
    let s = (raw || '').trim();
    if (!s) return { ok: true, url: '' };
    if (!/^https?:\/\//i.test(s)) s = 'https://' + s;
    let u;
    try { u = new URL(s); } catch (e) { return { ok: false }; }
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    const allowed = ['youtube.com', 'm.youtube.com', 'youtu.be', 'tiktok.com', 'm.tiktok.com', 'vm.tiktok.com', 'vt.tiktok.com'];
    if (!allowed.includes(host)) return { ok: false };
    u.protocol = 'https:';
    return { ok: true, url: u.toString() };
  };

  const setWordStory = (wordIndex, story) => {
    if (!activeCategory) return;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory || ''))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const next = { ...word };
      const clean = (story || '').slice(0, 801);
      if (clean.trim()) next.story = clean;
      else delete next.story;
      words[wordIndex] = next;
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory || '', words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const setWordLink = (wordIndex, url) => {
    if (!activeCategory) return;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory || ''))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const next = { ...word };
      if (url) next.link = url;
      else delete next.link;
      words[wordIndex] = next;
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory || '', words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const genderBackfillRef = useRef(new Set());
  const requestGenderBackfill = (word, wordIndex) => {
    if (!word || word.gender) return;
    const key = (word.text || '').toLowerCase();
    if (!key || genderBackfillRef.current.has(key)) return;
    genderBackfillRef.current.add(key);
    (async () => {
      try {
        const data = await fetchOfficialNounForms(word.text);
        if (data && data.gender) setWordGender(wordIndex, data.gender, word.text);
      } catch (_) {}
    })();
  };

  const applyBackfilledForms = (wordIndex, base, forms) => {
    if (!activeCategory) return;
    if (!forms || Object.keys(forms).length === 0) return;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const existing = { ...(word.forms || {}), ...(word.manualForms || {}) };
      if (Object.values(existing).some(Boolean)) return currentDb;
      {
        const nw = { ...word, text: (word.type === 'verb' && base) ? base : word.text, forms, formsSource: 'dictionary' };
        if (nw.ord && nw.ord.evik && nw.ord.evikSnapshot) nw.ord = { ...nw.ord, evikSnapshot: evikContentSig(nw) };
        words[wordIndex] = nw;
      }
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const formsBackfillRef = useRef(new Set());
  const requestFormsBackfill = (word, wordIndex) => {
    if (!word) return;
    const existing = { ...(word.forms || {}), ...(word.manualForms || {}) };
    if (Object.values(existing).some(Boolean)) return;
    const type = getTypeForCategory(activeCategory) || word.type;
    if (!['verb', 'noun', 'adjective'].includes(type)) return;
    const key = type + ':' + (word.text || '').toLowerCase();
    if (!key.trim() || formsBackfillRef.current.has(key)) return;
    formsBackfillRef.current.add(key);
    (async () => {
      try {
        let data = null;
        if (type === 'verb') data = await fetchOfficialVerbForms(word.text);
        else if (type === 'noun') data = await fetchOfficialNounForms(word.text);
        else if (type === 'adjective') data = await fetchOfficialAdjectiveForms(word.text);
        if (data && data.forms) applyBackfilledForms(wordIndex, data.base, data.forms);
      } catch (_) {}
    })();
  };

  const persistNounGender = (categoryKey, subPath, text, gender) => {
    if (!categoryKey || !text || !gender) return;
    setDb((currentDb) => {
      if (!currentDb[categoryKey]) return currentDb;
      const nextDb = { ...currentDb };
      let cat = { ...nextDb[categoryKey] };
      const node = getNodeAtPath(cat, subPath || '');
      const words = [...getWordsFromNode(node)];
      const idx = words.findIndex((w) => (w.text || '') === text && w.type === 'noun' && !w.gender);
      if (idx < 0) return currentDb;
      words[idx] = { ...words[idx], gender };
      cat = setWordsAtPath(cat, subPath || '', words);
      nextDb[categoryKey] = cat;
      return nextDb;
    });
  };

  const reorderWords = (fromIndex, toIndex) => {
    if (!activeCategory || fromIndex === toIndex) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const [movedWord] = words.splice(fromIndex, 1);

      if (!movedWord) return currentDb;

      words.splice(toIndex, 0, movedWord);
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });
  };

  const addWordTranslation = (wordIndex, translation) => {
    if (!activeCategory) return;

    const cleanTranslation = normalizeWordText(translation);
    if (!cleanTranslation) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];

      if (!word) return currentDb;

      const translations = word.translations || [];
      if (translations.length >= 5) return currentDb;

      words[wordIndex] = {
        ...word,
        translations: [...translations, cleanTranslation],
      };
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });
  };

  const deleteWordTranslation = (wordIndex, translationIndex) => {
    if (!activeCategory) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];

      if (!word) return currentDb;

      words[wordIndex] = {
        ...word,
        translations: (word.translations || []).filter((_, index) => index !== translationIndex),
      };
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });
  };

  const addWordRelation = (wordIndex, relationKey, value) => {
    if (!activeCategory) return;

    const cleanValue = normalizeWordText(value);
    if (!cleanValue || !['synonyms', 'antonyms', 'adjectives'].includes(relationKey)) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];

      if (!word) return currentDb;

      const values = word[relationKey] || [];
      if (values.length >= 3) return currentDb;

      words[wordIndex] = {
        ...word,
        [relationKey]: [...values, cleanValue],
      };
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return ['synonyms', 'antonyms'].includes(relationKey) ? syncReciprocalRelations(nextDb) : nextDb;
    });
  };

  const deleteWordRelation = (wordIndex, relationKey, valueIndex) => {
    if (!activeCategory) return;
    if (!['synonyms', 'antonyms', 'adjectives'].includes(relationKey)) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];

      if (!word) return currentDb;

      words[wordIndex] = {
        ...word,
        [relationKey]: (word[relationKey] || []).filter((_, index) => index !== valueIndex),
      };
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });
  };

  const editWordText = (wordIndex, newText) => {
    if (!activeCategory) return;
    const cleanText = normalizeWordText(newText || '');
    if (!cleanText) return;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const path = activeSubcategory || '';
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, path))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const newType = getTypeForCategory(activeCategory);
      const updatedWord = newType ? { ...word, text: cleanText, type: newType } : { ...word, text: cleanText };
      words[wordIndex] = updatedWord;
      currentCategory = setWordsAtPath(currentCategory, path, words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const openOrdskjonnhet = (wordIndex) => {
    if (!activeCategory) return;
    const node = getNodeAtPath(db[activeCategory], activeSubcategory);
    const word = getWordsFromNode(node)[wordIndex];
    if (!word) return;
    if (word.photo && word.photo.photoKey && !word.photo.dataUrl && userProfile && userProfile.id) {
      loadOnePhotoLazy(word.photo.photoKey, userProfile.id);
    }
    setOrdDraft({ wordIndex, word });
  };

  const wordTextSet = useMemo(() => {
    const s = new Set();
    forEachWordInDb(db, (w) => { if (w && w.text) s.add(String(w.text).trim().toLowerCase()); });
    return s;
  }, [db]);

  const openWordByText = (text) => {
    const loc = findWordLocation(db, text);
    if (!loc) return;
    const node = getNodeAtPath(db[loc.category], loc.subPath);
    const word = getWordsFromNode(node)[loc.wordIndex];
    if (!word) return;
    setActiveCategory(loc.category);
    setActiveSubcategory(loc.subPath || null);
    if (word.photo && word.photo.photoKey && !word.photo.dataUrl && userProfile && userProfile.id) {
      loadOnePhotoLazy(word.photo.photoKey, userProfile.id);
    }
    setOrdDraft({ wordIndex: loc.wordIndex, word });
  };

  const applyOrdData = (wordIndex, json, mode) => {
    if (!activeCategory || !json) return;
    const repl = mode === 'replace';
    const POS_TYPE = { substantiv: 'noun', verb: 'verb', adjektiv: 'adjective', adverb: 'adverb' };
    const arr = (v) => (Array.isArray(v) ? v : []);
    const pickArr = (ex, inc) => (repl ? inc : (ex && ex.length ? ex : inc));
    const pickStr = (ex, inc) => (repl ? inc : (ex ? ex : inc));
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const level = LEVEL_ORDER.includes(word.lvl) ? word.lvl : 'A1';
      const levels = buildLevels(word);
      const existPhrase = (levels[level] && levels[level].phrase) || '';
      const nextPhrase = pickStr(existPhrase, (json.phrase || '').slice(0, 200));
      levels[level] = { ...(levels[level] || { phrase: '', photo: null }), phrase: nextPhrase };
      let nextWord = applyWordLevel(word, levels, level);
      const prev = nextWord.ord || {};
      const ord = {
        phrase_translation: pickStr(prev.phrase_translation, (json.phrase_translation || '').slice(0, 160)),
        examples: pickArr(prev.examples, arr(json.examples).map((s) => (s || '').slice(0, 200))),
        example_translations: pickArr(prev.example_translations, arr(json.example_translations)),
        phonetic_similar: pickArr(prev.phonetic_similar, arr(json.phonetic_similar)),
        etymology: pickStr(prev.etymology, (json.etymology || '').slice(0, 500)),
        cognates: pickArr(prev.cognates, arr(json.cognates)),
        socialLinks: prev.socialLinks || { youtube: '', tiktok: '', instagram: '' },
        associations: prev.associations || [],
        authored: prev.authored || false,
        fullnessDate: prev.fullnessDate || null,
        photo: prev.photo || null,
        photoReady: prev.photoReady || false,
        gradboyning: repl
          ? (((json.gradboyning && typeof json.gradboyning === 'object') ? json.gradboyning : (prev.gradboyning || null)))
          : ((prev.gradboyning || ((json.gradboyning && typeof json.gradboyning === 'object') ? json.gradboyning : null))),
      };
      const okGender = json.gender && ['en', 'ei', 'et'].includes(json.gender);
      nextWord = {
        ...nextWord,
        translations: pickArr(nextWord.translations, arr(json.translations).slice(0, 5)),
        synonyms: pickArr(nextWord.synonyms, arr(json.synonyms).slice(0, 6)),
        antonyms: pickArr(nextWord.antonyms, arr(json.antonyms).slice(0, 6)),
        story: pickStr(nextWord.story, (json.story || '').slice(0, 1000)),
        gender: okGender ? (repl ? json.gender : (nextWord.gender || json.gender)) : nextWord.gender,
        type: nextWord.type || POS_TYPE[json.pos] || nextWord.type || '',
        ord,
      };
      const hasRoot = (Array.isArray(nextWord.roots) && nextWord.roots.length) || nextWord.root;
      if (json.root && (repl || !hasRoot)) {
        const cleanRoot = normalizeRootValue(json.root);
        if (cleanRoot) {
          nextWord = { ...nextWord, roots: [{ root: cleanRoot, translation: '' }], root: '', rootTranslation: '' };
        }
      }
      words[wordIndex] = nextWord;
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const saveSongToMyDictionary = (pub, song) => {
    if (!pub || !song || !userProfile.id) return;
    const text = String(pub.text || '').trim();
    const norm = normalizeWordText(text);
    if (!norm) return;
    const pd = pub.data || {};
    let found = false;
    const attach = (w) => {
      if (w && normalizeWordText(w.text || '') === norm) {
        found = true;
        return { ...w, ord: { ...(w.ord || {}), song } };
      }
      return w;
    };
    const walk = (node) => {
      if (Array.isArray(node)) return node.map(attach);
      if (!node || typeof node !== 'object') return node;
      const out = {};
      Object.keys(node).forEach((k) => {
        if (k === WORDS_KEY) out[k] = (node[k] || []).map(attach);
        else if (k === ORDER_KEY) out[k] = node[k];
        else out[k] = walk(node[k]);
      });
      return out;
    };
    setDb((curDb) => {
      const nextDb = {};
      Object.keys(curDb).forEach((cat) => { nextDb[cat] = walk(curDb[cat]); });
      if (found) return nextDb;
      const posKey = String(pd.pos || pub.type || pd.type || '').toLowerCase().trim();
      const canon = canonicalCategoryAliases[posKey] || null;
      let target = null;
      Object.keys(curDb).forEach((cat) => {
        if (!target && canon && categoryCanonicalMap[cat] === canon) target = cat;
      });
      if (!target) target = Object.keys(curDb)[0];
      if (!target) return nextDb;
      const newWord = {
        text,
        capitalized: false,
        translations: (pd.translations || []).filter(Boolean).slice(0, 5),
        synonyms: (pd.synonyms || []).filter(Boolean),
        antonyms: (pd.antonyms || []).filter(Boolean),
        adjectives: [],
        root: pd.root || '',
        roots: pd.roots || [],
        forms: pd.forms || null,
        story: pd.story || '',
        type: pd.pos || pub.type || '',
        ord: { song },
      };
      const words = [...getWordsFromNode(getNodeAtPath(nextDb[target], ''))];
      if (!words.some((w) => normalizeWordText(w.text || '') === norm)) words.push(newWord);
      nextDb[target] = setWordsAtPath(nextDb[target], '', words);
      return nextDb;
    });
    notify(found ? 'Песня сохранена к слову в вашем словаре.' : 'Слово добавлено в ваш словарь со своей песней.');
  };

  const setOrdAuthorship = (wordIndex, authored, payload) => {
    if (!activeCategory) return;
    if (authored && !['paid', 'admin'].includes(userProfile?.role)) {
      notify('Опубликовать слово для всех могут только пользователи с платной подпиской и администраторы. Слово остаётся в вашем словаре, его можно добавлять и менять.');
      return;
    }
    const node0 = getNodeAtPath(db[activeCategory], activeSubcategory);
    const word0 = node0 ? getWordsFromNode(node0)[wordIndex] : null;
    if (!word0) return;
    const wordText = word0.text || '';
    const wordNorm = wordText.trim().toLowerCase();
    const fullnessDate = authored ? new Date().toISOString() : null;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const pd = (payload && payload.data) || {};
      const ord = { ...(word.ord || {}), authored: !!authored, fullnessDate, assoc: (payload && payload.assoc !== undefined) ? payload.assoc : ((word.ord && word.ord.assoc) || null) };
      if (authored && pd.grad && (pd.grad.positiv || pd.grad.komparativ || pd.grad.superlativ)) {
        ord.gradboyning = pd.grad;
      }
      const nextType = (authored && pd.type) ? pd.type : word.type;
      words[wordIndex] = { ...word, type: nextType, ord };
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
    const ownerId = userProfile && userProfile.id;
    if (!ownerId || !wordNorm) return;
    if (authored && payload && payload.data) {
      const avaSrc = (userProfile.photo && userProfile.photo.dataUrl) || null;
      const existingThumb = (userProfile.photo && userProfile.photo.thumb) || null;
      (existingThumb ? Promise.resolve(existingThumb) : makeAvatarThumb(avaSrc)).then((thumb) => {
        const catColor = categoryColors[activeCategory] || null;
        const dataOut = {
          ...payload.data,
          ...(catColor ? { catColor } : {}),
          ...(thumb ? { ownerAvatar: thumb } : {}),
          customer: { id: ownerId, nick: (userProfile.nickname || ''), avatar: thumb || null },
        };
        const pubRow = {
          owner_id: ownerId,
          word_norm: wordNorm,
          word_text: wordText,
          owner_nick: userProfile.nickname || null,
          pos: (dataOut.type || 'noun'),
          data: dataOut,
          photo: payload.fullPhoto || null,
          completeness: payload.completeness || 0,
          authored: true,
          fullness_date: fullnessDate,
          updated_at: new Date().toISOString(),
        };
        (async () => {
          let res = await supabase.from('ordskjonnhet_public').upsert(pubRow, { onConflict: 'owner_id,word_norm,pos' });
          if (res && res.error) {
            await new Promise((r) => window.setTimeout(r, 2000));
            res = await supabase.from('ordskjonnhet_public').upsert(pubRow, { onConflict: 'owner_id,word_norm,pos' });
          }
          if (res && res.error) {
            notify('Не удалось опубликовать слово в облако — проверь связь и нажми «Подписать» ещё раз.');
            setDb((cur) => {
              const nd = { ...cur };
              let cat = { ...nd[activeCategory] };
              const ws = [...getWordsFromNode(getNodeAtPath(cat, activeSubcategory))];
              const w2 = ws[wordIndex];
              if (!w2) return cur;
              ws[wordIndex] = { ...w2, ord: { ...(w2.ord || {}), authored: false, fullnessDate: null } };
              cat = setWordsAtPath(cat, activeSubcategory, ws);
              nd[activeCategory] = cat;
              return nd;
            });
          }
        })();
      });
    } else if (!authored) {
      supabase.from('ordskjonnhet_public').delete().eq('owner_id', ownerId).eq('word_norm', wordNorm).eq('pos', (word0.type || 'noun')).then(() => {}, () => {});
    }
  };

  const setOrdPhoto = (wordIndex, photo) => {
    if (!activeCategory || !photo) return;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const pic = { dataUrl: photo.dataUrl, name: photo.name || '', type: photo.type || '' };
      const lvl = LEVEL_ORDER.includes(word.lvl) ? word.lvl : 'A1';
      const levels = buildLevels(word);
      levels[lvl] = { ...(levels[lvl] || { phrase: '', photo: null }), photo: { ...pic } };
      let nextWord = applyWordLevel(word, levels, lvl);
      nextWord = { ...nextWord, ord: { ...(nextWord.ord || {}), photo: { ...pic }, photoReady: true } };
      words[wordIndex] = nextWord;
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const setWordPhrase = (wordIndex, phrase) => {
    if (!activeCategory) return;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const level = LEVEL_ORDER.includes(word.lvl) ? word.lvl : 'A1';
      const levels = buildLevels(word);
      levels[level] = { ...(levels[level] || { phrase: '', photo: null }), phrase: (phrase || '').slice(0, 200) };
      words[wordIndex] = applyWordLevel(word, levels, level);
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const uploadPhotoToStorage = async (userId, photoKey, dataUrl) => {
    try {
      if (!userId || !photoKey || !dataUrl || dataUrl.indexOf(',') === -1) return;
      const head = dataUrl.slice(0, dataUrl.indexOf(','));
      const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
      const mime = (head.match(/data:([^;]+)/) || [])[1] || 'image/jpeg';
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: mime });
      await supabase.storage.from('word-photos').upload(userId + '/' + photoKey, blob, { upsert: true, contentType: mime });
    } catch (e) {}
  };

  const setWordPhoto = (wordIndex, photo) => {
    if (!activeCategory) return;
    let savedPhoto = photo;
    let keyToSave = null;
    if (photo && photo.dataUrl) {
      const key = photo.photoKey || ('ph_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8));
      savedPhoto = { ...photo, photoKey: key };
      keyToSave = key;
    }
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];
      if (!word) return currentDb;
      const level = LEVEL_ORDER.includes(word.lvl) ? word.lvl : 'A1';
      const levels = buildLevels(word);
      levels[level] = { ...(levels[level] || { phrase: '', photo: null }), photo: savedPhoto };
      words[wordIndex] = applyWordLevel(word, levels, level);
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
    if (userProfile.id && keyToSave && savedPhoto) {
      const photoForCloud = savedPhoto;
      (async () => {
        const { error } = await supabase.from('word_photos').upsert({
          user_id: userProfile.id,
          photo_key: keyToSave,
          data: photoForCloud,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,photo_key' });
        if (error) {
          console.error('PHOTO SAVE ERROR:', JSON.stringify(error));
        }
        uploadPhotoToStorage(userProfile.id, keyToSave, photoForCloud.dataUrl);
      })();
    }
  };

  const setWordForms = (wordIndex, forms) => {
    if (!activeCategory) return;

    const cleanForms = Object.entries(forms || {}).reduce((nextForms, [key, value]) => {
      const cleanValue = key === 'infinitive'
        ? normalizeWordText(value || '').replace(/^å\s+/i, '')
        : normalizeWordText(value || '');
      return cleanValue ? { ...nextForms, [key]: cleanValue } : nextForms;
    }, {});

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      const word = words[wordIndex];

      if (!word) return currentDb;

      words[wordIndex] = {
        ...word,
        type: word.type || (isNounGroup(activeCategory, activeSubcategory) ? 'noun' : word.type),
        text: word.type === 'verb' && cleanForms.infinitive
          ? cleanForms.infinitive
          : ['adjective', 'adverb'].includes(word.type) && cleanForms.positive ? cleanForms.positive : word.text,
        forms: Object.keys(cleanForms).length > 0 ? cleanForms : null,
        manualForms: Object.keys(cleanForms).length > 0 ? cleanForms : null,
        formsSource: Object.keys(cleanForms).length > 0 ? 'manual' : null,
      };
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });
  };

  const setWordRoot = (wordIndex, root, rootTranslation = '') => {
    if (!activeCategory) return;

    const cleanRoot = normalizeRootValue(root);
    const cleanRootKey = normalizeRootKey(cleanRoot);
    const cleanRootTranslation = normalizeWordText(rootTranslation || '');
    if (!cleanRootKey) return false;

    const rootAlreadyExists = Object.values(analyzerMap).some((entry) => (
      (entry.roots || []).some((item) => normalizeRootKey(item.root) === cleanRootKey)
    ));

    if (rootAlreadyExists) {
      notify(`Корень "${cleanRoot}" уже есть в базе. Для двукоренного слова добавь другой корень.`);
      return false;
    }

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      if (!words[wordIndex]) return currentDb;

      words[wordIndex] = {
        ...words[wordIndex],
        roots: [
          ...normalizeWordRoots(words[wordIndex]),
          {
            root: cleanRoot,
            translation: cleanRootTranslation,
          },
        ],
      };
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });

    return true;
  };

  const deleteWordRoot = (wordIndex, rootValue) => {
    if (!activeCategory) return;

    const cleanRoot = normalizeRootValue(rootValue);
    const cleanRootKey = normalizeRootKey(cleanRoot);
    if (!cleanRootKey) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...nextDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, activeSubcategory))];
      if (!words[wordIndex]) return currentDb;

      words[wordIndex] = {
        ...words[wordIndex],
        root: normalizeRootKey(words[wordIndex].root || '') === cleanRootKey ? '' : words[wordIndex].root || '',
        rootTranslation: normalizeRootKey(words[wordIndex].root || '') === cleanRootKey
          ? ''
          : words[wordIndex].rootTranslation || '',
        roots: normalizeWordRoots(words[wordIndex]).filter((item) => normalizeRootKey(item.root) !== cleanRootKey),
      };
      currentCategory = setWordsAtPath(currentCategory, activeSubcategory, words);
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });
  };

  const moveOrCopyWord = async (wordIndex, targetSubcategory, mode, targetCategoryOverride) => {
    const targetCategory = targetCategoryOverride || activeCategory;
    const isCrossCategory = targetCategory !== activeCategory;
    if (!activeCategory || !targetCategory) {
      return;
    }
    if (!isCrossCategory && !targetSubcategory) {
      return;
    }
    if (mode === 'move') {
      const msg = isCrossCategory
        ? 'Перенести слово в категорию "' + targetCategory + '"? Оно исчезнет из текущего раздела.'
        : 'Перенести слово в другой раздел? Оно исчезнет из текущего раздела.';
      const ok = await requestConfirm(msg, { title: 'Подтвердите перенос', confirmLabel: 'Перенести', tone: 'normal' });
      if (!ok) return;
    }

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      const srcCat = { ...nextDb[activeCategory] };
      const sourceWords = [...getWordsFromNode(getNodeAtPath(srcCat, activeSubcategory))];
      const word = sourceWords[wordIndex];
      if (!word) return currentDb;

      const copiedWord = {
        ...word,
        root: word.root || '',
        rootTranslation: word.rootTranslation || '',
        roots: word.roots ? [...word.roots] : [],
        forms: word.forms ? { ...word.forms } : undefined,
        manualForms: word.manualForms ? { ...word.manualForms } : null,
        formsSource: word.formsSource || null,
        translations: word.translations ? [...word.translations] : [],
        synonyms: word.synonyms ? [...word.synonyms] : [],
        antonyms: word.antonyms ? [...word.antonyms] : [],
        adjectives: word.adjectives ? [...word.adjectives] : [],
        photo: word.photo ? { ...word.photo } : null,
      };

      if (isCrossCategory) {
        const newType = getTypeForCategory(targetCategory);
        const adjustedWord = newType ? { ...copiedWord, type: newType } : { ...copiedWord, type: copiedWord.type };
        const dstCat = { ...(nextDb[targetCategory] || {}) };
        const dstWords = [...getWordsFromNode(dstCat)];
        dstCat[WORDS_KEY] = [...dstWords, adjustedWord];
        nextDb[targetCategory] = dstCat;
      } else {
        const targetWords = [...getWordsFromNode(getNodeAtPath(srcCat, targetSubcategory))];
        const updated = setWordsAtPath(srcCat, targetSubcategory, [...targetWords, copiedWord]);
        nextDb[activeCategory] = updated;
      }

      if (mode === 'move') {
        const newSrc = isCrossCategory ? { ...nextDb[activeCategory] } : { ...nextDb[activeCategory] };
        const filtered = sourceWords.filter((_, i) => i !== wordIndex);
        const updatedSrc = setWordsAtPath(newSrc, activeSubcategory, filtered);
        nextDb[activeCategory] = updatedSrc;
      }

      return nextDb;
    });
  };

  const addMainCategory = (name, color) => {
    if (!name) return;

    setDb((currentDb) => ({ ...currentDb, [name]: {} }));
    setCategoryOrder((currentOrder) => (
      currentOrder.includes(name) ? currentOrder : [...currentOrder, name]
    ));
    setCategoryColors((currentColors) => ({ ...currentColors, [name]: color }));
    setIsAddingCategory(false);
  };

  const addConstructorGroup = () => {
    const target = constructorSourceTargets.find((item) => (
      `${item.category}${PATH_SEPARATOR}${item.path}` === constructorGroupDraft.sourceKey
    ));

    if (!target) {
      notify('Выберите категорию или конечную подкатегорию из базы.');
      return;
    }

    const group = createConstructorGroup(target.category, target.path, constructorGroupDraft.label);
    setConstructorGroups((currentGroups) => [
      ...normalizeConstructorGroups(currentGroups, db),
      group,
    ]);
    setConstructorGroupDraft({ label: '', sourceKey: '' });
    setIsAddingCategory(false);
  };

  const pinSubcategoryToConstructor = ({ category, path, label }) => {
    if (!category || !path) return;

    const normalizedGroups = normalizeConstructorGroups(constructorGroups, db);
    const alreadyPinned = normalizedGroups.some((group) => (
      group.category === category && group.path === path
    ));

    if (alreadyPinned) {
      notify('Эта подкатегория уже прикреплена на панель конструктора.');
      return;
    }

    setConstructorGroups((currentGroups) => [
      ...normalizeConstructorGroups(currentGroups, db),
      createConstructorGroup(category, path, label),
    ]);
  };

  const renameConstructorGroup = (groupId, nextName) => {
    const cleanName = nextName.trim();
    if (!cleanName) return;

    setConstructorGroups((currentGroups) => {
      const groups = normalizeConstructorGroups(currentGroups, db);
      return groups.map((group) => (
      group.id === groupId ? { ...group, label: cleanName } : group
      ));
    });
  };

  const deleteConstructorGroup = async (groupId) => {
    const ok = await requestConfirm('Удалить эту группу конструктора? Это действие нельзя отменить.');
    if (!ok) return;
    setConstructorGroups((currentGroups) => {
      const groups = normalizeConstructorGroups(currentGroups, db);
      return groups.filter((group) => group.id !== groupId);
    });
  };

  const reorderConstructorGroups = (sourceId, targetId, placement = 'before') => {
    if (!sourceId || !targetId || sourceId === targetId) return;

    setConstructorGroups((currentGroups) => {
      const nextGroups = normalizeConstructorGroups(currentGroups, db);
      const sourceIndex = nextGroups.findIndex((group) => group.id === sourceId);
      const targetIndex = nextGroups.findIndex((group) => group.id === targetId);

      if (sourceIndex < 0 || targetIndex < 0) return currentGroups;

      const [sourceGroup] = nextGroups.splice(sourceIndex, 1);
      const adjustedTargetIndex = nextGroups.findIndex((group) => group.id === targetId);
      const insertIndex = placement === 'after' ? adjustedTargetIndex + 1 : adjustedTargetIndex;

      nextGroups.splice(insertIndex, 0, sourceGroup);
      return nextGroups;
    });
  };

  const selectConstructorGroup = (groupId) => {
    const group = constructorGroupById[groupId];
    if (!group) return;

    setActiveCategory(group.category);
    setActiveSubcategory(group.path || null);
  };

  const changeCategoryColor = (category, color) => {
    setCategoryColors((currentColors) => ({
      ...currentColors,
      [category]: color,
    }));
  };

  const setCategoryTextTheme = (category, theme) => {
    if (!category) return;
    setCategoryMeta((cur) => {
      const key = getCategoryMetaKey(category);
      const prev = cur[key] || {};
      return { ...cur, [key]: { ...prev, textTheme: theme } };
    });
  };

  const reorderCategories = (sourceCategory, targetCategory, placement = 'before') => {
    if (!sourceCategory || !targetCategory || sourceCategory === targetCategory) return;

    const makeNextOrder = (order, currentDb) => {
      const nextOrder = normalizeCategoryOrder(order, currentDb);
      const sourceIndex = nextOrder.indexOf(sourceCategory);
      const targetIndex = nextOrder.indexOf(targetCategory);

      if (sourceIndex < 0 || targetIndex < 0) return nextOrder;

      const [sourceEntry] = nextOrder.splice(sourceIndex, 1);
      const adjustedTargetIndex = nextOrder.indexOf(targetCategory);
      const insertIndex = placement === 'after' ? adjustedTargetIndex + 1 : adjustedTargetIndex;

      nextOrder.splice(insertIndex, 0, sourceEntry);
      return nextOrder;
    };

    setCategoryOrder((currentOrder) => {
      const nextOrder = makeNextOrder(currentOrder, db);
      return nextOrder.join(PATH_SEPARATOR) === normalizeCategoryOrder(currentOrder, db).join(PATH_SEPARATOR)
        ? currentOrder
        : nextOrder;
    });

    setDb((currentDb) => {
      const normalizedOrder = makeNextOrder(categoryOrder, currentDb);
      return Object.fromEntries(normalizedOrder.map((category) => [category, currentDb[category]]));
    });
  };

  const renameCategory = (oldName, nextName) => {
    const cleanName = nextName.trim();
    if (!oldName || !cleanName || oldName === cleanName) return;

    if (db[cleanName]) {
      notify(`Категория "${cleanName}" уже есть.`);
      return;
    }

    setDb((currentDb) => Object.entries(currentDb).reduce((nextDb, [category, node]) => ({
      ...nextDb,
      [category === oldName ? cleanName : category]: node,
    }), {}));

    setCategoryOrder((currentOrder) => normalizeCategoryOrder(currentOrder, db).map((category) => (
      category === oldName ? cleanName : category
    )));

    setCategoryColors((currentColors) => {
      const nextColors = { ...currentColors };
      nextColors[cleanName] = nextColors[oldName] || '#64748b';
      delete nextColors[oldName];
      return nextColors;
    });

    const inferredCanonicalId = categoryCanonicalMap[oldName] || getInferredCanonicalCategoryId(oldName);
    setCategoryMeta((currentMeta) => {
      const renamedMeta = Object.entries(currentMeta).reduce((nextMeta, [key, value]) => {
      const oldPrefix = `${oldName}::`;
      const nextKey = key.startsWith(oldPrefix)
        ? `${cleanName}::${key.slice(oldPrefix.length)}`
        : key;

      return {
        ...nextMeta,
        [nextKey]: value,
      };
      }, {});
      const nextCategoryKey = getCategoryMetaKey(cleanName);

      if (!renamedMeta[nextCategoryKey]?.canonicalId && inferredCanonicalId) {
        renamedMeta[nextCategoryKey] = {
          ...(renamedMeta[nextCategoryKey] || {}),
          canonicalId: inferredCanonicalId,
        };
      }

      return renamedMeta;
    });

    setSentence((currentSentence) => currentSentence.map((item) => (
      item.sourceCategory === oldName ? { ...item, sourceCategory: cleanName } : item
    )));

    if (activeCategory === oldName) setActiveCategory(cleanName);
  };

  const deleteCategory = async (category) => {
    const label = (typeof category === 'string' && category) ? category : 'эту категорию';
    const isSkeleton = isSkeletonCategory(category);
    if (isSkeleton) {
      // Проверяем: это единственная категория с таким canonicalId или один из дублей?
      const myCid = categoryCanonicalMap[category] || getInferredCanonicalCategoryId(category);
      const sameCidCount = Object.keys(db).filter((c) => {
        const cid = categoryCanonicalMap[c] || getInferredCanonicalCategoryId(c);
        return cid === myCid;
      }).length;
      if (sameCidCount <= 1) {
        const okHide = await requestConfirm('Категория "' + label + '" относится к стандартным частям речи и не может быть удалена. Скрыть её из списка?');
        if (!okHide) return;
        if (myCid) {
          setHiddenSkeletonCanonicalIds((prev) => (prev.includes(myCid) ? prev : [...prev, myCid]));
        }
      } else {
        const wordCountDup = countWordsInNode(db[category]);
        if (wordCountDup > 0) {
          await notify('Нельзя удалить категорию «' + label + '», пока внутри есть слова (' + wordCountDup + '). Сначала удалите всё содержимое.', { title: 'Категория не пустая' });
          return;
        }
        const okDel = await requestConfirm('Найден дубль категории «' + label + '». Удалить именно её? Все слова и подкатегории внутри будут утеряны.');
        if (!okDel) return;
        setDb((currentDb) => { const next = { ...currentDb }; delete next[category]; return next; });
        setCategoryColors((c) => { const next = { ...c }; delete next[category]; return next; });
        setCategoryOrder((o) => o.filter((i) => i !== category));
        setCategoryMeta((m) => { const next = { ...m }; delete next[getCategoryMetaKey(category)]; return next; });
      }
      return;
    }
    const wordCountCat = countWordsInNode(db[category]);
    if (wordCountCat > 0) {
      await notify('Нельзя удалить категорию «' + label + '», пока внутри есть слова (' + wordCountCat + '). Сначала удалите все слова из неё и её подкатегорий, и только потом удаляйте саму категорию.', { title: 'Категория не пустая' });
      return;
    }
    const ok = await requestConfirm('Удалить категорию "' + label + '"? Это действие нельзя отменить, все слова и подкатегории внутри будут утеряны.');
    if (!ok) return;
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      delete nextDb[category];
      return nextDb;
    });
    setCategoryColors((currentColors) => {
      const nextColors = { ...currentColors };
      delete nextColors[category];
      return nextColors;
    });
    setCategoryOrder((currentOrder) => currentOrder.filter((item) => item !== category));
    setSentence((currentSentence) => currentSentence.filter((item) => item.role !== category));
    setPositionHistory(['START']);

    if (activeCategory === category) {
      setActiveCategory(null);
      setActiveSubcategory(null);
    }
  };

  const copyOrMoveSection = (source, target, mode) => {
    if (!source?.category || !target?.category || !['copy', 'move'].includes(mode)) return;

    const sourcePath = source.path || '';
    const targetPath = target.path || '';
    const sourceName = sourcePath ? pathToArray(sourcePath).at(-1) : source.category;

    if (!sourceName) return;
    if (source.category === target.category && isSameOrChildPath(targetPath, sourcePath)) return;

    let inserted = null;

    setDb((currentDb) => {
      const sourceRoot = normalizeNode(currentDb[source.category]);
      const sourceNode = sourcePath ? getNodeAtPath(sourceRoot, sourcePath) : sourceRoot;
      if (!sourceNode) return currentDb;

      let nextDb = { ...currentDb };
      let targetRoot = normalizeNode(nextDb[target.category]);
      const targetNode = normalizeNode(getNodeAtPath(targetRoot, targetPath));
      const insertedName = getUniqueSectionName(targetNode, sourceName);
      const insertedPath = arrayToPath([...pathToArray(targetPath), insertedName]);
      const sectionNode = cloneSectionNode(sourceNode);

      targetRoot = updateNodeAtPath(targetRoot, targetPath, (node) => ({
        ...normalizeNode(node),
        [insertedName]: sectionNode,
      }));
      nextDb[target.category] = targetRoot;

      if (mode === 'move') {
        if (!sourcePath) {
          delete nextDb[source.category];
        } else {
          const pathParts = pathToArray(sourcePath);
          const leafName = pathParts.at(-1);
          const parentPath = arrayToPath(pathParts.slice(0, -1));
          let sourceCategoryRoot = normalizeNode(nextDb[source.category]);

          sourceCategoryRoot = updateNodeAtPath(sourceCategoryRoot, parentPath, (node) => {
            const nextNode = normalizeNode(node);
            delete nextNode[leafName];
            return nextNode;
          });
          nextDb[source.category] = sourceCategoryRoot;
        }
      }

      inserted = {
        sourceCategory: source.category,
        sourcePath,
        targetCategory: target.category,
        targetPath,
        insertedPath,
        mode,
      };

      return nextDb;
    });

    if (!inserted) return;

    setCategoryMeta((currentMeta) => {
      const nextMeta = { ...currentMeta };
      const sourceMetaPrefix = sourcePath ? getSubcategoryMetaKey(inserted.sourceCategory, sourcePath) : '';
      const sourceCategoryPrefix = `${inserted.sourceCategory}::`;
      const affectedKeys = Object.keys(currentMeta).filter((key) => {
        if (!sourcePath) return key === getCategoryMetaKey(inserted.sourceCategory) || key.startsWith(sourceCategoryPrefix);
        return key === sourceMetaPrefix || key.startsWith(`${sourceMetaPrefix}${PATH_SEPARATOR}`);
      });

      affectedKeys.forEach((key) => {
        let suffix = '';
        if (!sourcePath) {
          suffix = key === getCategoryMetaKey(inserted.sourceCategory)
            ? ''
            : key.slice(sourceCategoryPrefix.length);
        } else {
          suffix = key === sourceMetaPrefix ? '' : key.slice(sourceMetaPrefix.length + PATH_SEPARATOR.length);
        }
        const nextPath = suffix
          ? `${inserted.insertedPath}${PATH_SEPARATOR}${suffix}`
          : inserted.insertedPath;
        const nextKey = getSubcategoryMetaKey(inserted.targetCategory, nextPath);

        nextMeta[nextKey] = currentMeta[key];
        if (mode === 'move') delete nextMeta[key];
      });

      return nextMeta;
    });

    if (mode === 'move' && !sourcePath) {
      setCategoryColors((currentColors) => {
        const nextColors = { ...currentColors };
        delete nextColors[source.category];
        return nextColors;
      });
      setCategoryOrder((currentOrder) => currentOrder.filter((category) => category !== source.category));
    }

    if (mode === 'move' && activeCategory === source.category) {
      if (!sourcePath || activeSubcategory === sourcePath || activeSubcategory?.startsWith(`${sourcePath}${PATH_SEPARATOR}`)) {
        setActiveCategory(inserted.targetCategory);
        setActiveSubcategory(inserted.insertedPath);
      }
    }
  };

  const copyOrMoveCategorySection = async (category, target, mode) => {
    if (mode === 'move' && isSkeletonCategory(category)) {
      notify('Эта категория относится к стандартным частям речи и не может быть перенесена в другую категорию.');
      return;
    }
    if (mode === 'move') {
      const ok = await requestConfirm('Перенести раздел в другое место? Он переедет на новое место.', { title: 'Подтвердите перенос', confirmLabel: 'Перенести', tone: 'normal' });
      if (!ok) return;
    }
    copyOrMoveSection({ category, path: '' }, target, mode);
  };

  const copyOrMoveSubcategorySection = async (subcategory, target, mode) => {
    if (!activeCategory || !subcategory) return;
    if (mode === 'move') {
      const ok = await requestConfirm('Перенести подкатегорию в другое место? Она переедет на новое место.', { title: 'Подтвердите перенос', confirmLabel: 'Перенести', tone: 'normal' });
      if (!ok) return;
    }
    copyOrMoveSection({ category: activeCategory, path: subcategory }, target, mode);
  };

  const addDefaultSkeleton = () => {
    const userCanonicalIds = new Set();
    Object.keys(db).forEach((catName) => {
      const meta = categoryMeta[getCategoryMetaKey(catName)];
      const cid = meta?.canonicalId || getInferredCanonicalCategoryId(catName);
      if (cid) userCanonicalIds.add(cid);
    });
    const filteredFallbackDb = Object.entries(fallbackDb).reduce((acc, [catName, node]) => {
      const cid = getInferredCanonicalCategoryId(catName);
      if (cid && userCanonicalIds.has(cid)) return acc;
      acc[catName] = node;
      return acc;
    }, {});
    setDb((currentDb) => mergeDatabases(currentDb, filteredFallbackDb));
    setCategoryOrder((currentOrder) => normalizeCategoryOrder([
      ...currentOrder,
      ...Object.keys(filteredFallbackDb),
    ], mergeDatabases(db, filteredFallbackDb)));
    setCategoryColors((currentColors) => ({
      ...defaultColors,
      ...currentColors,
    }));
    setCategoryMeta((currentMeta) => Object.keys(filteredFallbackDb).reduce((meta, category) => ({
      ...meta,
      [getCategoryMetaKey(category)]: {
        ...(meta[getCategoryMetaKey(category)] || {}),
        canonicalId: getInferredCanonicalCategoryId(category),
      },
    }), { ...currentMeta }));
    setHiddenSkeletonCanonicalIds([]);
    setAuthStatus('Базовый скелет категорий восстановлен. Твои старые разделы и слова сохранены.');
  };

  const addSubcategory = (name, parentPath = activeSubcategory) => {
    const cleanName = name.trim();
    if (!activeCategory || !cleanName) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...(nextDb[activeCategory] || {}) };

      if (!parentPath) {
        currentCategory[cleanName] = currentCategory[cleanName] || [];
      } else {
        currentCategory = updateNodeAtPath(currentCategory, parentPath, (node) => {
          const normalizedNode = normalizeNode(node);

          return {
            ...normalizedNode,
            [cleanName]: normalizedNode[cleanName] || [],
          };
        });
      }

      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const renameSubcategory = (oldName, newName) => {
    const cleanName = newName.trim();
    if (!activeCategory || !oldName || !cleanName || oldName === cleanName) return;
    const oldPathParts = pathToArray(oldName);
    const oldLeafName = oldPathParts.at(-1);
    const parentPath = arrayToPath(oldPathParts.slice(0, -1));
    const newPath = arrayToPath([...oldPathParts.slice(0, -1), cleanName]);

    const parentNodeNow = normalizeNode(getNodeAtPath((db && db[activeCategory]) || {}, parentPath));
    if (!Object.prototype.hasOwnProperty.call(parentNodeNow, oldLeafName)) return;
    if (oldLeafName === cleanName) return;
    if (Object.prototype.hasOwnProperty.call(parentNodeNow, cleanName)) {
      if (typeof window !== 'undefined') notify('Подкатегория с названием «' + cleanName + '» здесь уже есть. Выбери другое название.');
      return;
    }

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...(nextDb[activeCategory] || {}) };

      const parentNode = normalizeNode(getNodeAtPath(currentCategory, parentPath));
      if (!Object.prototype.hasOwnProperty.call(parentNode, oldLeafName)) return currentDb;

      currentCategory = updateNodeAtPath(currentCategory, parentPath, (node) => {
        const normalizedNode = normalizeNode(node);
        const nextNode = { ...normalizedNode, [cleanName]: normalizedNode[oldLeafName] };
        delete nextNode[oldLeafName];
        if (Array.isArray(nextNode[ORDER_KEY])) {
          nextNode[ORDER_KEY] = nextNode[ORDER_KEY].map((n) => (n === oldLeafName ? cleanName : n));
        }
        return nextNode;
      });
      nextDb[activeCategory] = currentCategory;

      return nextDb;
    });

    if (activeSubcategory === oldName || activeSubcategory?.startsWith(`${oldName}${PATH_SEPARATOR}`)) {
      setActiveSubcategory(activeSubcategory.replace(oldName, newPath));
    }

    setCategoryMeta((currentMeta) => {
      const oldKey = getSubcategoryMetaKey(activeCategory, oldName);
      const newKey = getSubcategoryMetaKey(activeCategory, newPath);
      const nextMeta = { ...currentMeta };
      Object.keys(currentMeta)
        .filter((key) => key === oldKey || key.startsWith(`${oldKey}${PATH_SEPARATOR}`))
        .forEach((key) => {
          const renamedKey = key.replace(oldKey, newKey);
          nextMeta[renamedKey] = nextMeta[key];
          delete nextMeta[key];
        });
      const parentKey = getSubcategoryMetaKey(activeCategory, parentPath);
      const parentMeta = nextMeta[parentKey];
      if (parentMeta && parentMeta.rowOf && Object.prototype.hasOwnProperty.call(parentMeta.rowOf, oldLeafName)) {
        const nextRowOf = { ...parentMeta.rowOf };
        nextRowOf[cleanName] = nextRowOf[oldLeafName];
        delete nextRowOf[oldLeafName];
        nextMeta[parentKey] = { ...parentMeta, rowOf: nextRowOf };
      }
      return nextMeta;
    });
  };

  const markSubcategoryAsDeck = (subcategory) => {
    if (!activeCategory || !subcategory) return;
    const deckKey = activeCategory + PATH_SEPARATOR + subcategory;
    setDeckSubcategories((current) => {
      const list = Array.isArray(current) ? current : [];
      if (list.includes(deckKey)) return list;
      return [...list, deckKey];
    });
  };
  // Сделать колодой целую категорию: ключ = имя категории (без подкатегории),
  // тогда в колоду попадают все слова всех её подкатегорий.
  const markCategoryAsDeck = (category) => {
    if (!category) return;
    const deckKey = category;
    setDeckSubcategories((current) => {
      const list = Array.isArray(current) ? current : [];
      if (list.includes(deckKey)) return list;
      return [...list, deckKey];
    });
  };

  // Полное удаление колоды из служебной категории «Из анализатора» (вместе со словами-копиями).
  const deleteAnalyzerDeck = (deckKey) => {
    if (!deckKey) return;
    const parts = pathToArray(deckKey);
    const category = parts[0];
    if (category !== ANALYZER_DECK_CATEGORY) return;
    const leaf = parts[parts.length - 1];
    const parentPath = arrayToPath(parts.slice(1, -1));
    const catNode = db[category];
    let willBeEmpty = false;
    if (catNode) {
      const subKeys = Object.keys(catNode).filter((k) => k !== WORDS_KEY && k !== ORDER_KEY);
      const directWords = getWordsFromNode(catNode).length;
      if (parts.length === 2 && subKeys.length === 1 && subKeys[0] === leaf && directWords === 0) {
        willBeEmpty = true;
      }
    }
    if (willBeEmpty) {
      setDb((cur) => { const n = { ...cur }; delete n[category]; return n; });
      setCategoryColors((c) => { const n = { ...c }; delete n[category]; return n; });
      setCategoryOrder((o) => o.filter((i) => i !== category));
    } else {
      setDb((cur) => {
        const next = { ...cur };
        const node = next[category];
        if (!node) return cur;
        next[category] = updateNodeAtPath({ ...node }, parentPath, (nd) => {
          const nn = normalizeNode(nd);
          delete nn[leaf];
          if (Array.isArray(nn[ORDER_KEY])) nn[ORDER_KEY] = nn[ORDER_KEY].filter((x) => x !== leaf);
          return nn;
        });
        return next;
      });
    }
    setDeckSubcategories((curr) => (Array.isArray(curr) ? curr : []).filter((k) => k !== deckKey));
  };


  // Альтернативы анализатора: ручная замена слова (исходное → правильное слово/фраза), глобально.
  const setAnalyzerAlias = (key, value) => {
    const k = String(key || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const v = String(value || '').trim().slice(0, 50);
    if (!k) return;
    if (k.split(' ').filter(Boolean).length > 5) return;
    if (!v) { setAnalyzerAliases((prev) => { const n = { ...prev }; delete n[k]; return n; }); return; }
    setAnalyzerAliases((prev) => ({ ...prev, [k]: v }));
  };
  const removeAnalyzerAlias = (key) => {
    const k = String(key || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!k) return;
    setAnalyzerAliases((prev) => { const n = { ...prev }; delete n[k]; return n; });
  };

  // Собрать найденные в анализаторе слова в отдельную лёгкую колоду (без фото-данных).
  const createDeckFromAnalyzerText = (refs) => {
    if (!Array.isArray(refs) || refs.length === 0) {
      notify('В тексте нет слов из вашего словаря — собирать в колоду нечего.');
      return;
    }
    const SERVICE_CAT = ANALYZER_DECK_CATEGORY;
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const baseName = `${pad(now.getDate())}.${pad(now.getMonth() + 1)} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const serviceExisting = db[SERVICE_CAT] || {};
    let subName = baseName;
    let n = 2;
    while (serviceExisting[subName]) { subName = `${baseName} (${n})`; n += 1; }
    const copies = [];
    const seen = new Set();
    refs.forEach((ref) => {
      const key = String(ref.text || '').trim().toLowerCase();
      if (!key || seen.has(key)) return;
      const catNode = db[ref.category];
      if (!catNode) return;
      const node = ref.subcategory ? getNodeAtPath(catNode, ref.subcategory) : catNode;
      const src = getWordsFromNode(node).find((w) => String(w.text || '').trim().toLowerCase() === key);
      if (!src) return;
      const copy = JSON.parse(JSON.stringify(src));
      eachWordPhoto(copy, (ph) => (ph && ph.dataUrl
        ? { name: ph.name || '', type: ph.type || '', photoKey: ph.photoKey, position: ph.position }
        : undefined));
      copy.createdAt = Date.now() + copies.length;
      seen.add(key);
      copies.push(copy);
    });
    if (copies.length === 0) { notify('Не удалось собрать слова из текста.'); return; }
    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      const serviceNode = { ...(nextDb[SERVICE_CAT] || {}) };
      serviceNode[subName] = { [WORDS_KEY]: copies };
      nextDb[SERVICE_CAT] = serviceNode;
      return nextDb;
    });
    setCategoryColors((c) => (c[SERVICE_CAT] ? c : { ...c, [SERVICE_CAT]: '#64748b' }));
    setCategoryOrder((o) => (o.includes(SERVICE_CAT) ? o : [...o, SERVICE_CAT]));
    const deckKey = SERVICE_CAT + PATH_SEPARATOR + subName;
    setDeckSubcategories((list) => {
      const arr = Array.isArray(list) ? list : [];
      return arr.includes(deckKey) ? arr : [...arr, deckKey];
    });
    const cnt = copies.length;
    const wf = (cnt % 10 === 1 && cnt % 100 !== 11) ? 'слово'
      : ((cnt % 10 >= 2 && cnt % 10 <= 4 && (cnt % 100 < 10 || cnt % 100 >= 20)) ? 'слова' : 'слов');
    notify(`Колода создана: ${cnt} ${wf}. Открой раздел «Учить слова» — папка «${subName}» в категории «${SERVICE_CAT}».`);
  };



  const deleteSubcategory = (subcategory) => {
    if (!activeCategory || !subcategory) return;
    const pathParts = pathToArray(subcategory);
    const leafName = pathParts.at(-1);
    if (activeCategory !== ANALYZER_DECK_CATEGORY) {
      const wordCountSub = countWordsInNode(getNodeAtPath(db[activeCategory], subcategory));
      if (wordCountSub > 0) {
        notify('Нельзя удалить подкатегорию «' + leafName + '», пока внутри есть слова (' + wordCountSub + '). Сначала удалите все слова из неё и её вложенных подкатегорий, и только потом удаляйте пустую подкатегорию.', { title: 'Подкатегория не пустая' });
        return;
      }
    }
    setSubcategoryDeleteConfirm({ subcategory, leafName, activeCategory });
  };

  const confirmDeleteSubcategory = () => {
    if (!subcategoryDeleteConfirm) return;
    const { subcategory, leafName, activeCategory: targetCategory } = subcategoryDeleteConfirm;
    setSubcategoryDeleteConfirm(null);
    if (!targetCategory || !subcategory) return;
    const pathParts = pathToArray(subcategory);
    const parentPath = arrayToPath(pathParts.slice(0, -1));

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...(nextDb[targetCategory] || {}) };

      currentCategory = updateNodeAtPath(currentCategory, parentPath, (node) => {
        const nextNode = normalizeNode(node);
        delete nextNode[leafName];
        if (Array.isArray(nextNode[ORDER_KEY])) {
          nextNode[ORDER_KEY] = nextNode[ORDER_KEY].filter((n) => n !== leafName);
        }
        return nextNode;
      });
      nextDb[targetCategory] = currentCategory;

      return nextDb;
    });

    if (activeSubcategory === subcategory || activeSubcategory?.startsWith(`${subcategory}${PATH_SEPARATOR}`)) {
      setActiveSubcategory(parentPath || null);
    }

    setCategoryMeta((currentMeta) => {
      const nextMeta = { ...currentMeta };
      Object.keys(nextMeta).forEach((key) => {
        const prefix = getSubcategoryMetaKey(targetCategory, subcategory);
        if (key === prefix || key.startsWith(`${prefix}${PATH_SEPARATOR}`)) delete nextMeta[key];
      });
      return nextMeta;
    });
  };

  const getSubcategoryRowsMeta = (category, subcategoryPath) => {
    const key = getSubcategoryMetaKey(category, subcategoryPath || '');
    const meta = categoryMeta[key] || {};
    return {
      rows: Array.isArray(meta.rows) ? meta.rows : [],
      rowOf: meta.rowOf && typeof meta.rowOf === 'object' ? meta.rowOf : {},
    };
  };

  const addRowToSubcategory = (subcategoryPath, rawName) => {
    if (!activeCategory) return;
    const name = (rawName || '').trim();
    if (!name) return;
    const key = getSubcategoryMetaKey(activeCategory, subcategoryPath || '');
    setCategoryMeta((current) => {
      const prev = current[key] || {};
      const prevRows = Array.isArray(prev.rows) ? prev.rows : [];
      if (prevRows.includes(name)) return current;
      return { ...current, [key]: { ...prev, rows: [...prevRows, name] } };
    });
  };

  const deleteRowFromSubcategory = (subcategoryPath, rowName) => {
    if (!activeCategory || !rowName) return false;
    const key = getSubcategoryMetaKey(activeCategory, subcategoryPath || '');
    const meta = categoryMeta[key] || {};
    const rowOf = (meta.rowOf && typeof meta.rowOf === 'object') ? meta.rowOf : {};
    const occupied = Object.values(rowOf).some((r) => r === rowName);
    if (occupied) return false;
    setCategoryMeta((current) => {
      const prev = current[key] || {};
      const prevRows = Array.isArray(prev.rows) ? prev.rows : [];
      return { ...current, [key]: { ...prev, rows: prevRows.filter((r) => r !== rowName) } };
    });
    return true;
  };

  const handleDeleteRowClick = () => {
    if (!deleteRowModal || !deleteRowModal.selected) return;
    const ok = deleteRowFromSubcategory(deleteRowModal.subcategoryPath, deleteRowModal.selected);
    if (ok === false) {
      setDeleteRowModal((m) => m ? { ...m, error: 'В этом рядке есть подкатегории — сначала очисти его.' } : m);
      return;
    }
    setDeleteRowModal(null);
  };

  const setSubcategoryRow = (subcategoryPath, childName, rowName) => {
    if (!activeCategory || !childName) return;
    const key = getSubcategoryMetaKey(activeCategory, subcategoryPath || '');
    setCategoryMeta((current) => {
      const prev = current[key] || {};
      const prevRowOf = (prev.rowOf && typeof prev.rowOf === 'object') ? prev.rowOf : {};
      const nextRowOf = { ...prevRowOf };
      if (rowName) nextRowOf[childName] = rowName;
      else delete nextRowOf[childName];
      return { ...current, [key]: { ...prev, rowOf: nextRowOf } };
    });
  };

  const moveSubcategoryRow = (subcategoryPath, rowName, direction) => {
    if (!activeCategory || !rowName) return;
    const key = getSubcategoryMetaKey(activeCategory, subcategoryPath || '');
    setCategoryMeta((current) => {
      const prev = current[key] || {};
      const prevRows = Array.isArray(prev.rows) ? [...prev.rows] : [];
      const idx = prevRows.indexOf(rowName);
      if (idx === -1) return current;
      const swapWith = direction === 'up' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= prevRows.length) return current;
      const tmp = prevRows[idx];
      prevRows[idx] = prevRows[swapWith];
      prevRows[swapWith] = tmp;
      return { ...current, [key]: { ...prev, rows: prevRows } };
    });
  };

  const renameSubcategoryRow = (subcategoryPath, oldName, newName) => {
    if (!activeCategory || !oldName) return;
    const name = (newName || '').trim();
    if (!name || name === oldName) return;
    const key = getSubcategoryMetaKey(activeCategory, subcategoryPath || '');
    setCategoryMeta((current) => {
      const prev = current[key] || {};
      const prevRows = Array.isArray(prev.rows) ? [...prev.rows] : [];
      if (prevRows.includes(name)) return current;
      const idx = prevRows.indexOf(oldName);
      if (idx === -1) return current;
      prevRows[idx] = name;
      const prevRowOf = (prev.rowOf && typeof prev.rowOf === 'object') ? prev.rowOf : {};
      const nextRowOf = {};
      Object.keys(prevRowOf).forEach((child) => {
        nextRowOf[child] = prevRowOf[child] === oldName ? name : prevRowOf[child];
      });
      return { ...current, [key]: { ...prev, rows: prevRows, rowOf: nextRowOf } };
    });
    setDb((currentDb) => {
      if (!currentDb[activeCategory]) return currentDb;
      let currentCategory = { ...currentDb[activeCategory] };
      const words = [...getWordsFromNode(getNodeAtPath(currentCategory, subcategoryPath || ''))];
      let changed = false;
      const nextWords = words.map((w) => {
        if (w && w.row === oldName) { changed = true; return { ...w, row: name }; }
        return w;
      });
      if (!changed) return currentDb;
      currentCategory = setWordsAtPath(currentCategory, subcategoryPath || '', nextWords);
      return { ...currentDb, [activeCategory]: currentCategory };
    });
  };

  const setSubcategoryIcon = (subcategory, iconId) => {
    if (!activeCategory || !subcategory) return;

    setCategoryMeta((currentMeta) => {
      const metaKey = getSubcategoryMetaKey(activeCategory, subcategory);
      const nextMeta = {
        ...currentMeta,
        [metaKey]: {
          ...(currentMeta[metaKey] || {}),
          iconId,
        },
      };

      if (!iconId) {
        delete nextMeta[metaKey].iconId;
        if (Object.keys(nextMeta[metaKey]).length === 0) delete nextMeta[metaKey];
      }

      return nextMeta;
    });
  };

  const setActivePanelDescription = (description) => {
    if (!activeCategory) return;

    const cleanDescription = description.trim();
    const metaKey = activeSubcategory
      ? getSubcategoryMetaKey(activeCategory, activeSubcategory)
      : getCategoryMetaKey(activeCategory);

    setCategoryMeta((currentMeta) => {
      const nextMeta = {
        ...currentMeta,
        [metaKey]: {
          ...(currentMeta[metaKey] || {}),
          description: cleanDescription,
        },
      };

      if (!cleanDescription && Object.keys(nextMeta[metaKey]).length === 1) {
        delete nextMeta[metaKey];
      }

      return nextMeta;
    });
  };

  const reorderSubcategories = (fromIndex, toIndex, parentPath = '') => {
    if (!activeCategory || fromIndex === toIndex) return;

    setDb((currentDb) => {
      const nextDb = { ...currentDb };
      let currentCategory = { ...(nextDb[activeCategory] || {}) };
      const parentNode = normalizeNode(getNodeAtPath(currentCategory, parentPath));
      const subNames = getSubcategoryNamesFromNode(parentNode);
      const movedName = subNames[fromIndex];
      if (!movedName) return currentDb;
      const newOrder = subNames.slice();
      newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedName);
      const wordEntries = parentNode[WORDS_KEY] ? [[WORDS_KEY, parentNode[WORDS_KEY]]] : [];
      const subEntries = newOrder.map((name) => [name, parentNode[name]]);
      const reorderedNode = Object.fromEntries([...wordEntries, ...subEntries, [ORDER_KEY, newOrder]]);
      currentCategory = updateNodeAtPath(currentCategory, parentPath, () => reorderedNode);
      nextDb[activeCategory] = currentCategory;
      return nextDb;
    });
  };

  const setSentenceFromPhraseMoves = (moves) => {
    setSentence(phraseMovesToSentenceItems(moves || []));
    setPositionHistory(['START', ...(moves || []).map((move) => move.word_category)]);
  };

  const hasReachedDailyPublicPhraseLimit = async () => {
    const { count, error } = await supabase
      .from('phrase_games')
      .select('id', { count: 'exact', head: true })
      .eq('author_id', userProfile.id)
      .gte('created_at', getLocalDayStartIso());

    if (error) {
      setPhraseGamesError(translateSupabaseError(error.message));
      return true;
    }

    return (count || 0) >= MAX_PUBLIC_PHRASES_PER_DAY;
  };

  useEffect(() => {
    if (!activePhraseGameId || !activePhraseGame) return;
    setSentenceFromPhraseMoves(activePhraseGame.moves || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activePhraseGameId,
    activePhraseMoveSignature,
  ]);

  const savePhraseMove = async (wordText, role, game = activePhraseGame) => {
    if (!userProfile.id || !wordText || !role) return false;
    if (isProfileBlocked(userProfile)) {
      setPhraseGamesError('Профиль заблокирован модератором. Ходы недоступны.');
      return false;
    }
    const canonicalRole = getCanonicalCategoryForRole(role);

    if (!game) {
      const dailyLimitReached = await hasReachedDailyPublicPhraseLimit();
      if (dailyLimitReached) {
        setPhraseGamesError('Сегодня уже создано 3 публичные фразы. Продолжайте чужие фразы или вернитесь завтра.');
        return false;
      }

      const { data: createdGame, error: gameError } = await supabase
        .from('phrase_games')
        .insert({
          author_id: userProfile.id,
          status: 'active',
        })
        .select('id, author_id, status, created_at, updated_at')
        .single();

      if (gameError) {
        setPhraseGamesError(translateSupabaseError(gameError.message));
        return false;
      }

      const firstMove = {
        game_id: createdGame.id,
        user_id: userProfile.id,
        move_index: 1,
        word_text: wordText,
        word_category: canonicalRole,
        word_path: activeSubcategory || '',
        word_level: '',
      };
      const { data: insertedMove, error: moveError } = await supabase
        .from('phrase_moves')
        .insert(firstMove)
        .select('id, game_id, user_id, move_index, word_text, word_category, word_path, word_level, created_at')
        .single();

      if (moveError) {
        setPhraseGamesError(translateSupabaseError(moveError.message));
        return false;
      }

      setActivePhraseGameId(createdGame.id);
      setIsWatchingPhrase(false);
      setSentenceFromPhraseMoves([insertedMove]);
      await loadPhraseGames();
      return true;
    }

    if (!game.canMove) {
      setPhraseGamesError(game.moveBlockReason || 'Сейчас ход другого участника. Выберите зеленую фразу или начните новую.');
      return false;
    }

    const nextMove = {
      game_id: game.id,
      user_id: userProfile.id,
      move_index: (game.moves || []).length + 1,
      word_text: wordText,
      word_category: canonicalRole,
      word_path: activeSubcategory || '',
      word_level: '',
    };
    const { data: insertedMove, error: moveError } = await supabase
      .from('phrase_moves')
      .insert(nextMove)
      .select('id, game_id, user_id, move_index, word_text, word_category, word_path, word_level, created_at')
      .single();

    if (moveError) {
      setPhraseGamesError(translateSupabaseError(moveError.message));
      return false;
    }

    const nextMoves = [...(game.moves || []), insertedMove];
    setSentenceFromPhraseMoves(nextMoves);
    if (shouldAutoFinishPhrase(nextMoves)) {
      await closePhraseGameWithEnding({ ...game, moves: nextMoves }, '.');
      setPhraseGamesError('Фраза достигла лимита и завершена автоматически.');
      return true;
    }

    await loadPhraseGames();
    return true;
  };

  const closePhraseGameWithEnding = async (game, ending = '.') => {
    const lastMove = game?.moves?.at(-1);
    if (!lastMove) return;

    const finishedLastMoveText = appendPhraseEnding(lastMove.word_text, ending);
    if (finishedLastMoveText !== lastMove.word_text) {
      const { error: moveError } = await supabase
        .from('phrase_moves')
        .update({ word_text: finishedLastMoveText })
        .eq('id', lastMove.id)
        .eq('game_id', game.id);

      if (moveError) {
        setPhraseGamesError(translateSupabaseError(moveError.message));
        return false;
      }
    }

    const { error } = await supabase
      .from('phrase_games')
      .update({ status: 'finished' })
      .eq('id', game.id);

    if (error) {
      setPhraseGamesError(translateSupabaseError(error.message));
      return false;
    }

    setIsFinishMenuOpen(false);
    setSentence([]);
    setPositionHistory(['START']);
    setActivePhraseGameId(null);
    setIsComposingPhrase(false);
    setIsWatchingPhrase(false);
    await loadPhraseGames();
    return true;
  };

  const finishPhraseGame = async (ending = '.') => {
    if (!activePhraseGame || activePhraseGame.author_id !== userProfile.id) return;
    await closePhraseGameWithEnding(activePhraseGame, ending);
  };

  const selectWord = async (word, role) => {
    const sentenceText = getWordSentenceText(word, role);
    const savedToPhrase = await savePhraseMove(sentenceText, role);
    if (savedToPhrase || userProfile.id) {
      setActiveCategory(null);
      setActiveSubcategory(null);
      return;
    }

    setSentence((currentSentence) => [
      ...currentSentence,
      {
        text: sentenceText,
        role: getCanonicalCategoryForRole(role),
        sourceCategory: activeCategory,
        sourceSubcategory: activeSubcategory,
      },
    ]);
    setPositionHistory((history) => [...history, getCanonicalCategoryForRole(role)]);
    setActiveCategory(null);
    setActiveSubcategory(null);
  };

  const selectWordForm = async (text, role) => {
    if (!text) return;

    const savedToPhrase = await savePhraseMove(text, role);
    if (savedToPhrase || userProfile.id) {
      setActiveCategory(null);
      setActiveSubcategory(null);
      return;
    }

    setSentence((currentSentence) => [
      ...currentSentence,
      {
        text,
        role: getCanonicalCategoryForRole(role),
        sourceCategory: activeCategory,
        sourceSubcategory: activeSubcategory,
      },
    ]);
    setPositionHistory((history) => [...history, getCanonicalCategoryForRole(role)]);
    setActiveCategory(null);
    setActiveSubcategory(null);
  };

  const stepBack = () => {
    if (activePhraseGameId) {
      setPhraseGamesError('В совместной фразе ход уже сохранен. Его нельзя вернуть назад.');
      return;
    }

    const lastSentenceItem = sentence[sentence.length - 1];

    setSentence((currentSentence) => currentSentence.slice(0, -1));
    setPositionHistory((history) => history.slice(0, -1));

    if (lastSentenceItem?.sourceCategory) {
      setActiveCategory(lastSentenceItem.sourceCategory);
      setActiveSubcategory(lastSentenceItem.sourceSubcategory || null);
    }
  };

  const cancelLastSentenceAction = async () => {
    if (!activePhraseGameId) {
      stepBack();
      return;
    }

    const { data: ok, error: rpcError } = await supabase.rpc('cancel_my_last_phrase_move', { p_game_id: activePhraseGameId });

    if (rpcError) {
      setPhraseGamesError(translateSupabaseError(rpcError.message));
      return;
    }
    if (ok === false) {
      setPhraseGamesError('Отменить можно только свой последний ход, пока после него никто не добавил слово.');
      return;
    }

    setActivePhraseGameId(null);
    setIsWatchingPhrase(false);
    setSentence([]);
    setPositionHistory(['START']);
    setPhraseGamesError('');
    await loadPhraseGames();
  };

  const startNewPhraseDraft = () => {
    setActivePhraseGameId(null);
    setIsWatchingPhrase(false);
    setIsComposingPhrase(true);
    setPhraseGamesError('');
    setSentence([]);
    setPositionHistory(['START']);
  };

  const showEvikWordUnderSearch = (word) => {
    const w = String(word || '').trim();
    if (!w) return;
    setDatabaseSearch(w);
    const norm = w.toLowerCase();
    setEvikHighlight(norm);
    window.setTimeout(() => { setEvikHighlight((cur) => (cur === norm ? '' : cur)); }, 2500);
  };

  const addToSearchHistory = (rawWord) => {
    const w = String(rawWord || '').trim();
    if (!w) return;
    setSearchHistory((prev) => {
      const wl = w.toLowerCase();
      const next = [w, ...prev.filter((x) => String(x).toLowerCase() !== wl)].slice(0, 5);
      try { localStorage.setItem('norsk_search_history', JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  useEffect(() => {
    if (publicPage && (publicPage.wordText || publicPage.wordNorm)) {
      addToSearchHistory(publicPage.wordText || publicPage.wordNorm);
    }
  }, [publicPage && publicPage.openedAt]);

  const openSearchResult = (result) => {
    addToSearchHistory(result.word);
    setActiveCategory(result.category);
    setActiveSubcategory(result.subcategory || null);
    setSearchHighlight({
      category: result.category,
      subcategory: result.subcategory || null,
      word: result.word,
      createdAt: Date.now(),
    });
    setDeskDict(true);
    setActiveMobileSection('dictionary');
    setDatabaseSearch('');
  };

  const openSearchResultMenu = (result, x, y) => {
    setActiveCategory(result.category);
    setActiveSubcategory(result.subcategory || null);
    setDeskDict(true);
    setActiveMobileSection('dictionary');
    setSearchHighlight({ category: result.category, subcategory: result.subcategory || null, word: result.word, createdAt: Date.now(), openMenu: true, x, y });
  };

  useEffect(() => {
    if (!searchHighlight) return undefined;

    const timer = window.setTimeout(() => setSearchHighlight(null), 5000);
    return () => window.clearTimeout(timer);
  }, [searchHighlight]);

  useEffect(() => {
    if (publicPage) setDatabaseSearch('');
  }, [publicPage]);

  // Держим актуальный снимок места работы.
  useEffect(() => {
    navSnapshotRef.current = {
      activeCategory, activeSubcategory, activeMobileSection,
      deskDict, deskAnalyzer, isLearningMode, analyzerText,
    };
  }, [activeCategory, activeSubcategory, activeMobileSection, deskDict, deskAnalyzer, isLearningMode, analyzerText]);

  // Сохраняем снимок при уходе со страницы — тогда ЛЮБАЯ перезагрузка вернёт на место.
  useEffect(() => {
    const save = () => {
      try {
        if (navSnapshotRef.current) localStorage.setItem('norsk_nav_restore_v1', JSON.stringify(navSnapshotRef.current));
      } catch (_) {}
    };
    const onVis = () => { if (document.visibilityState === 'hidden') save(); };
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('pagehide', save); document.removeEventListener('visibilitychange', onVis); };
  }, []);


  const downloadDatabase = () => {
    const payload = {
      app: 'Norsk Pro',
      format: 'norsk-pro-database',
      version: 1,
      exportedAt: new Date().toISOString(),
      data: db,
      colors: categoryColors,
      categoryMeta,
      categoryOrder: normalizeCategoryOrder(categoryOrder, db),
      constructorGroups: normalizeConstructorGroups(constructorGroups, db),
      analysisLibrary,
      learningProgress,
      deckSubcategories,
      analyzerAliases,
      userProfile,
    };
    const fileText = JSON.stringify(payload, null, 2);
    const date = new Date().toISOString().slice(0, 10);

    downloadTextFile(fileText, `norsk-pro-database-${date}.txt`);
  };

  const downloadAllWords = () => {
    const lines = [];
    let totalWords = 0;

    const writeWordsAt = (node, indent) => {
      const normalizedNode = normalizeNode(node);
      const seen = new Set();
      getWordsFromNode(normalizedNode).forEach((word) => {
        const cleanWord = normalizeWordText(word.text || '');
        if (!cleanWord) return;
        if (seen.has(cleanWord)) return;
        seen.add(cleanWord);
        lines.push(indent + cleanWord);
        totalWords += 1;
      });
    };

    const walkNode = (node, name, depth) => {
      const prefix = '  '.repeat(depth);
      lines.push(prefix + name + ':');
      writeWordsAt(node, prefix + '  ');
      const normalizedNode = normalizeNode(node);
      Object.entries(normalizedNode).forEach(([subName, childNode]) => {
        if (subName === WORDS_KEY) return;
        walkNode(childNode, subName, depth + 1);
      });
    };

    Object.entries(db).forEach(([categoryName, categoryNode]) => {
      walkNode(categoryNode, categoryName, 0);
      lines.push('');
    });

    if (totalWords === 0) {
      notify('В базе пока нет слов для скачивания.');
      return;
    }

    const date = new Date().toISOString().slice(0, 10);
    downloadTextFile(lines.join('\n'), `norsk-pro-all-words-${date}.txt`);
  };

  const importDatabase = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const importedDb = payload?.data || payload?.db || payload;

      if (!importedDb || typeof importedDb !== 'object' || Array.isArray(importedDb)) {
        notify('Не получилось найти базу слов в этом файле.');
        return;
      }

      const mode = await requestChoice('Как загрузить базу из файла?', {
        title: 'Загрузка базы',
        buttons: [
          { label: 'Дополнить', value: 'append' },
          { label: 'Заменить', value: 'replace', tone: 'danger' },
        ],
      });
      if (!mode) return;

      cloudLoadedRef.current = true;
      const normalizedImportedDb = syncReciprocalRelations(normalizeDb(importedDb));
      const importedColors = payload?.colors || {};
      const importedMeta = payload?.categoryMeta && typeof payload.categoryMeta === 'object'
        ? payload.categoryMeta
        : {};
      const importedOrder = normalizeCategoryOrder(payload?.categoryOrder, normalizedImportedDb);
      const importedConstructorGroups = normalizeConstructorGroups(payload?.constructorGroups, normalizedImportedDb);
      const importedLearningProgress = normalizeLearningProgress(payload?.learningProgress);
      const importedUserProfile = normalizeUserProfile(payload?.userProfile);

      if (mode === 'append') {
        setDb((currentDb) => syncReciprocalRelations(mergeDatabases(currentDb, normalizedImportedDb)));
        setCategoryOrder((currentOrder) => normalizeCategoryOrder([
          ...currentOrder,
          ...importedOrder,
        ], mergeDatabases(db, normalizedImportedDb)));
        setCategoryColors((currentColors) => normalizeColors({ ...importedColors, ...currentColors }));
        setCategoryMeta((currentMeta) => ({ ...importedMeta, ...currentMeta }));
        setConstructorGroups((currentGroups) => {
          const currentNormalizedGroups = normalizeConstructorGroups(currentGroups, db);
          const existingKeys = new Set(currentNormalizedGroups.map((group) => (
            `${group.label}${PATH_SEPARATOR}${group.category}${PATH_SEPARATOR}${group.path}`
          )));
          return [
            ...currentNormalizedGroups,
            ...importedConstructorGroups.filter((group) => {
              const key = `${group.label}${PATH_SEPARATOR}${group.category}${PATH_SEPARATOR}${group.path}`;
              return !existingKeys.has(key);
            }),
          ];
        });
        setLearningProgress((currentProgress) => {
          const normalizedProgress = normalizeLearningProgress(currentProgress);
          return {
            learned: { ...importedLearningProgress.learned, ...normalizedProgress.learned },
            difficult: { ...importedLearningProgress.difficult, ...normalizedProgress.difficult },
            reviewQueue: { ...importedLearningProgress.reviewQueue, ...normalizedProgress.reviewQueue },
            totalSeen: Math.max(importedLearningProgress.totalSeen, normalizedProgress.totalSeen),
          };
        });
        if (Array.isArray(payload?.deckSubcategories)) {
          setDeckSubcategories((cur) => Array.from(new Set([...(cur || []), ...payload.deckSubcategories])));
        }
        if (payload?.analyzerAliases && typeof payload.analyzerAliases === 'object') {
          setAnalyzerAliases((cur) => ({ ...(cur || {}), ...payload.analyzerAliases }));
        }
      } else if (mode === 'replace') {
        const shouldReplace = await requestConfirm('Точно заменить текущую базу содержимым файла? Текущая база будет перезаписана.', { title: 'Замена базы', confirmLabel: 'Заменить' });
        if (!shouldReplace) return;

        setDb(normalizedImportedDb);
        setCategoryOrder(importedOrder);
        setCategoryColors(normalizeColors(importedColors));
        setCategoryMeta(importedMeta);
        setConstructorGroups(importedConstructorGroups);
        setAnalysisLibrary(normalizeAnalysisLibrary(payload?.analysisLibrary));
        setLearningProgress(importedLearningProgress);
        if (Array.isArray(payload?.deckSubcategories)) setDeckSubcategories(payload.deckSubcategories);
        if (payload?.analyzerAliases && typeof payload.analyzerAliases === 'object') setAnalyzerAliases(payload.analyzerAliases);
        if (importedUserProfile.email && importedUserProfile.nickname) {
          setUserProfile(importedUserProfile);
          setRegistrationDraft(importedUserProfile);
        }
      } else {
        return;
      }

      setSentence([]);
      setPositionHistory(['START']);
      setActiveCategory(null);
      setActiveSubcategory(null);
      setAnalyzerText('');
      if (userProfile.id) {
        window.setTimeout(() => migrateInlinePhotos(userProfile.id), 500);
      }
    } catch {
      notify('Не получилось загрузить файл. Проверь, что это экспорт базы Norsk Pro.');
    }
  };

  const changeProfileAvatar = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const allowedPhotoTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
    const allowedPhotoExtensions = /\.(jpe?g|png|webp|heic|heif)$/i;
    const maxPhotoSize = 3 * 1024 * 1024;

    if (!allowedPhotoTypes.has(file.type) && !allowedPhotoExtensions.test(file.name)) {
      setAuthStatus('Фото аватара должно быть в формате JPG, PNG, WEBP, HEIC или HEIF.');
      return;
    }

    if (file.size > maxPhotoSize) {
      setAuthStatus('Фото аватара должно быть не больше 3 МБ.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const avatar = {
        name: file.name,
        type: file.type || 'image/jpeg',
        dataUrl: reader.result,
      };

      setUserProfile((current) => ({ ...current, photo: avatar }));
      setRegistrationDraft((current) => ({ ...current, photo: avatar }));

      if (userProfile.id) {
        const { error } = await supabase
          .from('profiles')
          .update({ avatar })
          .eq('id', userProfile.id);

        if (error) {
          setAuthStatus(`Не удалось сохранить аватар: ${translateSupabaseError(error.message)}`);
        }
      }
    };
    reader.onerror = () => setAuthStatus('Не удалось загрузить фото аватара.');
    reader.readAsDataURL(file);
  };

  // Kept only as a local fallback while Supabase auth is being wired in.
  // eslint-disable-next-line no-unused-vars
  const submitRegistration = async (event) => {
    event.preventDefault();

    const nextProfile = normalizeUserProfile(registrationDraft);
    const nextPassword = typeof registrationDraft.password === 'string' ? registrationDraft.password.trim() : '';
    const normalizedNickname = normalizeNicknameForRegistry(nextProfile.nickname);

    if (authMode === 'login') {
      if (!nextProfile.email || !nextPassword) {
        setRegistrationError(t('fillEmailPass'));
        return;
      }
    } else if (!nextProfile.email || !nextProfile.nickname || !nextPassword) {
      setRegistrationError(t('fillAll'));
      return;
    }

    if (authMode === 'register' && !isValidEmail(nextProfile.email)) {
      setRegistrationError('Укажите корректный email: латинские буквы, цифры, @, точка, дефис, плюс или подчеркивание.');
      return;
    }

    if (nextProfile.email.length > 80) {
      setRegistrationError('Email должен быть не длиннее 80 символов.');
      return;
    }

    if (!/^[\p{L}\p{N}_ -]{2,32}$/u.test(nextProfile.nickname)) {
      setRegistrationError('Ник: 2-32 символа, буквы, цифры, пробел, дефис или подчеркивание.');
      return;
    }

    if (nextPassword.length < 6) {
      setRegistrationError('Пароль должен быть не короче 6 символов.');
      return;
    }

    if (nextPassword.length > 20) {
      setRegistrationError('Пароль должен быть не длиннее 20 символов.');
      return;
    }

    const normalizedEmail = nextProfile.email.trim().toLowerCase();
    const emailTaken = userRegistry.some((entry) => entry?.email === normalizedEmail);

    if (emailTaken) {
      setRegistrationError('Этот email уже зарегистрирован. Используйте вход для зарегистрированных пользователей.');
      return;
    }

    const nicknameTaken = userRegistry.some((entry) => entry?.nickname === normalizedNickname);

    if (nicknameTaken) {
      setRegistrationError('Такой ник уже занят. Попробуйте другой вариант или добавьте цифры.');
      return;
    }

    const profileToSave = {
      ...nextProfile,
      email: normalizedEmail,
      nickname: nextProfile.nickname.trim().replace(/\s+/g, ' '),
      registeredAt: nextProfile.registeredAt || new Date().toISOString(),
    };

    setUserRegistry((current) => {
      const withoutCurrentEmail = current.filter((entry) => entry?.email !== profileToSave.email);
      return [
        ...withoutCurrentEmail,
        {
          email: profileToSave.email,
          nickname: normalizedNickname,
          registeredAt: profileToSave.registeredAt,
        },
      ];
    });
    setUserProfile(profileToSave);
    setRegistrationDraft((current) => ({ ...current, password: '' }));
    setRegistrationError('');
  };

  const awardRegistrationBadge = async () => {
    try {
      const { data, error } = await supabase.rpc('award_badge', { badge_key: 'registration' });
      if (error) return;
      if (data === true) {
        setLastBadgeKey('registration');
        setShowElephantBadge(true);
        setUserProfile((current) => {
          const list = Array.isArray(current?.badges) ? current.badges : [];
          if (list.includes('registration')) return current;
          return { ...current, badges: [...list, 'registration'] };
        });
      }
    } catch (e) {}
  };

  const submitAuth = async (event) => {
    event.preventDefault();

    const nextProfile = normalizeUserProfile(registrationDraft);
    const nextPassword = typeof registrationDraft.password === 'string' ? registrationDraft.password.trim() : '';
    const loginIdentifier = nextProfile.email.trim();
    let normalizedEmail = loginIdentifier.toLowerCase();
    const normalizedNickname = normalizeNicknameForRegistry(nextProfile.nickname);

    if (!nextProfile.email || !nextPassword || (authMode === 'register' && !nextProfile.nickname)) {
      setRegistrationError(authMode === 'login' ? t('fillEmailPass') : t('fillAll'));
      return;
    }

    if (!isValidEmail(nextProfile.email)) {
      setRegistrationError('Укажите корректный email.');
      return;
    }

    if (nextProfile.email.length > 80) {
      setRegistrationError('Email должен быть не длиннее 80 символов.');
      return;
    }

    if (authMode === 'register' && !/^[\p{L}\p{N}_ -]{2,32}$/u.test(nextProfile.nickname)) {
      setRegistrationError('Ник: 2-32 символа, буквы, цифры, пробел, дефис или подчеркивание.');
      return;
    }

    if (nextPassword.length < 6 || nextPassword.length > 20) {
      setRegistrationError('Пароль должен быть от 6 до 20 символов.');
      return;
    }

    setIsAuthLoading(true);
    setRegistrationError('');
    setAuthStatus('');

    try {
      if (authMode === 'login') {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password: nextPassword,
        });

        if (error) throw error;

        const profile = await loadCloudProfile(data.user, true);
        if (!profile) {
          const fallbackNickname = sanitizeRegistrationNickname(
            data.user.user_metadata?.nickname || nextProfile.nickname || normalizedEmail.split('@')[0],
          ).trim();

          if (!fallbackNickname) {
            setRegistrationError('Аккаунт найден. Введите ник, чтобы создать профиль.');
            return;
          }

          const profileToSave = {
            id: data.user.id,
            email: normalizedEmail,
            nickname: fallbackNickname,
            photo: nextProfile.photo,
            registeredAt: new Date().toISOString(),
          };

          const { error: profileError } = await supabase
            .from('profiles')
            .insert({
              id: profileToSave.id,
              email: profileToSave.email,
              nickname: profileToSave.nickname,
              avatar: profileToSave.photo,
            });

          if (profileError) {
            throw profileError;
          }

          await supabase
            .from('user_databases')
            .upsert({
              user_id: profileToSave.id,
              data: createEmptyCloudDataPayload(),
            }, { onConflict: 'user_id' });

          setUserProfile(profileToSave);
          applyCloudDataPayload(createEmptyCloudDataPayload());
          setRegistrationDraft((current) => ({ ...profileToSave, password: current.password ? '' : '' }));
          awardRegistrationBadge();
          return;
        }

        setRegistrationDraft((current) => ({ ...current, password: '' }));
        return;
      }

      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password: nextPassword,
        options: {
          data: {
            nickname: nextProfile.nickname.trim().replace(/\s+/g, ' '),
            lang: uiLang,
          },
          emailRedirectTo: window.location.origin + '/?lang=' + uiLang,
        },
      });

      if (error) throw error;

      if (!data.session || !data.user) {
        try {
          localStorage.setItem(STORAGE_KEYS.pendingRegistration, JSON.stringify({
            email: normalizedEmail,
            nickname: nextProfile.nickname.trim().replace(/\s+/g, ' '),
            photo: nextProfile.photo || null,
            savedAt: new Date().toISOString(),
          }));
        } catch (e) {}
        setAuthStatus(t('regCreated'));
        return;
      }

      const profileToSave = {
        id: data.user.id,
        email: normalizedEmail,
        nickname: nextProfile.nickname.trim().replace(/\s+/g, ' '),
        photo: nextProfile.photo,
        registeredAt: new Date().toISOString(),
      };

      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: profileToSave.id,
          email: profileToSave.email,
          nickname: profileToSave.nickname,
          avatar: profileToSave.photo,
          status: 'pending',
          registered_at: new Date().toISOString(),
        });

      if (profileError) {
        if (/duplicate key|unique/i.test(profileError.message)) {
          setRegistrationError('Email или ник уже занят. Попробуйте войти, выбрать другой ник или добавить цифры.');
          return;
        }
        throw profileError;
      }

      await supabase
        .from('user_databases')
        .upsert({
          user_id: profileToSave.id,
          data: createEmptyCloudDataPayload(),
        }, { onConflict: 'user_id' });

      setUserRegistry((current) => [
        ...current.filter((entry) => entry?.email !== profileToSave.email),
        {
          email: profileToSave.email,
          nickname: normalizedNickname,
          registeredAt: profileToSave.registeredAt,
        },
      ]);
      setUserProfile(profileToSave);
      applyCloudDataPayload(createEmptyCloudDataPayload());
      setRegistrationDraft((current) => ({ ...profileToSave, password: '' }));
      awardRegistrationBadge();
    } catch (error) {
      const message = error?.message || 'Не удалось выполнить действие.';
      if (/already registered|already exists|duplicate/i.test(message)) {
        setRegistrationError('Email или ник уже занят. Попробуйте войти, выбрать другой ник или добавить цифры.');
      } else if (/invalid login credentials/i.test(message)) {
        setRegistrationError('Неверный email или пароль.');
      } else {
        setRegistrationError(translateSupabaseError(message));
      }
    } finally {
      setIsAuthLoading(false);
    }
  };

  const openPasswordResetRequest = () => {
    setAuthMode('login');
    setIsPasswordResetRequestOpen(true);
    setShouldPulseResetEmail(false);
    setRegistrationError('');
    setAuthStatus('');
    setRegistrationDraft((current) => ({ ...current, password: '' }));
  };

  const closePasswordResetRequest = () => {
    setIsPasswordResetRequestOpen(false);
    setShouldPulseResetEmail(false);
    setShouldPulseRecoveryPassword(false);
    setAuthMode('login');
    setRegistrationError('');
    setAuthStatus('');
    setRegistrationDraft((current) => ({ ...current, password: '' }));
  };

  const sendPasswordReset = async (event) => {
    event.preventDefault();
    const email = registrationDraft.email.trim().toLowerCase();

    if (!email) {
      setShouldPulseResetEmail(true);
      setRegistrationError('Введите email, указанный при регистрации.');
      return;
    }

    if (!isValidEmail(email)) {
      setShouldPulseResetEmail(true);
      setRegistrationError('Для восстановления пароля нужно ввести корректный email.');
      return;
    }

    setShouldPulseResetEmail(false);
    setIsAuthLoading(true);
    setRegistrationError('');
    setAuthStatus('');

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getPasswordResetRedirectUrl(),
    });

    setIsAuthLoading(false);

    if (error) {
      setShouldPulseResetEmail(true);
      setRegistrationError(translateSupabaseError(error.message));
      return;
    }

    setAuthStatus('Письмо для смены пароля отправлено. Откройте почту и перейдите по ссылке.');
    setRegistrationDraft((current) => ({ ...current, password: '' }));
  };

  const submitPasswordRecovery = async (event) => {
    event.preventDefault();
    const nextPassword = recoveryPassword.trim();

    if (nextPassword.length < 6 || nextPassword.length > 20) {
      setShouldPulseRecoveryPassword(true);
      setRegistrationError('Новый пароль должен быть от 6 до 20 символов.');
      return;
    }

    setShouldPulseRecoveryPassword(false);
    setIsAuthLoading(true);
    setRegistrationError('');

    const { error } = await supabase.auth.updateUser({ password: nextPassword });

    setIsAuthLoading(false);

    if (error) {
      setShouldPulseRecoveryPassword(true);
      setRegistrationError(translateSupabaseError(error.message));
      return;
    }

    setRecoveryPassword('');
    setIsPasswordRecovery(false);
    setAuthMode('login');
    setAuthStatus('Пароль обновлен. Теперь можно войти с новым паролем.');
    await supabase.auth.signOut();
    setUserProfile(normalizeUserProfile(null));
    setRegistrationDraft((current) => ({ ...current, password: '' }));
  };

  const logoutUser = async () => {
    setIsProfileStatsOpen(false);
    setIsPasswordResetRequestOpen(false);
    setIsPasswordRecovery(false);
    setAuthStatus('');
    setRegistrationError('');

    const { error } = await supabase.auth.signOut();
    if (error) {
      setAuthStatus(`Не удалось выйти: ${translateSupabaseError(error.message)}`);
      return;
    }
    cloudLoadedRef.current = false;

    const emptyProfile = normalizeUserProfile(null);
    setUserProfile(emptyProfile);
    setRegistrationDraft((current) => ({
      ...emptyProfile,
      email: current.email || '',
      password: '',
    }));
    setAuthMode('login');
  };

  const deleteUserAccount = () => {
    if (!userProfile.id) return;
    setDeleteAccountModal({ password: '', error: '', busy: false });
  };

  const confirmDeleteAccount = async () => {
    if (!userProfile.id) return;
    const password = (deleteAccountModal && deleteAccountModal.password) || '';
    if (!password) {
      setDeleteAccountModal((m) => (m ? { ...m, error: 'Введите пароль.' } : m));
      return;
    }
    setDeleteAccountModal((m) => (m ? { ...m, busy: true, error: '' } : m));
    const { error: passwordError } = await supabase.auth.signInWithPassword({
      email: userProfile.email,
      password,
    });
    if (passwordError) {
      setDeleteAccountModal((m) => (m ? { ...m, busy: false, error: 'Пароль неверный.' } : m));
      return;
    }
    const { error } = await supabase.rpc('delete_current_user_account');
    if (error) {
      const message = /function .*delete_current_user_account|could not find|schema cache/i.test(error.message)
        ? 'Удаление пока не настроено на сервере (нужно выполнить SQL-файл supabase-delete-account.sql).'
        : `Не удалось удалить аккаунт: ${translateSupabaseError(error.message)}`;
      setDeleteAccountModal((m) => (m ? { ...m, busy: false, error: message } : m));
      return;
    }
    await supabase.auth.signOut();
    const emptyProfile = normalizeUserProfile(null);
    setUserProfile(emptyProfile);
    setRegistrationDraft({ ...emptyProfile, password: '' });
    applyCloudDataPayload(createEmptyCloudDataPayload());
    setIsProfileStatsOpen(false);
    setAuthMode('register');
    setAuthStatus('');
    setRegistrationError('');
    setDeleteAccountModal(null);
  };

  const blockUserByIdentifier = async (event) => {
    event.preventDefault();
    const identifier = moderationTarget.trim();

    if (userProfile.role !== 'moderator') return;
    if (!identifier) {
      setAuthStatus('Введите email или ник пользователя.');
      return;
    }

    if (identifier.toLowerCase() === userProfile.email.toLowerCase()
      || identifier.toLowerCase() === userProfile.nickname.toLowerCase()) {
      setAuthStatus('Нельзя заблокировать собственный профиль.');
      return;
    }

    const { data, error } = await supabase.rpc('moderator_block_profile', {
      target_identifier: identifier,
    });

    if (error) {
      const message = /moderator_block_profile|could not find|schema cache/i.test(error.message)
        ? 'Блокировка пока не настроена на сервере. Выполните обновленный supabase-moderation.sql в Supabase SQL Editor.'
        : translateSupabaseError(error.message);
      setAuthStatus(message);
      return;
    }

    if (!data) {
      setAuthStatus('Пользователь не найден.');
      return;
    }

    setModerationTarget('');
    setAuthStatus('Пользователь заблокирован.');
  };

  const unblockUserByIdentifier = async () => {
    const identifier = moderationTarget.trim();

    if (userProfile.role !== 'moderator') return;
    if (!identifier) {
      setAuthStatus('Введите email или ник пользователя.');
      return;
    }

    const { data, error } = await supabase.rpc('moderator_unblock_profile', {
      target_identifier: identifier,
    });

    if (error) {
      const message = /moderator_unblock_profile|could not find|schema cache/i.test(error.message)
        ? 'Разблокировка пока не настроена на сервере. Выполните обновленный supabase-moderation.sql в Supabase SQL Editor.'
        : translateSupabaseError(error.message);
      setAuthStatus(message);
      return;
    }

    if (!data) {
      setAuthStatus('Пользователь не найден.');
      return;
    }

    setModerationTarget('');
    setAuthStatus('Пользователь разблокирован.');
  };

  const reloadFromLogo = (event) => {
    event?.preventDefault?.();
    if (isLogoSpinning) return;
    setIsLogoSpinning(true);
    try { sessionStorage.setItem("__skip_splash", "1"); } catch (_) {}
    try {
      localStorage.setItem('norsk_nav_restore_v1', JSON.stringify({
        activeCategory, activeSubcategory, activeMobileSection,
        deskDict, deskAnalyzer, isLearningMode, analyzerText,
      }));
    } catch (_) {}
    setTimeout(() => { window.location.reload(); }, 2000);
  };

  const clearOrdFromUrl = () => {
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.has('ord')) {
        u.searchParams.delete('ord');
        const qs = u.searchParams.toString();
        window.history.replaceState({}, '', u.pathname + (qs ? '?' + qs : '') + u.hash);
      }
    } catch (e) {}
  };
  const closePublicPage = () => { setPublicPage(null); clearOrdFromUrl(); };
  const evikClose = () => setEvik(null);
  const lockAiButtons = () => {
    setAiLock(true);
    if (aiLockTimerRef.current) clearTimeout(aiLockTimerRef.current);
    aiLockTimerRef.current = setTimeout(() => setAiLock(false), 5000);
  };

  const startPhoto = (mode) => {
    photoModeRef.current = mode === 'text' ? 'text' : 'objects';
    setPhotoModePick(false);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
      photoInputRef.current.click();
    }
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!userProfile.id) { setEvik({ stage: 'error', message: 'Войдите, чтобы распознавать фото' }); return; }
    const mode = photoModeRef.current || 'objects';
    setEvik({ stage: 'loading', message: mode === 'text' ? 'Читаю текст с фото…' : 'Распознаю предметы на фото…' });
    lockAiButtons();
    try {
      const dataUrl = await resizePhotoFile(file, 1024, 0.8);
      const headers = await evikHeaders();
      const r = await fetch('/api/ai/photo', {
        method: 'POST',
        headers,
        body: JSON.stringify({ mode, image: dataUrl, user: userProfile.id }),
      });
      const j = await r.json().catch(() => null);
      if (r.status === 429) { setEvik({ stage: 'error', message: 'Дневной лимит распознаваний исчерпан. Попробуйте позже.' }); return; }
      if (!r.ok || !j || !j.ok) { setEvik({ stage: 'error', message: 'Не удалось распознать фото. Попробуйте ещё раз.' }); return; }
      const result = String(j.result || '').trim();
      if (!result) { setEvik({ stage: 'error', message: mode === 'text' ? 'На фото не найден норвежский текст.' : 'На фото не удалось распознать предметы.' }); return; }
      setEvik(null);
      const _now = new Date();
      const _pad = (n) => String(n).padStart(2, '0');
      const _stamp = _pad(_now.getDate()) + '.' + _pad(_now.getMonth() + 1) + '.' + _now.getFullYear() + ' ' + _pad(_now.getHours()) + ':' + _pad(_now.getMinutes());
      const _label = mode === 'text' ? 'Текст с фото' : 'Предметы с фото';
      const _block = '📷 ' + _label + ' · ' + _stamp + '\n' + result;
      setAnalyzerText((prev) => {
        const t = (prev || '').replace(/\s+$/, '');
        return t ? (t + '\n\n──────────────────\n' + _block) : _block;
      });
      openAnalyzerSection();
    } catch (err) {
      setEvik({ stage: 'error', message: 'Не удалось обработать фото.' });
    }
  };

  const addPublicWord = async (wordNorm, wordText, wantPos) => {
    setPubMenuWord(null);
    if (!userProfile.id) { setEvik({ stage: 'error', message: 'Войдите, чтобы добавлять слова в свой словарь' }); return; }
    setEvik({ stage: 'loading', word: wordText, message: 'Добавляю слово в твой словарь…' });
    let data = null, wt = wordText;
    try {
      let q = supabase
        .from('ordskjonnhet_public')
        .select('word_text,data,owner_nick')
        .eq('word_norm', wordNorm)
        .eq('authored', true);
      if (wantPos) q = q.eq('pos', wantPos);
      const { data: rows } = await q.limit(1);
      const row = rows && rows[0];
      data = row && row.data;
      wt = (row && row.word_text) || wordText;
      var onick = (row && row.owner_nick) || 'Evik';
    } catch (e) { data = null; }
    if (!data) { setEvik({ stage: 'error', message: 'Не удалось получить слово из базы' }); return; }
    const pos = String(data.type || '').toLowerCase().trim();
    const posCanon = canonicalCategoryAliases[pos] || null;
    let targetCat = null;
    if (posCanon) {
      for (const cat of Object.keys(db)) {
        const cid = (categoryMeta[getCategoryMetaKey(cat)] || {}).canonicalId || getInferredCanonicalCategoryId(cat);
        if (cid === posCanon) { targetCat = cat; break; }
      }
      if (!targetCat && db[posCanon]) targetCat = posCanon;
    }
    if (!targetCat) { setEvik({ stage: 'error', message: 'Не удалось определить часть речи слова' }); return; }
    const asArr = (x) => (Array.isArray(x) ? x.filter((t) => typeof t === 'string' && t.trim()) : []);
    const word = {
      text: data.text || wt,
      root: typeof data.root === 'string' ? data.root : '',
      rootTranslation: '',
      roots: [],
      capitalized: !!data.capitalized,
      lvl: 'A1',
      type: pos || null,
      forms: null,
      manualForms: null,
      formsSource: null,
      translations: asArr(data.translations),
      synonyms: asArr(data.synonyms),
      antonyms: asArr(data.antonyms),
      adjectives: [],
      gender: data.gender || null,
      story: typeof data.story === 'string' ? data.story : '',
      phrase: typeof data.phrase === 'string' ? data.phrase : '',
      photo: (data.photo && data.photo.dataUrl) ? { dataUrl: data.photo.dataUrl, name: '', type: 'image/png' } : null,
      ord: {
        evik: true, evikSourceNorm: wordNorm,
        ownerNick: (typeof onick !== 'undefined' ? onick : 'Evik'),
        phrase_translation: typeof data.phraseTranslation === 'string' ? data.phraseTranslation : '',
        examples: asArr(data.examples), example_translations: asArr(data.exampleTranslations),
        cognates: asArr(data.cognates), phonetic_similar: asArr(data.phonetic),
        etymology: typeof data.etymology === 'string' ? data.etymology : '',
        photoReady: !!(data.photo && data.photo.dataUrl),
      },
    };
    word.ord.evikSnapshot = evikContentSig(word);
    const existingWords = getWordsFromNode(db[targetCat] || {});
    const normNew = normalizeWordText(word.text);
    if (existingWords.some((w) => normalizeWordText(w.text) === normNew)) {
      setEvik({ stage: 'info', message: 'Слово «' + word.text + '» уже есть в категории «' + targetCat + '».' });
      return;
    }
    setDb((cur) => {
      const next = { ...cur };
      const node = next[targetCat] ? { ...next[targetCat] } : {};
      const words = getWordsFromNode(node);
      node[WORDS_KEY] = [...words, word];
      next[targetCat] = node;
      return syncReciprocalRelations(next);
    });
    setEvik({ stage: 'info', message: 'Слово «' + word.text + '» добавлено в категорию «' + targetCat + '». Откройте эту категорию и перенесите слово в нужную подкатегорию.' });
  };


  const EVIK_OWNER_ID = '30fdabae-32a9-47c7-a550-f94880ae1757';
  const evikPublish = async (data, wordNorm, wordText) => {
    const ownerId = userProfile && userProfile.id;
    if (!ownerId || !wordNorm) return;
    const avaSrc = (userProfile.photo && userProfile.photo.dataUrl) || null;
    let custThumb = (userProfile.photo && userProfile.photo.thumb) || null;
    if (!custThumb && avaSrc) { try { custThumb = await makeAvatarThumb(avaSrc); } catch (e) { custThumb = avaSrc; } }
    const pubRow = {
      owner_id: EVIK_OWNER_ID,
      word_norm: wordNorm,
      word_text: wordText,
      owner_nick: 'Evik',
      pos: ((data && (data.type || data.aiPos)) || 'noun'),
      data: { ...data, customer: { id: ownerId, nick: (userProfile.nickname || ''), avatar: custThumb || null } },
      photo: null,
      completeness: 100,
      authored: true,
      fullness_date: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    try {
      let res = await supabase.from('ordskjonnhet_public').upsert(pubRow, { onConflict: 'owner_id,word_norm,pos' });
      if (res && res.error) {
        await new Promise((r) => window.setTimeout(r, 1500));
        res = await supabase.from('ordskjonnhet_public').upsert(pubRow, { onConflict: 'owner_id,word_norm,pos' });
      }
      if (res && res.error) { notify('Карточка показана, но не сохранилась в общий словарь. Проверь связь.'); }
      else { loadEvikAnalyzerWords(); }
    } catch (e) {
      notify('Карточка показана, но не сохранилась в общий словарь.');
    }
  };

  const evikShowCard = (card) => {
    const data = aiCardToPublicData(card);
    if (!data) { setEvik({ stage: 'error', message: 'Пустой ответ ИИ' }); return; }
    const text = data.text || '';
    const wordNorm = text.trim().toLowerCase();
    data.aiGenerated = true;
    data.aiWord = text;
    data.aiPos = card.type || '';
    data.aiVersions = card.versions || null;
    setEvik(null);
    setPublicPage({ wordNorm, wordText: text, openedAt: Date.now(), seed: { data, owner_nick: 'Evik', fullness_date: new Date().toISOString(), authored: true, completeness: 100 } });
    evikPublish(data, wordNorm, text);
  };


  const evikHeaders = async () => {
    let token = '';
    try {
      const s = await supabase.auth.getSession();
      let sess = s && s.data && s.data.session;
      const now = Math.floor(Date.now() / 1000);
      const needRefresh = !sess || !sess.access_token || (sess.expires_at && sess.expires_at < now + 90);
      if (needRefresh) {
        try { const r = await supabase.auth.refreshSession(); if (r && r.data && r.data.session) sess = r.data.session; } catch (e) {}
      }
      token = (sess && sess.access_token) || '';
    } catch (e) {}
    const h = { 'Content-Type': 'application/json' };
    if (token) h.Authorization = 'Bearer ' + token;
    return h;
  };


  const blobToB64 = (b) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : '');
    };
    r.onerror = () => reject(new Error('read'));
    r.readAsDataURL(b);
  });


  const uploadChunks = async (blob, sid) => {
    const CHUNK = 400 * 1024;
    const total = Math.max(1, Math.ceil(blob.size / CHUNK));
    for (let i = 0; i < total; i += 1) {
      const b64 = await blobToB64(blob.slice(i * CHUNK, (i + 1) * CHUNK));
      const headers = await evikHeaders();
      const r = await fetch('/api/ai/dialog-chunk', { method: 'POST', headers, body: JSON.stringify({ sid, index: i, total, data: b64, user: userProfile.id }) });
      if (!r.ok) {
        let t = '';
        try { t = (await r.text()).slice(0, 80); } catch (_) {}
        throw new Error('chunk HTTP ' + r.status + (t ? ' ' + t : ''));
      }
    }
  };


  const loadVoiceStatus = async () => {
    try {
      const headers = await evikHeaders();
      const res = await fetch('/api/ai/voice-status', { method: 'POST', headers, body: JSON.stringify({ user: userProfile.id }) });
      const j = await res.json().catch(() => null);
      if (j && j.ok) setVoiceStatus({ has: !!j.has, name: j.name || '' });
    } catch (e) {}
  };

  const deleteVoiceSample = async () => {
    try {
      const headers = await evikHeaders();
      await fetch('/api/ai/voice-delete', { method: 'POST', headers, body: JSON.stringify({ user: userProfile.id }) });
      setVoiceStatus({ has: false, name: '' });
      notify('Образец голоса удалён.');
    } catch (e) {}
  };

  const saveVoiceSample = async (blob, secs) => {
    setShowVoiceRecorder(false);
    if (!blob || !blob.size) { notify('Запись пустая — попробуйте ещё раз.'); return; }
    try {
      const sid = 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      await uploadChunks(blob, sid);
      const headers = await evikHeaders();
      const res = await fetch('/api/ai/voice-save', { method: 'POST', headers, body: JSON.stringify({ sid, name: userProfile.nickname || '', secs, user: userProfile.id }) });
      const j = await res.json().catch(() => null);
      if (!res.ok || !j || !j.ok) {
        const e = j && j.error;
        notify(e === 'too_long' ? 'Образец длиннее допустимого.' : 'Не удалось сохранить образец. Попробуйте снова.');
      } else {
        notify('Голос сохранён. Теперь ИИ будет узнавать вас в разборах.');
        loadVoiceStatus();
      }
    } catch (e) {
      const msg = (e && e.message) ? e.message : '';
      notify('Ошибка при сохранении образца: ' + (msg || 'неизвестно') + '.');
    }
  };

  const analyzeDialog = async (blob, secs) => {
    setShowRecorder(false);
    if (!blob) return;
    setDialogBusy(true);
    setDialogResult(null);
    try {
      const sid = 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      if (!blob.size) { notify('Запись пустая — попробуйте записать ещё раз.'); setDialogBusy(false); return; }
      await uploadChunks(blob, sid);
      const headers = await evikHeaders();
      const res = await fetch('/api/ai/dialog-analyze', { method: 'POST', headers, body: JSON.stringify({ sid, secs, user: userProfile.id }) });
      const j = await res.json().catch(() => null);
      if (!res.ok || !j || !j.ok) {
        const e = j && j.error;
        notify(e === 'too_long' ? 'Запись длиннее вашего лимита.' : e === 'audio_too_big' ? 'Запись слишком большая для текущего режима.' : e === 'no_audio' ? 'Запись не получена, попробуйте снова.' : 'Не удалось разобрать запись. Попробуйте ещё раз.');
      } else {
        setDialogResult({ analysis: j.analysis, secs: j.secs, id: j.id });
      }
    } catch (e) {
      try { console.error('[dialog] ошибка:', e); } catch (_) {}
      const msg = (e && e.message) ? e.message : '';
      if (msg.indexOf('401') >= 0 || msg.indexOf('login required') >= 0) {
        notify('Сессия истекла. Закройте и снова откройте вкладку (или войдите заново) и повторите запись.');
      } else {
        notify('Ошибка при отправке записи: ' + (msg || 'неизвестно') + '. Попробуйте снова.');
      }
    }
    setDialogBusy(false);
  };


  const parseDialog = (raw) => {
    const txt = String(raw || '');
    const T = '=== ТРАНСКРИПТ ===';
    const R = '=== РАЗБОР ===';
    const S = '=== СОВЕТЫ ===';
    const grab = (start, ends) => {
      const i = txt.indexOf(start);
      if (i < 0) return '';
      const from = i + start.length;
      let end = txt.length;
      ends.forEach((e) => { const j = txt.indexOf(e, from); if (j >= 0 && j < end) end = j; });
      return txt.slice(from, end).trim();
    };
    const transcript = grab(T, [R, S]);
    const razbor = grab(R, [S]);
    const sovety = grab(S, []);
    if (!transcript && !razbor && !sovety) return { transcript: '', razbor: txt.trim(), sovety: '' };
    return { transcript, razbor, sovety };
  };

  const openTranscriptInAnalyzer = (transcript) => {
    const t = String(transcript || '').slice(0, 200000);
    if (!t) { notify('В разборе нет транскрипта для анализа.'); return; }
    setAnalyzerMaxChars(200000);
    setAnalyzerText(t);
    setDeskAnalyzer(true);
    setActiveMobileSection('analyzer');
    setDialogResult(null);
    setTimeout(() => {
      try {
        const el = document.getElementById('analyzer-section-anchor');
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {}
    }, 80);
  };

  const maybeRefreshAiCard = async (wordNorm) => {
    const uid = userProfile && userProfile.id;
    if (!uid || !wordNorm) return;
    try {
      const { data: rows } = await supabase
        .from('ordskjonnhet_public')
        .select('data,word_text')
        .eq('owner_id', uid)
        .eq('word_norm', wordNorm)
        .limit(1);
      const row = rows && rows[0];
      const d = row && row.data;
      if (!d || !d.aiGenerated) return;
      const hr = await fetch('/api/ai/health');
      const hj = await hr.json().catch(() => null);
      const cur = hj && hj.versions;
      if (!cur) return;
      const have = d.aiVersions || {};
      const imgMissing = !(d.photo && (d.photo.dataUrl || d.photo.thumb)) && !d.imgHealTried;
      let lag = false;
      for (const k in cur) { if ((have[k] || 0) < cur[k]) { lag = true; break; } }
      if (!lag && !imgMissing) return;
      const headers = await evikHeaders();
      const r = await fetch('/api/ai/refresh', {
        method: 'POST',
        headers,
        body: JSON.stringify({ word: d.aiWord || d.text || wordNorm, pos: d.aiPos || d.type || 'unknown', user: uid, have, image: (d.photo && d.photo.dataUrl) || '' }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j || !j.ok || !j.updated || !j.result) return;
      const nd = aiCardToPublicData(j.result);
      if (!nd) return;
      const lagP = {};
      for (const k in cur) lagP[k] = (have[k] || 0) < cur[k];
      const merged = { ...d };
      if (lagP.story) merged.story = nd.story;
      if (lagP.phrases) { merged.phrase = nd.phrase; merged.phraseTranslation = nd.phraseTranslation; merged.examples = nd.examples; merged.exampleTranslations = nd.exampleTranslations; }
      if (lagP.lexicon) { merged.translations = nd.translations; merged.synonyms = nd.synonyms; merged.antonyms = nd.antonyms; merged.root = nd.root; merged.cognates = nd.cognates; merged.phonetic = nd.phonetic; }
      if (lagP.forms) { merged.forms = nd.forms; merged.grad = nd.grad; merged.type = nd.type || merged.type; merged.gender = nd.gender || merged.gender; }
      if (j.imageUpdated) merged.photo = nd.photo;
      else if (imgMissing) merged.imgHealTried = true;
      const nv = {};
      for (const k in cur) {
        if (k === 'image') nv[k] = (j.imageUpdated || !lagP.image) ? cur[k] : (have[k] || 0);
        else nv[k] = lagP[k] ? cur[k] : (have[k] || 0);
      }
      merged.aiGenerated = true;
      merged.aiWord = merged.text || nd.text;
      merged.aiPos = d.aiPos || nd.aiPos || j.result.type || '';
      merged.aiVersions = nv;
      const wt = merged.text || (row && row.word_text) || wordNorm;
      await evikPublish(merged, wordNorm, wt);
      setPublicPage((pp) => (pp && pp.wordNorm === wordNorm ? { wordNorm, wordText: wt, openedAt: Date.now(), seed: { data: merged, owner_nick: 'Evik', fullness_date: new Date().toISOString(), authored: true, completeness: 100 } } : pp));
    } catch (e) {}
  };

  useEffect(() => {
    if (publicPage && publicPage.wordNorm && !publicPage.seed && userProfile.id) {
      maybeRefreshAiCard(publicPage.wordNorm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicPage && publicPage.wordNorm, publicPage && publicPage.openedAt, userProfile.id]);

  useEffect(() => {
    let hasDirty = false;
    forEachWordInDb(db, (w) => {
      if (!hasDirty && w && w.ord && w.ord.evik && w.ord.evikSnapshot && evikContentSig(w) !== w.ord.evikSnapshot) hasDirty = true;
    });
    if (!hasDirty) return;
    let lastName = '';
    const flip = (w) => {
      if (w && w.ord && w.ord.evik && w.ord.evikSnapshot && evikContentSig(w) !== w.ord.evikSnapshot) {
        lastName = w.text || '';
        const ord = { ...w.ord };
        delete ord.evik; delete ord.evikSourceNorm; delete ord.evikSnapshot; delete ord.ownerNick;
        return { ...w, ord };
      }
      return w;
    };
    const walk = (node) => {
      if (Array.isArray(node)) return node.map(flip);
      if (!node || typeof node !== 'object') return node;
      const out = {};
      Object.keys(node).forEach((k) => {
        if (k === WORDS_KEY) out[k] = (node[k] || []).map(flip);
        else if (k === ORDER_KEY) out[k] = node[k];
        else out[k] = walk(node[k]);
      });
      return out;
    };
    const nextDb = {};
    Object.keys(db).forEach((cat) => { nextDb[cat] = walk(db[cat]); });
    setDb(nextDb);
    if (lastName) notify('Слово «' + lastName + '» изменено — теперь это ваша личная копия. Оригинал Эвика остался публичным.');
  }, [db]);


  const recognizeWordForRadio = async (rawWord) => {
    const word = String(rawWord || '').trim();
    if (!word) return { variants: [] };
    const uid = userProfile && userProfile.id;
    if (!uid) throw new Error('Войдите, чтобы создавать песни');
    const headers = await evikHeaders();
    const rr = await fetch('/api/ai/recognize', { method: 'POST', headers, body: JSON.stringify({ word, user: uid }) });
    const rj = await rr.json().catch(() => null);
    if (!rr.ok || !rj || !rj.ok || !rj.result) throw new Error('не удалось распознать слово');
    return { variants: Array.isArray(rj.result.variants) ? rj.result.variants : [], corrected: (rj.result.corrected || '') };
  };

  const createSongFromOrder = async (rawWord, posArg, srcSong, onProgress) => {
    const word = String(rawWord || '').trim();
    if (!word) throw new Error('пустое слово');
    const uid = userProfile && userProfile.id;
    if (!uid) throw new Error('Войдите, чтобы создавать песни');
    const filters = (srcSong && srcSong.filters) || {};
    const full = !!(srcSong && srcSong.full);
    const headers = await evikHeaders();
    const cardWord = word.toLowerCase();
    const pos = posArg || 'noun';
    let cardData = null;
    try {
      const fu = SUPABASE_URL + '/rest/v1/ordskjonnhet_public?select=data&authored=eq.true&word_norm=eq.' + encodeURIComponent(cardWord) + '&pos=eq.' + encodeURIComponent(pos) + '&limit=1';
      const fr = await fetch(fu, { headers: { apikey: SUPABASE_ANON_KEY, Accept: 'application/json' }, cache: 'no-store' });
      if (fr.ok) {
        const frows = await fr.json();
        if (Array.isArray(frows) && frows.length && frows[0].data && Object.keys(frows[0].data).length) cardData = frows[0].data;
      }
    } catch (e) {}
    if (cardData) {
      if (onProgress) onProgress('Слово уже есть в базе…');
    } else {
      if (onProgress) onProgress('Создаём карточку слова…');
      const cr = await fetch('/api/ai/card', { method: 'POST', headers, body: JSON.stringify({ word: cardWord, pos, user: uid }) });
      const cj = await cr.json().catch(() => null);
      if (!cr.ok || !cj || !cj.ok || !cj.result) throw new Error('не удалось создать карточку слова');
      const pubData = aiCardToPublicData(cj.result) || {};
      pubData.aiWord = pubData.text || cardWord;
      pubData.aiPos = cj.result.type || pos;
      pubData.aiVersions = cj.result.versions || null;
      cardData = pubData;
      if (onProgress) onProgress('Публикуем слово…');
      try { await evikPublish(cardData, cardWord, cardData.text || cardWord); } catch (e) {}
    }
    if (onProgress) onProgress('Сочиняем песню в этом стиле (пару минут)…');
    const sr = await fetch('/api/ai/song', { method: 'POST', headers, body: JSON.stringify({ word: cardWord, pos, user: uid, nick: (userProfile.nickname || ''), data: cardData, filters, full, intent: 'public' }) });
    const sj = await sr.json().catch(() => null);
    if (sr.status === 429) throw new Error('Достигнут лимит создания песен на сегодня');
    if (sr.status === 409) throw new Error('Для этого слова песня в этом слоте уже есть');
    if (!sr.ok || !sj || !sj.ok || !sj.url) throw new Error('не удалось создать песню');
    return { url: sj.url, lyrics: sj.lyrics, full: sj.full, word: cardWord, pos };
  };

  const evikAiFetch = async (url, body, timeoutMs) => {
    const ctrl = new AbortController();
    const slowT = setTimeout(() => {
      setEvik((prev) => (prev && prev.stage === 'loading') ? { ...prev, message: 'Эвик подбирает лучший вариант оформления слова…' } : prev);
    }, 7000);
    const killT = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const headers = await evikHeaders();
      return await fetch(url, { method: 'POST', headers, body: JSON.stringify(body), signal: ctrl.signal });
    } finally {
      clearTimeout(slowT);
      clearTimeout(killT);
    }
  };

  const evikFetchCard = async (word, pos) => {
    const uid = userProfile && userProfile.id;
    if (!uid) { setEvik({ stage: 'error', message: 'Войдите, чтобы пользоваться Evik' }); return; }
    setEvik({ stage: 'loading', word, message: 'Evik проверяет словарь…' });
    const existingPub2 = await findExistingPublicWord(String(word || '').toLowerCase(), pos);
    if (existingPub2) { setEvik(null); setPublicPage({ wordNorm: String(word || '').trim().toLowerCase(), wordText: existingPub2.text, wordPos: existingPub2.pos || pos, preferOwnerId: EVIK_OWNER_ID, openedAt: Date.now() }); return; }
    setEvik({ stage: 'loading', word, message: 'Evik работает над твоим запросом… через секунды ты увидишь результат!' });
    try {
      const r = await evikAiFetch('/api/ai/card', { word, pos: pos || 'unknown', user: uid }, 45000);
      const j = await r.json().catch(() => null);
      if (r.status === 429 && j && j.error === 'daily limit') { setEvik({ stage: 'error', message: 'Лимит на сегодня: 10 слов. Завтра снова можно.' }); return; }
      if (j && j.error === 'gemini http') { setEvik({ stage: 'error', message: 'ИИ сейчас перегружен. Попробуйте ещё раз через минуту.', retry: () => evikFetchCard(word, pos) }); return; }
      if (!r.ok || !j || !j.ok || !j.result) { setEvik({ stage: 'error', message: 'Не удалось получить карточку', retry: () => evikFetchCard(word, pos) }); return; }
      evikShowCard(j.result);
    } catch (e) {
      const timedOut = e && e.name === 'AbortError';
      setEvik({ stage: 'error', message: timedOut ? 'Сеть медленная — Evik не успел сделать карточку. Проверьте интернет и попробуйте ещё раз.' : 'Сеть недоступна. Проверьте подключение и попробуйте ещё раз.', retry: () => evikFetchCard(word, pos) });
    }
  };


  const findExistingPublicWord = async (wordNorm, pos) => {
    const wn = String(wordNorm || '').trim().toLowerCase();
    if (!wn) return null;
    try {
      let url = SUPABASE_URL + '/rest/v1/ordskjonnhet_public?select=word_text,data,pos&authored=eq.true&owner_id=eq.' + EVIK_OWNER_ID + '&word_norm=eq.' + encodeURIComponent(wn);
      if (pos) url += '&pos=eq.' + encodeURIComponent(pos);
      url += '&limit=1';
      const r = await fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Accept: 'application/json' }, cache: 'no-store' });
      if (r.ok) {
        const data = await r.json();
        if (Array.isArray(data) && data.length) {
          const dd = data[0].data || {};
          return { text: data[0].word_text || wn, pos: data[0].pos || dd.type || dd.aiPos || null, data: dd };
        }
      }
    } catch (e) {}
    return null;
  };

  const evikStart = async (rawWord) => {
    const word = String(rawWord || '').trim();
    if (!word) return;
    addToSearchHistory(word);
    const uid = userProfile && userProfile.id;
    if (!uid) { setEvik({ stage: 'error', message: 'Войдите, чтобы пользоваться Evik' }); return; }
    setEvik({ stage: 'loading', word, message: 'Evik распознаёт слово…' });
    lockAiButtons();
    try {
      const r = await evikAiFetch('/api/ai/recognize', { word, user: uid }, 22000);
      const j = await r.json().catch(() => null);
      if (!r.ok || !j || !j.ok || !j.result) { setEvik({ stage: 'error', message: 'Evik не смог сейчас распознать слово — возможно, прервалась связь с ИИ. Нажмите «Повторить». Если не поможет, проверьте написание слова.', retry: () => evikStart(rawWord) }); return; }
      const variants = Array.isArray(j.result.variants) ? j.result.variants : [];
      const corrected = (j.result.corrected || '').trim();
      if (variants.length > 1) { setEvik({ stage: 'choose', word, corrected, variants }); return; }
      if (variants.length === 1) { evikFetchCard(variants[0].word, variants[0].pos); return; }
      const pub = await findExistingPublicWord(word.toLowerCase());
      if (pub) { setEvik(null); setPublicPage({ wordNorm: word.toLowerCase(), wordText: pub.text, openedAt: Date.now() }); return; }
      setEvik({ stage: 'choose', word, corrected, variants: [] });
    } catch (e) {
      const timedOut = e && e.name === 'AbortError';
      setEvik({ stage: 'error', message: timedOut ? 'Сеть медленная — Evik не успел распознать слово. Проверьте интернет и попробуйте ещё раз.' : 'Сеть недоступна. Проверьте подключение и попробуйте ещё раз.', retry: () => evikStart(rawWord) });
    }
  };

  const evikForcePos = async (pos) => {
    const base = String((evik && (evik.corrected || evik.word)) || '').trim();
    if (!base) return;
    const uid = userProfile && userProfile.id;
    if (!uid) { setEvik({ stage: 'error', message: 'Войдите, чтобы пользоваться Evik' }); return; }
    setEvik({ stage: 'loading', word: base, message: 'Evik подбирает слово…' });
    try {
      const r = await evikAiFetch('/api/ai/recognize', { word: base, user: uid, force_pos: pos }, 22000);
      const j = await r.json().catch(() => null);
      if (!r.ok || !j || !j.ok || !j.result) { setEvik({ stage: 'error', message: 'Не удалось подобрать слово', retry: () => evikForcePos(pos) }); return; }
      const variants = Array.isArray(j.result.variants) ? j.result.variants : [];
      const corrected = (j.result.corrected || base).trim();
      if (variants.length === 0) { setEvik({ stage: 'choose', word: base, corrected, variants: [], noPos: pos }); return; }
      if (variants.length === 1) { evikFetchCard(variants[0].word, variants[0].pos); return; }
      setEvik({ stage: 'choose', word: base, corrected, variants });
    } catch (e) {
      const timedOut = e && e.name === 'AbortError';
      setEvik({ stage: 'error', message: timedOut ? 'Сеть медленная — Evik не успел ответить. Проверьте интернет и попробуйте ещё раз.' : 'Сеть недоступна. Проверьте подключение и попробуйте ещё раз.', retry: () => evikForcePos(pos) });
    }
  };


  const goToStartPage = (event) => {
    event?.preventDefault?.();
    setIsLearningMode(false);
    setIsConstructorOpen(false);
    setIsMobileMenuOpen(false);
    setActiveMobileSection('dictionary');
    setIsPartsMenuOpen(false);
    setIsProfileStatsOpen(false);
    setIsAddingCategory(false);
    setActiveCategory(null);
    setActiveSubcategory(null);
    setDatabaseSearch('');
    setSearchHighlight(null);
    setIsFinishMenuOpen(false);
    setIsFinishedPhrasesOpen(false);
    setPhraseGamesError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const openDictionarySection = () => {
    setMobileSearchMode(false);
    setIsLearningMode(false);
    setIsConstructorOpen(false);
    setActiveMobileSection('dictionary');
    setIsPartsMenuOpen(false);
    setActiveCategory(null);
    setActiveSubcategory(null);
    closeMobileMenu();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openSearchSection = () => {
    setMobileSearchMode(true);
    setIsLearningMode(false);
    setIsConstructorOpen(false);
    setActiveMobileSection('dictionary');
    setIsPartsMenuOpen(false);
    closeMobileMenu();
    window.requestAnimationFrame(() => {
      const el = document.getElementById('database-search-input');
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
    });
  };

  const openLearningSection = () => {
    setIsPartsMenuOpen(false);
    closeMobileMenu();
    if (isLearningMode) {
      setIsLearningMode(false);
      setActiveMobileSection('none');
      return;
    }
    setIsLearningMode(true);
    setIsConstructorOpen(false);
    setActiveMobileSection('learning');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openConstructorSection = () => {
    setIsConstructorOpen(true);
    setIsComposingPhrase(false);
    setIsLearningMode(false);
    setActiveMobileSection('constructor');
    setIsPartsMenuOpen(false);
    closeMobileMenu();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openWordStoryInAnalyzer = (story) => {
    const text = (story || '').trim();
    if (!text) return;
    setAnalyzerText(text);
    openAnalyzerSection();
  };

  const openAnalyzerSection = () => {
    setIsLearningMode(false);
    setIsConstructorOpen(false);
    setDeskAnalyzer(true);
    setActiveMobileSection('analyzer');
    setIsPartsMenuOpen(false);
    setActiveCategory(null);
    setActiveSubcategory(null);
    closeMobileMenu();
    window.setTimeout(() => {
      document.querySelector('.text-analyzer')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
  };

  const toggleDictionarySection = () => setDeskDict((v) => !v);
  const toggleAnalyzerSection = () => setDeskAnalyzer((v) => !v);
  const mobileToggleDictionary = () => {
    if (activeMobileSection === 'dictionary' && !mobileSearchMode && !isLearningMode && !isConstructorOpen) { closeMobileMenu(); setActiveMobileSection('none'); }
    else { openDictionarySection(); }
  };
  const mobileToggleSearch = () => {
    if (activeMobileSection === 'dictionary' && mobileSearchMode && !isLearningMode && !isConstructorOpen) { setActiveMobileSection('none'); }
    else { openSearchSection(); }
  };
  const mobileToggleAnalyzer = () => {
    if (activeMobileSection === 'analyzer' && !isLearningMode && !isConstructorOpen) { closeMobileMenu(); setActiveMobileSection('none'); setDeskAnalyzer(false); }
    else { openAnalyzerSection(); }
  };

  if (publicPage && publicPage.deepLink) {
    return (
      <OrdskjonnhetPublic key={(publicPage.wordNorm || "") + "-" + (publicPage.openedAt || 0)} supabase={supabase} userProfile={userProfile} onEvik={(w) => evikStart(w)} uiLang={uiLang} onRequestLogin={() => { closePublicPage(); setAuthMode("login"); setShowAuthScreen(true); }} guestPromptText={t("notFound")} loginLabel={t("login")} evikBusy={aiBtnBusy} onSongSaved={saveSongToMyDictionary} openedAt={publicPage.openedAt || 0} wordNorm={publicPage.wordNorm} wordText={publicPage.wordText} seed={publicPage.seed} preferOwnerId={publicPage.preferOwnerId} wordPos={publicPage.wordPos} onClose={closePublicPage} onOpenWord={(t) => setPublicPage({ wordNorm: String(t || '').trim().toLowerCase(), wordText: t, openedAt: Date.now(), deepLink: true })} />
    );
  }

  if (isPasswordRecovery) {
    return (
      <main className="registration-shell">
        <form className="registration-card" onSubmit={submitPasswordRecovery} noValidate>
          <div className="registration-brand">
            <p><span className="slogan-w1">Enkelt</span> · <span className="slogan-w2">Forståelig</span> · <span className="slogan-w3">Pålitelig</span></p>
            <h1>Norsk Pro</h1>
          </div>

          <div className="registration-avatar">
            <img className="registration-logo-spin" src="/norsk-logo.png" alt="Norsk Pro logo" />
          </div>

          <label>
            <span>Новый пароль</span>
            <input
              type="password"
              value={recoveryPassword}
              onChange={(event) => {
                setShouldPulseRecoveryPassword(false);
                setRecoveryPassword(sanitizeRegistrationPassword(event.target.value));
              }}
              className={shouldPulseRecoveryPassword ? 'attention-pulse' : ''}
              placeholder="••••••••"
              minLength={6}
              maxLength={20}
              autoComplete="new-password"
            />
          </label>

          {registrationError && <p className="registration-error">{registrationError}</p>}
          {authStatus && <p className="registration-status">{authStatus}</p>}

          <button type="submit" disabled={isAuthLoading}>
            {isAuthLoading ? 'Подождите...' : 'Сохранить пароль'}
          </button>
          <p className="registration-credit">Design by City108</p>
        </form>
      </main>
    );
  }

  if (isPasswordResetRequestOpen) {
    return (
      <main className="registration-shell">
        <form className="registration-card" onSubmit={sendPasswordReset} noValidate>
          <div className="registration-brand">
            <p><span className="slogan-w1">Enkelt</span> · <span className="slogan-w2">Forståelig</span> · <span className="slogan-w3">Pålitelig</span></p>
            <h1>Norsk Pro</h1>
          </div>

          <div className="registration-avatar">
            <img className="registration-logo-spin" src="/norsk-logo.png" alt="Norsk Pro logo" />
          </div>

          <label>
            <span>Email</span>
            <input
              type="email"
              value={registrationDraft.email}
              onChange={(event) => {
                setShouldPulseResetEmail(false);
                setRegistrationDraft((current) => ({
                  ...current,
                  email: sanitizeRegistrationEmail(event.target.value),
                }));
              }}
              className={shouldPulseResetEmail ? 'attention-pulse' : ''}
              placeholder="name@example.com"
              maxLength={80}
              autoComplete="username"
              aria-invalid={shouldPulseResetEmail}
            />
          </label>

          {registrationError && <p className="registration-error">{registrationError}</p>}
          {authStatus && <p className="registration-status">{authStatus}</p>}

          <button type="submit" disabled={isAuthLoading}>
            {isAuthLoading ? 'Подождите...' : 'Сменить пароль'}
          </button>
          <button
            className="registration-link-button"
            type="button"
            onClick={closePasswordResetRequest}
          >
            Вернуться ко входу
          </button>
          <p className="registration-credit">Design by City108</p>
        </form>
      </main>
    );
  }

  if (publicPage && (!userProfile.email || !userProfile.nickname)) {
    return (
      <OrdskjonnhetPublic key={(publicPage.wordNorm || "") + "-" + (publicPage.openedAt || 0)} supabase={supabase} userProfile={userProfile} onEvik={(w) => evikStart(w)} uiLang={uiLang} onRequestLogin={() => { closePublicPage(); setAuthMode("login"); setShowAuthScreen(true); }} guestPromptText={t("notFound")} loginLabel={t("login")} evikBusy={aiBtnBusy} onSongSaved={saveSongToMyDictionary} openedAt={publicPage.openedAt || 0} wordNorm={publicPage.wordNorm} wordText={publicPage.wordText} seed={publicPage.seed} preferOwnerId={publicPage.preferOwnerId} wordPos={publicPage.wordPos} onClose={closePublicPage} onOpenWord={(t) => setPublicPage({ wordNorm: String(t || '').trim().toLowerCase(), wordText: t, openedAt: Date.now() })} />
    );
  }

  if (!userProfile.email || !userProfile.nickname) {
    if (!showAuthScreen) {
      return (
        <main className="registration-shell">
          <div className="guest-home">
            <div className="app-brand">
              <img className="app-brand-logo" src="/norsk-logo.png" alt="Norsk Pro logo" />
              <div className="app-brand-copy">
                <h1>Norsk Pro</h1>
                <p><span className="slogan-w1">Enkelt</span> · <span className="slogan-w2">Forståelig</span> · <span className="slogan-w3">Pålitelig</span></p>
              </div>
            </div>
            <p className="guest-sub">{t('guestTagline')}</p>
            <div className={'guest-lang-row' + (!langChosen ? ' guest-lang-attention' : '')}>
              <span className="guest-lang-label">{t('nativeLangLabel')}</span>
              {renderLangPicker('guest-lang-picker')}
            </div>
            <input className="guest-search" type="text" value={guestQuery} onChange={(e) => setGuestQuery(e.target.value)} placeholder={t('guestSearchPh')} />
            <div className="guest-results">
              {guestSearching && <p className="guest-info">{t('searching')}</p>}
              {!guestSearching && guestQuery.trim() && !guestHasExact && !/[\u0400-\u04FF]/.test(guestQuery) && <p className="guest-info">{t('notFound').replace('{word}', guestQuery.trim())}</p>}
              {guestGrouped.map((r) => (
                <div key={r.word_norm} className="guest-result">
                  <span className="guest-result-word guest-result-wbtn" role="button" onClick={() => setPublicPage({ wordNorm: r.word_norm, wordText: r.word_text, openedAt: Date.now(), preferOwnerId: (r.authors[0] && r.authors[0].owner_id) })}>{r.word_text}</span>
                  <span className="pub-authors guest-authors">
                    {r.authors.map((a, ai) => {
                      const an = String(a.owner_nick || '?').trim();
                      const aEvik = an.toLowerCase() === 'evik';
                      return (
                        <span key={(a.owner_id || an) + '-' + ai} role="button" className={'pub-author' + (aEvik ? ' evik' : '')} title={'Открыть версию: ' + an} onClick={() => setPublicPage({ wordNorm: r.word_norm, wordText: a.word_text || r.word_text, wordPos: r.pos, openedAt: Date.now(), preferOwnerId: a.owner_id })}>
                          {aEvik ? <img className="pub-author-ava" src="/evik-avatar.jpg" alt="" /> : (a.avatar ? <img className="pub-author-ava" src={a.avatar} alt="" /> : <span className="pub-author-ava pub-author-initial">{an.charAt(0).toUpperCase()}</span>)}
                          {an}
                        </span>
                      );
                    })}
                  </span>
                </div>
              ))}
            </div>
            <button type="button" className="guest-login-btn" onClick={() => { setAuthMode('login'); setShowAuthScreen(true); }}>{t('login')}</button>
          </div>
        </main>
      );
    }

    return (
      <main className="registration-shell">
        <form className="registration-card" onSubmit={submitAuth} noValidate>

          {!langChosen && (
          <div className="reg-lang-choose">
            <span className="reg-lang-label">{t('langLabel')}</span>
            {renderLangPicker()}
          </div>
          )}

          <div className="registration-brand">
            <p><span className="slogan-w1">Enkelt</span> · <span className="slogan-w2">Forståelig</span> · <span className="slogan-w3">Pålitelig</span></p>
            <h1>Norsk Pro</h1>
          </div>

          <div className="registration-avatar">
            <img className="registration-logo-spin" src="/norsk-logo.png" alt="Norsk Pro logo" />
          </div>

          <label className={focusedAuthField === 'email' ? 'lesson-focus' : ''}>
            <span>Email</span>
            <input
              type="email"
              value={registrationDraft.email}
              onFocus={() => setFocusedAuthField('email')} onBlur={() => setFocusedAuthField(null)}
              onChange={(event) => setRegistrationDraft((current) => ({
                ...current,
                email: sanitizeRegistrationEmail(event.target.value),
              }))}
              placeholder="name@example.com"
              maxLength={80}
              autoComplete="username"
              required
            />
            {authMode === 'register' && focusedAuthField === 'email' && (
              <small className="lesson-hint">{t('hintEmail')}</small>
            )}
          </label>

          {authMode === 'register' && (
            <label className={focusedAuthField === 'nick' ? 'lesson-focus' : ''}>
              <span>{t('nick')}</span>
              <input
                type="text"
                value={registrationDraft.nickname}
                onFocus={() => setFocusedAuthField('nick')} onBlur={() => setFocusedAuthField(null)}
                onChange={(event) => setRegistrationDraft((current) => ({
                  ...current,
                  nickname: sanitizeRegistrationNickname(event.target.value),
                }))}
                placeholder={t('nickPh')}
                minLength={2}
                maxLength={32}
                pattern="[\p{L}\p{N}_ \-]{2,32}"
                required
              />
              {focusedAuthField === 'nick' && (
                <small className="lesson-hint">{t('hintNick')}</small>
              )}
            </label>
          )}

          <label className={focusedAuthField === 'pass' ? 'lesson-focus' : ''}>
            <span>{t('password')}</span>
            <div className="password-field-wrap">
            <input
              type={showPassword ? 'text' : 'password'}
              value={registrationDraft.password || ''}
              onFocus={() => setFocusedAuthField('pass')} onBlur={() => setFocusedAuthField(null)}
              onChange={(event) => setRegistrationDraft((current) => ({
                ...current,
                password: sanitizeRegistrationPassword(event.target.value),
              }))}
              placeholder={authMode === 'login' ? '••••••••' : t('passPh')}
              minLength={6}
              maxLength={20}
              autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
              required
            />
            <button type="button" className="password-eye-btn" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'} tabIndex={-1}>
              {showPassword ? (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
            </div>
            {authMode === 'register' && focusedAuthField === 'pass' && (
              <small className="lesson-hint">{t('hintPass')}</small>
            )}
          </label>
          {authMode === 'login' && (
            <button
              className="forgot-password-link"
              type="button"
              onClick={openPasswordResetRequest}
              disabled={isAuthLoading}
            >
              {t('forgot')}
            </button>
          )}

          {registrationError && <p className="registration-error">{registrationError}</p>}
          {authStatus && <p className="registration-status">{authStatus}</p>}

          <button type="submit" disabled={isAuthLoading}>
            {isAuthLoading ? t('wait') : authMode === 'login' ? t('login') : t('register')}
          </button>
          <button
            className="registration-link-button"
            type="button"
            onClick={() => {
              setAuthMode((mode) => (mode === 'register' ? 'login' : 'register'));
              setRegistrationError('');
              setAuthStatus('');
            }}
          >
            {authMode === 'register' ? t('haveAcc') : t('createAcc')}
          </button>
          <button type="button" className="registration-switch" onClick={() => setShowAuthScreen(false)}>{t('backHome')}</button>
          <p className="registration-credit">Design by City108</p>
        </form>
      </main>
    );
  };

  const isDictionarySectionActive = activeMobileSection === 'dictionary' && !isLearningMode && !isConstructorOpen;
  const isAnalyzerSectionActive = activeMobileSection === 'analyzer' && !isLearningMode && !isConstructorOpen;

  return (
    <main className={"app-shell" + (isRadioOpen ? " radio-active" : "")}>
      {activeLesson === "overview" && (
        <LessonOverlay
          steps={OVERVIEW_STEPS}
          onClose={() => setActiveLesson(null)}
          onComplete={async () => {
            setActiveLesson(null);
            setIsProfileStatsOpen(false);
            setForceOpenSection(null);
            try {
              const { data, error } = await supabase.rpc('award_badge', { badge_key: 'lesson_overview' });
              if (error) return;
              if (data === true) {
                setLastBadgeKey('lesson_overview');
                setShowElephantBadge(true);
                setUserProfile((current) => {
                  const list = Array.isArray(current?.badges) ? current.badges : [];
                  if (list.includes('lesson_overview')) return current;
                  return { ...current, badges: [...list, 'lesson_overview'] };
                });
              }
            } catch (_) {}
          }}
          onAutoOpen={(action) => {
            if (action === 'popover') {
              setIsProfileStatsOpen(true);
              setForceOpenSection(null);
            } else if (action && action.startsWith('section:')) {
              setIsProfileStatsOpen(true);
              setForceOpenSection(action.slice(8));
            }
          }}
        />
      )}
      {activeLesson === 'parts_of_speech' && (
        <LessonVideo
          videoId="4OwTmc2kuFc"
          onClose={() => setActiveLesson(null)}
          onEnd={async () => {
            setActiveLesson(null);
            try {
              const { data, error } = await supabase.rpc('award_badge', { badge_key: 'lesson_parts_of_speech' });
              if (error) return;
              if (data === true) {
                setLastBadgeKey('lesson_parts_of_speech');
                setShowElephantBadge(true);
                setUserProfile((current) => {
                  const list = Array.isArray(current?.badges) ? current.badges : [];
                  if (list.includes('lesson_parts_of_speech')) return current;
                  return { ...current, badges: [...list, 'lesson_parts_of_speech'] };
                });
              }
            } catch (_) {}
          }}
        />
      )}
      {activeLesson === 'dictionary_work' && (
        <LessonVideo
          videoId="XFrpWuRV6WE"
          onClose={() => setActiveLesson(null)}
          onEnd={async () => {
            setActiveLesson(null);
            try {
              const { data, error } = await supabase.rpc('award_badge', { badge_key: 'lesson_dictionary_work' });
              if (error) return;
              if (data === true) {
                setLastBadgeKey('lesson_dictionary_work');
                setShowElephantBadge(true);
                setUserProfile((current) => {
                  const list = Array.isArray(current?.badges) ? current.badges : [];
                  if (list.includes('lesson_dictionary_work')) return current;
                  return { ...current, badges: [...list, 'lesson_dictionary_work'] };
                });
              }
            } catch (_) {}
          }}
        />
      )}
      {activeLesson === 'how_to_learn' && (
        <LessonVideo
          videoId="PQC0NIZvA_A"
          onClose={() => setActiveLesson(null)}
          onEnd={async () => {
            setActiveLesson(null);
            try {
              const { data, error } = await supabase.rpc('award_badge', { badge_key: 'lesson_how_to_learn' });
              if (error) return;
              if (data === true) {
                setLastBadgeKey('lesson_how_to_learn');
                setShowElephantBadge(true);
                setUserProfile((current) => {
                  const list = Array.isArray(current?.badges) ? current.badges : [];
                  if (list.includes('lesson_how_to_learn')) return current;
                  return { ...current, badges: [...list, 'lesson_how_to_learn'] };
                });
              }
            } catch (_) {}
          }}
        />
      )}
      {activeLesson === 'innovations' && (
        <LessonVideo
          videoId="o25uMpeQamI"
          onClose={() => setActiveLesson(null)}
          onEnd={async () => {
            setActiveLesson(null);
            try {
              const { data, error } = await supabase.rpc('award_badge', { badge_key: 'lesson_innovations' });
              if (error) return;
              if (data === true) {
                setLastBadgeKey('lesson_innovations');
                setShowElephantBadge(true);
                setUserProfile((current) => {
                  const list = Array.isArray(current?.badges) ? current.badges : [];
                  if (list.includes('lesson_innovations')) return current;
                  return { ...current, badges: [...list, 'lesson_innovations'] };
                });
              }
            } catch (_) {}
          }}
        />
      )}
      {activeLesson === 'public_word' && (
        <LessonVideo
          videoId="7xv_gcBZ07A"
          downloadUrl="/downloads/promt-publichnoe-slovo.txt"
          onClose={() => setActiveLesson(null)}
          onEnd={async () => {
            try {
              const { data, error } = await supabase.rpc('award_badge', { badge_key: 'lesson_public_word' });
              if (error) return;
              if (data === true) {
                setLastBadgeKey('lesson_public_word');
                setShowElephantBadge(true);
                setUserProfile((current) => {
                  const list = Array.isArray(current?.badges) ? current.badges : [];
                  if (list.includes('lesson_public_word')) return current;
                  return { ...current, badges: [...list, 'lesson_public_word'] };
                });
              }
            } catch (_) {}
          }}
        />
      )}
      {isLogoSpinning && <div className="reload-overlay" aria-hidden="true" />}
      {showElephantBadge && (
        <div className="elephant-badge-overlay" onClick={() => setShowElephantBadge(false)}>
          <div className="elephant-badge-card" onClick={(e) => e.stopPropagation()}>
            <div className="elephant-badge-emoji"><ElephantBadgeIcon size={88} pink={lastBadgeKey === 'pink_elephant'} /></div>
            <h2>{lastBadgeKey === 'pink_elephant' ? 'Спасибо!' : 'Готово!'}</h2>
            {lastBadgeKey === 'lesson_overview' ? (
              <>
                <p>Урок пройден. Слоник твой.</p>
                <p className="elephant-badge-sub">Молодец! Этот слоник останется у тебя в разделе «Обучение» — как след того, что ты прошёл ознакомительную экскурсию по опциям своего профиля.</p>
              </>
            ) : lastBadgeKey === 'lesson_parts_of_speech' ? (
              <>
                <p>Молодец! Урок пройден. Слоник твой.</p>
                <p className="elephant-badge-sub">Ты посмотрел урок про части речи — теперь ты понимаешь как работают контекстные меню категорий частей речи в Norsk Pro.</p>
              </>
            ) : lastBadgeKey === 'lesson_dictionary_work' ? (
              <>
                <p>Ты просмотрел этот длинный урок, я горжусь тобой!</p>
                <p className="elephant-badge-sub">Слоник теперь твой! Теперь ты знаешь как добавлять и оформлять слова в своём словарике, и я надеюсь ты займёшься этим в ближайшее время. Жду тебя в следующем уроке!</p>
              </>
            ) : lastBadgeKey === 'lesson_how_to_learn' ? (
              <>
                <p>Мне нравится твое усердие в изучении программы Norsk Pro!</p>
                <p className="elephant-badge-sub">Получи своего слоника и до встречи на следующем уроке.</p>
              </>
            ) : lastBadgeKey === 'lesson_innovations' ? (
              <>
                <p>Отлично! Урок «Нововведения» пройден. Слоник твой.</p>
                <p className="elephant-badge-sub">Теперь ты знаешь о новых опциях Norsk Pro. Спасибо, что растёшь вместе с программой — впереди ещё больше нового!</p>
              </>
            ) : lastBadgeKey === 'lesson_public_word' ? (
              <>
                <p>Поздравляю! Вы уже много знаете о Norsk Pro и скоро станете экспертом в этой программе.</p>
                <p className="elephant-badge-sub">Скачайте промт по ссылке под этим уроком и пользуйтесь им. До скорой встречи!</p>
              </>
            ) : lastBadgeKey === 'pink_elephant' ? (
              <>
                <p>Спасибо за участие в разработке программы!</p>
                <p className="elephant-badge-sub">Возьми розового слоника, он тебе пригодится в дальнейшем!</p>
              </>
            ) : (
              <>
                <p>Ты получил своего первого слоника.</p>
                <p className="elephant-badge-sub">Это — маленький след твоего вклада в Norsk Pro. Идём дальше — впереди ещё много нового.</p>
              </>
            )}
            <button type="button" onClick={() => setShowElephantBadge(false)}>Спасибо</button>
          </div>
        </div>
      )}
      {subcategoryDeleteConfirm && (
        <div className="subcat-delete-overlay" onClick={() => setSubcategoryDeleteConfirm(null)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Удалить подкатегорию «{subcategoryDeleteConfirm.leafName}»?</p>
            <p className="subcat-delete-sub">Это действие нельзя отменить. Все слова и вложенные подкатегории внутри будут утеряны.</p>
            <div className="subcat-delete-actions">
              <button type="button" className="subcat-delete-cancel" onClick={() => setSubcategoryDeleteConfirm(null)}>Отмена</button>
              <button type="button" className="subcat-delete-confirm" onClick={confirmDeleteSubcategory}>Удалить</button>
            </div>
          </div>
        </div>
      )}
      {deleteAccountModal && (
        <div className="subcat-delete-overlay" onClick={() => { if (!deleteAccountModal.busy) setDeleteAccountModal(null); }}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="subcat-delete-title">Удалить аккаунт?</h3>
            <p className="subcat-delete-sub">Будут удалены профиль, база слов, тексты и вход через этот email. Это действие нельзя отменить. Введите пароль для подтверждения.</p>
            <input className="subcat-row-input" type="password" autoFocus value={deleteAccountModal.password} placeholder="Пароль от аккаунта" onChange={(e) => setDeleteAccountModal((m) => (m ? { ...m, password: e.target.value } : m))} />
            {deleteAccountModal.error && <p className="subcat-row-error">{deleteAccountModal.error}</p>}
            <div className="subcat-delete-actions">
              <button type="button" className="subcat-delete-cancel" disabled={deleteAccountModal.busy} onClick={() => setDeleteAccountModal(null)}>Отмена</button>
              <button type="button" className="subcat-delete-confirm" disabled={deleteAccountModal.busy} onClick={confirmDeleteAccount}>{deleteAccountModal.busy ? 'Удаляю…' : 'Удалить'}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDialog && (
        <div className="subcat-delete-overlay" onClick={() => resolveConfirm(confirmDialog.buttons ? null : false)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="subcat-delete-title">{confirmDialog.title || (confirmDialog.alert ? 'Внимание' : 'Подтвердите удаление')}</h3>
            <p className="subcat-delete-sub">{confirmDialog.message}</p>
            <div className="subcat-delete-actions" style={{ flexWrap: 'wrap' }}>
              {!confirmDialog.alert && (
                <button type="button" className="subcat-delete-cancel" onClick={() => resolveConfirm(confirmDialog.buttons ? null : false)}>{confirmDialog.cancelLabel || 'Отмена'}</button>
              )}
              {confirmDialog.buttons
                ? confirmDialog.buttons.map((b) => (
                    <button key={b.value} type="button" className={b.tone === 'danger' ? 'subcat-delete-confirm' : 'subcat-row-confirm'} onClick={() => resolveConfirm(b.value)}>{b.label}</button>
                  ))
                : (
                  <button type="button" className={confirmDialog.tone === 'normal' || confirmDialog.alert ? 'subcat-row-confirm' : 'subcat-delete-confirm'} onClick={() => resolveConfirm(true)}>{confirmDialog.confirmLabel || (confirmDialog.alert ? 'OK' : 'Удалить')}</button>
                )}
            </div>
          </div>
        </div>
      )}

      {renameRowModal && (
        <div className="subcat-delete-overlay" onClick={() => setRenameRowModal(null)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Переименовать рядок</p>
            <p className="subcat-delete-sub">Введи новое название рядка. Подкатегории внутри останутся на месте.</p>
            <form onSubmit={(e) => { e.preventDefault(); const name = (renameRowModal.draft || "").trim(); if (!name) return; if (name === renameRowModal.oldName) { setRenameRowModal(null); return; } const meta = getSubcategoryRowsMeta(activeCategory, renameRowModal.subcategoryPath); if (meta.rows.includes(name)) { setRenameRowModal((mm) => mm ? { ...mm, error: 'Рядок с таким названием уже есть.' } : mm); return; } renameSubcategoryRow(renameRowModal.subcategoryPath, renameRowModal.oldName, name); setRenameRowModal(null); }}>
              <input className="subcat-row-input" type="text" autoFocus value={renameRowModal.draft} onChange={(e) => setRenameRowModal((mm) => mm ? { ...mm, draft: e.target.value, error: "" } : mm)} placeholder="Название рядка" />
              {renameRowModal.error && <p className="subcat-delete-sub" style={{ color: '#dc2626' }}>{renameRowModal.error}</p>}
              <div className="subcat-delete-actions">
                <button type="button" className="subcat-delete-cancel" onClick={() => setRenameRowModal(null)}>Отмена</button>
                <button type="submit" className="subcat-row-confirm">Сохранить</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {ordDraft && (() => {
        const ordNode = activeCategory ? getNodeAtPath(db[activeCategory], activeSubcategory) : null;
        const liveWordRaw = (ordNode ? getWordsFromNode(ordNode)[ordDraft.wordIndex] : null) || ordDraft.word;
        const liveNorm = String(liveWordRaw.text || '').trim().toLowerCase();
        const liveThumb = (!(liveWordRaw.photo && liveWordRaw.photo.dataUrl) && liveNorm && ownPublicThumbs) ? ownPublicThumbs[liveNorm] : null;
        const liveWord = liveThumb ? { ...liveWordRaw, photo: { dataUrl: liveThumb, name: (liveWordRaw.photo && liveWordRaw.photo.name) || '', type: 'image/jpeg', position: (liveWordRaw.photo && liveWordRaw.photo.position) } } : liveWordRaw;
        return (
          <OrdskjonnhetDraft word={liveWord} canPublish={['paid', 'admin'].includes(userProfile?.role)} categoryColor={categoryColors[activeCategory] || null} onClose={() => setOrdDraft(null)} onSave={(json, mode) => applyOrdData(ordDraft.wordIndex, json, mode)} onAuthor={(a, payload) => setOrdAuthorship(ordDraft.wordIndex, a, payload)} onPhoto={(photo) => setOrdPhoto(ordDraft.wordIndex, photo)} wordSet={wordTextSet} onOpenWord={openWordByText} onViewPublic={(sp) => setPublicPage({ wordNorm: String(liveWord.text || "").trim().toLowerCase(), wordText: liveWord.text, openedAt: Date.now(), seed: sp ? (() => { const data = sp.data; if (data && data.photo && sp.fullPhoto) data.photo.thumb = sp.fullPhoto; return { data, owner_nick: userProfile.nickname || null, fullness_date: (liveWord.ord && liveWord.ord.fullnessDate) || new Date().toISOString(), authored: true, completeness: 100, _fullPhoto: sp.fullPhoto || null }; })() : null })} />
        );
      })()}
      {publicPage && (
        <OrdskjonnhetPublic key={(publicPage.wordNorm || "") + "-" + (publicPage.openedAt || 0)} supabase={supabase} userProfile={userProfile} onEvik={(w) => evikStart(w)} uiLang={uiLang} onRequestLogin={() => { closePublicPage(); setAuthMode("login"); setShowAuthScreen(true); }} guestPromptText={t("notFound")} loginLabel={t("login")} evikBusy={aiBtnBusy} onSongSaved={saveSongToMyDictionary} openedAt={publicPage.openedAt || 0} wordNorm={publicPage.wordNorm} wordText={publicPage.wordText} seed={publicPage.seed} preferOwnerId={publicPage.preferOwnerId} wordPos={publicPage.wordPos} onClose={closePublicPage} onOpenWord={(t) => setPublicPage({ wordNorm: String(t || '').trim().toLowerCase(), wordText: t, openedAt: Date.now() })} />
      )}
      {editWordModal && (
        <div className="subcat-delete-overlay" onClick={() => setEditWordModal(null)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Изменить слово</p>
            <p className="subcat-delete-sub">Введи новое написание слова. Формы будут пересчитаны под текущую категорию.</p>
            <form onSubmit={(e) => { e.preventDefault(); const t = (editWordModal.draft || '').trim(); if (!t) return; editWordText(editWordModal.wordIndex, t); setEditWordModal(null); }}>
              <input className="subcat-row-input" type="text" autoFocus value={editWordModal.draft} onChange={(e) => setEditWordModal((m) => m ? { ...m, draft: e.target.value } : m)} placeholder="Слово" />
              <div className="subcat-delete-actions">
                <button type="button" className="subcat-delete-cancel" onClick={() => setEditWordModal(null)}>Отмена</button>
                <button type="submit" className="subcat-row-confirm">Сохранить</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {storyModal && (
        <div className="subcat-delete-overlay" onClick={() => setStoryModal(null)}>
          <div className="subcat-delete-card story-modal-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Описание слова (история / пример)</p>
            <p className="subcat-delete-sub">Короткий рассказ или пример со словом. До 801 символа. Текст появится на странице слова, а его предложения смогут служить примерами для других слов.</p>
            <form onSubmit={(e) => { e.preventDefault(); setWordStory(storyModal.wordIndex, storyModal.draft); setStoryModal(null); }}>
              <textarea className="story-textarea" autoFocus maxLength={801} value={storyModal.draft} onChange={(e) => setStoryModal((m) => m ? { ...m, draft: e.target.value } : m)} placeholder="Например: короткая история или пример употребления слова..." />
              <p className="story-counter">{(storyModal.draft || '').length} / 801</p>
              <div className="subcat-delete-actions">
                {storyModal.had && (
                  <button type="button" className="subcat-delete-cancel" onClick={() => { setWordStory(storyModal.wordIndex, ''); setStoryModal(null); }}>Убрать текст</button>
                )}
                <button type="button" className="subcat-delete-cancel" onClick={() => setStoryModal(null)}>Отмена</button>
                <button type="submit" className="subcat-row-confirm">Сохранить</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {wordLinkModal && (
        <div className="subcat-delete-overlay" onClick={() => setWordLinkModal(null)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Ссылка на YouTube или TikTok</p>
            <p className="subcat-delete-sub">Вставь ссылку на YouTube или TikTok. Слово станет кликабельным и откроет её в новой вкладке.</p>
            <form onSubmit={(e) => { e.preventDefault(); const res = normalizeYouTubeLink(wordLinkModal.draft); if (!res.ok) { setWordLinkModal((m) => m ? { ...m, error: 'Пока поддерживаются только ссылки на YouTube или TikTok.' } : m); return; } setWordLink(wordLinkModal.wordIndex, res.url); setWordLinkModal(null); }}>
              <input className="subcat-row-input" type="text" autoFocus value={wordLinkModal.draft} onChange={(e) => setWordLinkModal((m) => m ? { ...m, draft: e.target.value, error: '' } : m)} placeholder="https://www.youtube.com/@channel или https://www.tiktok.com/@user" />
              {wordLinkModal.error && <p className="subcat-delete-sub" style={{ color: '#dc2626' }}>{wordLinkModal.error}</p>}
              <div className="subcat-delete-actions">
                {wordLinkModal.hadLink && (
                  <button type="button" className="subcat-delete-cancel" onClick={() => { setWordLink(wordLinkModal.wordIndex, ''); setWordLinkModal(null); }}>Убрать ссылку</button>
                )}
                <button type="button" className="subcat-delete-cancel" onClick={() => setWordLinkModal(null)}>Отмена</button>
                <button type="submit" className="subcat-row-confirm">Сохранить</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {addRowModal && (
        <div className="subcat-delete-overlay" onClick={() => setAddRowModal(null)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Новый рядок</p>
            <p className="subcat-delete-sub">Введи название рядка. Так ты сможешь навести порядок в подкатегориях.</p>
            <form onSubmit={(e) => { e.preventDefault(); const name = (addRowModal.draft || "").trim(); if (!name) return; addRowToSubcategory(addRowModal.subcategoryPath, name); setAddRowModal(null); }}>
              <input className="subcat-row-input" type="text" autoFocus value={addRowModal.draft} onChange={(e) => setAddRowModal((m) => m ? { ...m, draft: e.target.value } : m)} placeholder="Название рядка" />
              <div className="subcat-delete-actions">
                <button type="button" className="subcat-delete-cancel" onClick={() => setAddRowModal(null)}>Отмена</button>
                <button type="submit" className="subcat-row-confirm">Создать</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteRowModal && (
        <div className="subcat-delete-overlay" onClick={() => setDeleteRowModal(null)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Удалить рядок</p>
            <p className="subcat-delete-sub">Выбери рядок для удаления. Удалить можно только пустой рядок.</p>
            {(() => {
              const meta = getSubcategoryRowsMeta(activeCategory, deleteRowModal.subcategoryPath);
              const rows = meta.rows;
              if (rows.length === 0) {
                return <p className="subcat-row-empty">В этой подкатегории нет рядков.</p>;
              }
              return (
                <select className="subcat-row-select" value={deleteRowModal.selected} onChange={(e) => setDeleteRowModal((m) => m ? { ...m, selected: e.target.value } : m)}>
                  <option value="">— выбери рядок —</option>
                  {rows.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              );
            })()}
            {deleteRowModal.error && <p className="subcat-row-error">{deleteRowModal.error}</p>}
            <div className="subcat-delete-actions">
              <button type="button" className="subcat-delete-cancel" onClick={() => setDeleteRowModal(null)}>Отмена</button>
              <button type="button" className="subcat-delete-confirm" onClick={handleDeleteRowClick}>Удалить</button>
            </div>
          </div>
        </div>
      )}
      {isNewsOpen && (
        <NewsSection uiLang={uiLang} t={t} knownWords={newsKnownWords} onClose={() => setIsNewsOpen(false)} />
      )}
      {isBackupModalOpen && (
        <BackupRestoreModal onClose={() => setIsBackupModalOpen(false)} />
      )}
      {sessionExpired&&(
        <>
          <div className="session-expired-banner">
            Пожалуйста, войдите заново — это нужно, чтобы ваши новые слова и изменения сохранялись. <button onClick={()=>{setSessionExpired(false);logoutUser();}}>Войти</button>
          </div>
          <div className="session-blocker" aria-hidden="true" />
        </>
      )}
      <header className="app-header">
        <a
          className="app-brand app-brand-button"
          href="/"
          onClick={reloadFromLogo}
          aria-label="Перезагрузить и вернуться на начальную страницу"
        >
          <img className={"app-brand-logo " + (isLogoSpinning ? "is-spinning" : "")} src="/norsk-logo.png" alt="City 108 logo" data-lesson-target="logo" />
          <div className="app-brand-copy">
            <h1>Norsk Pro</h1>
            <p><span className="slogan-w1">Enkelt</span> · <span className="slogan-w2">Forståelig</span> · <span className="slogan-w3">Pålitelig</span></p>
          </div>
        </a>
        <div className="app-header-actions">
        {(!isLearningMode || !isStudyActive) && (
        <button
          className={isMobileMenuOpen ? 'mobile-menu-toggle is-open' : 'mobile-menu-toggle'}
          type="button"
          onClick={() => setIsMobileMenuOpen((value) => !value)}
          aria-expanded={isMobileMenuOpen}
        >
          <span className="menu-btn-label">Меню</span>
        </button>
        )}
        {isMobileMenuOpen && (
          <nav className="mobile-navigation" aria-label="Мобильное меню">
            <button
              className={activeMobileSection === 'dictionary' && mobileSearchMode && !isLearningMode && !isConstructorOpen ? 'mobile-nav-button is-active' : 'mobile-nav-button'}
              type="button"
              onClick={mobileToggleSearch}
            >
              Поиск
            </button>
            <button
              className={isNewsOpen ? 'mobile-nav-button is-active' : 'mobile-nav-button'}
              type="button"
              onClick={() => { setIsNewsOpen(true); setIsMobileMenuOpen(false); }}
            >
              {t('news')}
            </button>
            <button
              className={isRadioOpen ? 'mobile-nav-button is-active' : 'mobile-nav-button'}
              type="button"
              onClick={() => { setIsRadioOpen(true); setRadioMounted(true); setIsMobileMenuOpen(false); }}
            >
              {t('radio')}
            </button>
            <button
              className={activeMobileSection === 'dictionary' && !mobileSearchMode && !isLearningMode && !isConstructorOpen ? 'mobile-nav-button is-active' : 'mobile-nav-button'}
              type="button"
              onClick={mobileToggleDictionary}
            >
              Словарь
            </button>
            <button
              className={isLearningMode ? 'mobile-nav-button is-active' : 'mobile-nav-button'}
              type="button"
              onClick={openLearningSection}
            >
              Учить слова
            </button>
            <button
              className={activeMobileSection === 'analyzer' && !isLearningMode && !isConstructorOpen ? 'mobile-nav-button is-active' : 'mobile-nav-button'}
              type="button"
              onClick={mobileToggleAnalyzer}
            >
              Анализатор
            </button>
            {CONSTRUCTOR_ENABLED && (
            <button
              className={isConstructorOpen ? 'mobile-nav-button is-active' : 'mobile-nav-button'}
              type="button"
              onClick={openConstructorSection}
            >
              Конструктор
              {availablePhraseCount > 0 && (
                <span className="constructor-notice">{availablePhraseCount}</span>
              )}
            </button>
            )}


          </nav>
        )}
        <div className="database-actions">
          <button
            className={'desktop-only-btn radio-nav-btn' + (isRadioOpen ? ' active-mode' : '')}
            type="button"
            onClick={() => { setRadioMounted(true); setIsRadioOpen((v) => !v); }}
          >
            {t('radio')}
          </button>
          <button
            className={'desktop-only-btn news-nav-btn' + (isNewsOpen ? ' active-mode' : '')}
            type="button"
            onClick={() => setIsNewsOpen(true)}
          >
            {t('news')}
          </button>
          <button
            className={'desktop-only-btn' + (deskDict ? ' active-mode' : '')}
            type="button"
            onClick={toggleDictionarySection}
          >
            Словарь
          </button>
          <button
            className={isLearningMode ? 'active-mode' : ''}
            type="button"
            onClick={() => {
              setIsLearningMode((value) => {
                const nextValue = !value;
                setActiveMobileSection(nextValue ? 'learning' : 'dictionary');
                return nextValue;
              });
              setIsConstructorOpen(false);
            }}
          >
            Учить слова
          </button>
          <button
            className={'desktop-only-btn' + (deskAnalyzer ? ' active-mode' : '')}
            type="button"
            onClick={toggleAnalyzerSection}
          >
            Анализатор
          </button>
          {CONSTRUCTOR_ENABLED && (
          <button
            className={isConstructorOpen ? 'active-mode' : ''}
            type="button"
            onClick={() => {
              setIsConstructorOpen((value) => {
                const nextValue = !value;
                setActiveMobileSection(nextValue ? 'constructor' : 'dictionary');
                return nextValue;
              });
              setIsLearningMode(false);
            }}
          >
            Конструктор
            {availablePhraseCount > 0 && (
              <span className="constructor-notice">{availablePhraseCount}</span>
            )}
          </button>
          )}


        </div>
          <div className="user-profile-wrap">
            <button
              className={'user-profile-badge' + (isProfileStatsOpen ? ' is-open' : '')}
              type="button"
              data-lesson-target="avatar"
              title={userProfile.email}
              onClick={() => setIsProfileStatsOpen((value) => !value)}
            >
              <div className="user-profile-avatar">
                {userProfile.photo?.dataUrl ? (
                  <img src={userProfile.photo.dataUrl} alt={userProfile.nickname} />
                ) : (
                  <span>{getProfileInitial(userProfile)}</span>
                )}
              </div>
              <span>{userProfile.nickname}</span>
            </button>
            {isProfileStatsOpen && (
              <ProfilePopover
                userProfile={userProfile}
                profileStats={{ ...profileStats, ...subscriptionStats }}
                authStatus={authStatus}
                moderationTarget={moderationTarget}
                setModerationTarget={setModerationTarget}
                blockUserByIdentifier={blockUserByIdentifier}
                unblockUserByIdentifier={unblockUserByIdentifier}
                addDefaultSkeleton={addDefaultSkeleton}
                requestConfirm={requestConfirm}
                changeProfileAvatar={changeProfileAvatar}
                onOpenVoiceSetup={() => { setVoiceSetupOpen(true); loadVoiceStatus(); }}
                logoutUser={logoutUser}
                deleteUserAccount={deleteUserAccount}
                supabase={supabase}
                onlineUserCount={onlineUserCount}
                onDownloadDatabase={downloadDatabase}
                onImportDatabase={importDatabase}
                onOpenBackupRestore={() => setIsBackupModalOpen(true)}
                onDownloadAllWords={downloadAllWords}
                lessonsCatalog={LESSONS}
                onStartLesson={(lessonId) => { setActiveLesson(lessonId); setIsProfileStatsOpen(false); }}
                forceOpenSection={forceOpenSection}
                onClose={() => setIsProfileStatsOpen(false)}
                theme={theme}
                onToggleTheme={toggleTheme}
              />
            )}
          </div>
        </div>
      </header>

      {radioMounted && (
        <RadioSection t={t} open={isRadioOpen} onClose={() => setIsRadioOpen(false)} onCreateFromOrder={createSongFromOrder} onRecognize={recognizeWordForRadio} onOpenWord={(song) => { if (!song || !song.word) return; setIsRadioOpen(false); setPublicPage({ wordNorm: String(song.word).toLowerCase(), wordText: song.disp || song.word, wordPos: song.pos, openedAt: Date.now() }); }} />
      )}

      <section className={'database-search' + (!isDictionarySectionActive && !isAnalyzerSectionActive ? ' mobile-section-hidden' : '')} aria-label="Поиск по базе слов">
        <label htmlFor="database-search-input">Поиск по базе</label>
        <div className="database-search-box">
          <input
            id="database-search-input"
            type="search"
            autoComplete="off"
            value={databaseSearch || ''}
            onChange={(event) => setDatabaseSearch(event.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 160)}
            placeholder="Введите слово..."
          />
          {userProfile.id && (
            <button
              type="button"
              className="evik-search-btn"
              title="Сгенерировать карточку с ИИ Evik"
              disabled={aiBtnBusy}
              onClick={() => { if (evikLongRef.current) { evikLongRef.current = false; return; } evikStart(databaseSearch); }}
              onTouchStart={() => {
                if (databaseSearch.trim()) return;
                evikLongRef.current = false;
                if (evikTimerRef.current) clearTimeout(evikTimerRef.current);
                evikTimerRef.current = setTimeout(() => { evikLongRef.current = true; setEvikMenuOpen(true); }, 500);
              }}
              onTouchEnd={() => { if (evikTimerRef.current) { clearTimeout(evikTimerRef.current); evikTimerRef.current = null; } }}
              onTouchMove={() => { if (evikTimerRef.current) { clearTimeout(evikTimerRef.current); evikTimerRef.current = null; } }}
              onTouchCancel={() => { if (evikTimerRef.current) { clearTimeout(evikTimerRef.current); evikTimerRef.current = null; } }}
            >Evik</button>
          )}
          {userProfile.id && (
            <button type="button" className="evik-photo-btn" title="Распознать слова с фото" aria-label="Камера" disabled={aiBtnBusy} onClick={() => setPhotoModePick(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
            </button>
          )}
          {userProfile.id && (
            <button type="button" className="evik-photo-btn evik-mic-btn" title="Записать и разобрать разговор" aria-label="Микрофон" disabled={aiBtnBusy} onClick={() => setShowRecorder(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            </button>
          )}
          <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhotoFile} />
          {evikMenuOpen && (
            <>
              <div className="evik-menu-backdrop" onClick={() => setEvikMenuOpen(false)} />
              <div className="evik-menu" role="menu">
                <button type="button" className="evik-menu-item" onClick={() => { setEvikMenuOpen(false); setPhotoModePick(true); }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  <span>Камера</span>
                </button>
                <button type="button" className="evik-menu-item" onClick={() => { setEvikMenuOpen(false); setShowRecorder(true); }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                  <span>Микрофон</span>
                </button>
              </div>
            </>
          )}
        </div>
        {userProfile?.role === 'moderator' && (
          <div className="ratings-bar">
            <button type="button" className="ratings-toggle" onClick={() => { setShowRatings((v) => !v); loadWordRatings(); }}>Топ слов</button>
          </div>
        )}
        {showRatings && (
          <div className="ratings-panel">
            {wordRatings.length === 0 ? (<p className="ratings-empty">Пока нет данных</p>) : (
              <ol className="ratings-list">
                {wordRatings.slice(0, 20).map((row) => (
                  <li key={row.word}>
                    <button type="button" className="ratings-word" onClick={() => { setDatabaseSearch(row.word); setShowRatings(false); }}>{row.word}</button>
                    <span className="ratings-count">{row.count}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
        {searchFocused && !databaseSearch.trim() && searchHistory.length > 0 && (
          <div className="database-search-results search-history-box">
            <div className="search-history-title">Недавние поиски</div>
            {searchHistory.map((hw, hi) => (
              <button key={hi} type="button" className="search-history-item" onMouseDown={(e) => { e.preventDefault(); setDatabaseSearch(hw); }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span>{hw}</span>
              </button>
            ))}
          </div>
        )}
        {databaseSearch.trim() && (
          <div className="database-search-results">
            {databaseSearchResults.filter((result) => {
                if (!result.authored || !userProfile.id) return true;
                const wn = String(result.word || '').trim().toLowerCase().replace(/^(en|ei|et|\u00e5)\s+/i, '');
                const minePublic = dbPublicExtra.some((e) => String(e.word_norm || '').toLowerCase() === wn && Array.isArray(e.authors) && e.authors.some((a) => a && a.owner_id === userProfile.id));
                return !minePublic;
              }).map((result) => (
                <button
                  key={`${result.category}-${result.subcategory}-${result.word}`}
                  type="button"
                  className={result.hasExactOwnRoot ? 'search-root-match' : undefined}
                  onClick={() => openSearchResult(result)}
                  onContextMenu={(e) => { e.preventDefault(); openSearchResultMenu(result, e.clientX, e.clientY); }}
                >
                  <strong>{result.word}</strong>
                  {result.matchedTranslation && <span>{result.matchedTranslation}</span>}
                  <em>{result.pathLabel}</em>
                  {result.authored && (
                    <span className="search-result-globe" role="button" title="Открыть публичную страницу" onClick={(e) => { e.stopPropagation(); setPublicPage({ wordNorm: String(result.word || '').trim().toLowerCase(), wordText: result.word, openedAt: Date.now() }); }}>🌍</span>
                  )}
                </button>
              ))}
              {dbPublicExtra.map((r) => {
                const rowKey = String(r.word_norm || '') + '|' + String(r.pos || 'noun');
                return (
                <div key={`pub-${rowKey}`} className={"search-result-public" + (evikHighlight && evikHighlight === String(r.word_norm || "").toLowerCase() ? " evik-flash" : "")}>
                  <strong role="button" className="pub-word-clickable" title="Нажмите, чтобы добавить слово в свой словарь" onClick={() => setPubMenuWord(pubMenuWord === rowKey ? null : rowKey)}>{r.word_text}{posRu(r.pos) ? <span className="pub-pos-tag">{posRu(r.pos)}</span> : null}</strong>
                  {Array.isArray(r.authors) && r.authors.length > 0 && (
                    <span className="pub-authors">
                      {r.authors.map((a, ai) => {
                        const an = String(a.owner_nick || '?').trim();
                        const aEvik = an.toLowerCase() === 'evik';
                        return (
                          <span key={(a.owner_id || an) + '-' + ai} role="button" className={'pub-author' + (aEvik ? ' evik' : '')} title={'Открыть версию: ' + an} onClick={(e) => { e.stopPropagation(); setPubMenuWord(null); setPublicPage({ wordNorm: r.word_norm, wordText: a.word_text || r.word_text, openedAt: Date.now(), preferOwnerId: a.owner_id }); }}>
                            {aEvik ? <img className="pub-author-ava" src="/evik-avatar.jpg" alt="" /> : (a.avatar ? <img className="pub-author-ava" src={a.avatar} alt="" /> : <span className="pub-author-ava pub-author-initial">{an.charAt(0).toUpperCase()}</span>)}
                            {an}
                          </span>
                        );
                      })}
                    </span>
                  )}
                  <span className="search-result-globe" role="button" title="Открыть публичную страницу" onClick={() => setPublicPage({ wordNorm: r.word_norm, wordText: r.word_text, wordPos: r.pos, openedAt: Date.now() })}>🌍</span>
                  {pubMenuWord === rowKey && (
                    <div className="pub-word-menu" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="pub-word-menu-item" onClick={() => addPublicWord(r.word_norm, r.word_text, r.pos)}>Добавить в словарь</button>
                      <button type="button" className="pub-word-menu-item" onClick={() => { setPubMenuWord(null); setPublicPage({ wordNorm: r.word_norm, wordText: r.word_text, wordPos: r.pos, openedAt: Date.now() }); }}>Открыть страницу</button>
                    </div>
                  )}
                </div>
                );
              })}
              {databaseSearchResults.length === 0 && dbPublicExtra.length === 0 && !/[\u0400-\u04FF]/.test(databaseSearch) && (
                <p>{t('notFoundAuth').replace('{word}', databaseSearch.trim())}</p>
              )}
          </div>
        )}
      </section>
      {showRecorder && (
        <DialogRecorder
          userProfile={userProfile}
          onClose={() => setShowRecorder(false)}
          onAnalyze={analyzeDialog}
        />
      )}
      {voiceSetupOpen && (
        <div className="song-modal" onClick={() => setVoiceSetupOpen(false)}>
          <div className="song-modal-card rec-card" onClick={(e) => e.stopPropagation()}>
            <div className="song-modal-title">Определить голос</div>
            <p className="rec-hint">Запишите короткий образец своего голоса (2–3 предложения). ИИ будет узнавать вас в разборах разговоров и подписывать ваши реплики именем «{userProfile.nickname}».</p>
            <p className="rec-status">{voiceStatus.has ? '✓ Образец записан — голос будет узнаваться в разборах.' : 'Образец ещё не записан.'}</p>
            <div className="song-modal-actions">
              <button type="button" className="song-modal-cancel" onClick={() => setVoiceSetupOpen(false)}>Отмена</button>
              {voiceStatus.has && (
                <button type="button" className="btn-std song-modal-file" onClick={() => { deleteVoiceSample(); }}>Удалить</button>
              )}
              <button type="button" className="btn-std song-modal-go" onClick={() => { setVoiceSetupOpen(false); setShowVoiceRecorder(true); }}>{voiceStatus.has ? 'Перезаписать' : 'Записать'}</button>
            </div>
          </div>
        </div>
      )}
      {showVoiceRecorder && (
        <DialogRecorder
          userProfile={userProfile}
          variant="voice"
          onClose={() => setShowVoiceRecorder(false)}
          onAnalyze={saveVoiceSample}
        />
      )}
      {dialogBusy && (
        <div className="song-modal">
          <div className="song-modal-card rec-card">
            <div className="song-modal-title">Разбираю запись…</div>
            <div className="rec-live"><span className="rec-dot" /><span className="rec-hint">ИИ слушает и анализирует разговор. Это может занять до минуты.</span></div>
          </div>
        </div>
      )}
      {dialogResult && (() => {
        const parsed = parseDialog(dialogResult.analysis);
        return (
        <div className="song-modal" onClick={() => setDialogResult(null)}>
          <div className="song-modal-card dialog-result-card" onClick={(e) => e.stopPropagation()}>
            <div className="song-modal-title">Разбор разговора</div>
            <div className="dialog-result-scroll">
              {parsed.transcript && (
                <div className="dialog-sec">
                  <div className="dialog-sec-h">📝 Транскрипт</div>
                  <div className="dialog-sec-b">{parsed.transcript}</div>
                </div>
              )}
              {parsed.razbor && (
                <div className="dialog-sec">
                  <div className="dialog-sec-h">🔍 Разбор</div>
                  <div className="dialog-sec-b">{parsed.razbor}</div>
                </div>
              )}
              {parsed.sovety && (
                <div className="dialog-sec">
                  <div className="dialog-sec-h">💡 Советы</div>
                  <div className="dialog-sec-b">{parsed.sovety}</div>
                </div>
              )}
            </div>
            <div className="song-modal-actions dialog-result-actions">
              {parsed.transcript && (
                <button type="button" className="btn-std" onClick={() => openTranscriptInAnalyzer(parsed.transcript)}>Открыть в анализаторе</button>
              )}
              <button type="button" className="btn-std song-modal-go" onClick={() => setDialogResult(null)}>Закрыть</button>
            </div>
          </div>
        </div>
        );
      })()}
      {photoModePick && (
        <div className="subcat-delete-overlay" onClick={() => setPhotoModePick(false)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Распознать с фото</p>
            <p className="subcat-delete-sub">Что показать в анализаторе?</p>
            <div className="photo-mode-buttons">
              <button type="button" className="photo-mode-btn" onClick={() => startPhoto('objects')}>
                <strong>Предметы</strong>
                <span>назвать предметы на фото — списком слов</span>
              </button>
              <button type="button" className="photo-mode-btn" onClick={() => startPhoto('text')}>
                <strong>Текст на фото</strong>
                <span>прочитать норвежский текст с фото</span>
              </button>
            </div>
            <div className="subcat-delete-actions">
              <button type="button" className="subcat-delete-cancel" onClick={() => setPhotoModePick(false)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
      {evik && (
        <div className="subcat-delete-overlay" onClick={evikClose}>
          <div className="subcat-delete-card evik-modal-card" onClick={(e) => e.stopPropagation()}>
            {evik.stage === 'loading' && (
              <>
                <p className="subcat-delete-title">Evik</p>
                <p className="subcat-delete-sub">{evik.message || 'Подождите…'}</p>
                <div className="evik-spinner" />
              </>
            )}
            {evik.stage === 'choose' && (() => {
              const corDiff = evik.corrected && evik.corrected.trim().toLowerCase() !== String(evik.word || '').trim().toLowerCase();
              const POS_RU = { noun: 'существительное', verb: 'глагол', adjective: 'прилагательное', adverb: 'наречие' };
              const hasVariants = evik.variants && evik.variants.length > 0;
              const title = !hasVariants
                ? (evik.noPos ? ('«' + (evik.corrected || evik.word) + '» — нет варианта: ' + (POS_RU[evik.noPos] || evik.noPos)) : ('«' + evik.word + '» — не нашёл такого слова'))
                : (corDiff ? 'Возможно, вы имели в виду:' : (evik.variants.length > 1 ? ('«' + (evik.corrected || evik.word) + '» — какое значение?') : 'Подтвердите слово или выберите часть речи:'));
              return (
                <>
                  <p className="subcat-delete-title">{title}</p>
                  <p className="subcat-delete-sub">{hasVariants ? (corDiff ? 'Evik поправил написание. Выберите вариант или другую часть речи.' : 'Выберите вариант, чтобы Evik сделал карточку, или другую часть речи.') : 'Можно выбрать часть речи ниже или закрыть.'}</p>
                  {hasVariants && (
                    <div className="evik-variants">
                      {evik.variants.map((v, i) => (
                        <button key={i} type="button" className="evik-variant" onClick={() => evikFetchCard(v.word, v.pos)}>
                          <strong>{v.word}</strong>
                          <span>{v.pos_ru || v.pos}{v.gender ? ' · ' + v.gender : ''}</span>
                          {v.short_meaning && <em>{v.short_meaning}</em>}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="evik-pos-picker">
                    <span className="evik-pos-label">Другая часть речи:</span>
                    <div className="evik-pos-buttons">
                      {['noun', 'verb', 'adjective', 'adverb'].map((pp) => (
                        <button key={pp} type="button" className="evik-pos-btn" onClick={() => evikForcePos(pp)}>{POS_RU[pp]}</button>
                      ))}
                    </div>
                  </div>
                  <div className="subcat-delete-actions">
                    <button type="button" className="subcat-delete-cancel" onClick={evikClose}>Отмена</button>
                  </div>
                </>
              );
            })()}

            {evik.stage === 'info' && (
              <>
                <p className="subcat-delete-title">Готово</p>
                <p className="subcat-delete-sub">{evik.message || 'Готово'}</p>
                <div className="subcat-delete-actions">
                  <button type="button" className="subcat-delete-cancel" onClick={evikClose}>Закрыть</button>
                </div>
              </>
            )}
            {evik.stage === 'error' && (
              <>
                <p className="subcat-delete-title">Evik</p>
                <p className="subcat-delete-sub">{evik.message || 'Что-то пошло не так'}</p>
                <div className="subcat-delete-actions">
                  {evik.retry && <button type="button" className="subcat-delete-cancel evik-retry-btn" onClick={() => { const f = evik.retry; if (typeof f === 'function') f(); }}>Повторить</button>}
                  <button type="button" className="subcat-delete-cancel" onClick={evikClose}>Закрыть</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}


      {isLearningMode && (
        <WordLearningMode
          db={db}
          categoryColors={categoryColors}
          savedTexts={exampleTexts}
          learningProgress={learningProgress}
          deckSubcategories={deckSubcategories}
          onChangeDeckSubcategories={setDeckSubcategories}
          analyzerDeckCategory={ANALYZER_DECK_CATEGORY}
          onDeleteAnalyzerDeck={deleteAnalyzerDeck}
          onLearningProgressChange={setLearningProgress}
          onStudyActiveChange={setIsStudyActive}
          onClose={() => { setIsLearningMode(false); setIsStudyActive(false); }}
          onResolveNounGender={async (t) => { try { const d = await fetchOfficialNounForms(t); return d && d.gender ? d.gender : null; } catch (_) { return null; } }}
          onPersistNounGender={persistNounGender}
        />
      )}

      {CONSTRUCTOR_ENABLED && isConstructorOpen && isComposingPhrase && (
      <SentenceBar
        key={activePhraseGame?.fullText || 'local-constructor'}
        sentence={constructorSentence}
        categoryColors={categoryColorByRole}
        warning={sentenceWarning}
        canCancel={canCancelSentence}
        onCancel={cancelLastSentenceAction}
      />
      )}

      {CONSTRUCTOR_ENABLED && isConstructorOpen && (
        <section className="phrase-games-panel" aria-label="Совместные фразы">
          <div className="phrase-games-header">
            <div>
              <p>Выбери слово, сделай ход, жди другого</p>
            </div>
            <div className="phrase-games-toolbar">
              <button
                className={!activePhraseGame ? 'new-phrase-button is-active' : 'new-phrase-button'}
                type="button"
                onClick={() => {
                  setIsFinishMenuOpen(false);
                  startNewPhraseDraft();
                }}
              >
                Новая фраза
              </button>
              <button className="check-moves-button" type="button" onClick={loadPhraseGames}>Проверить ходы</button>
              {activePhraseGame?.author_id === userProfile.id && (
                <div className="finish-phrase-control">
                  <button
                    className="finish-phrase-button"
                    type="button"
                    onClick={() => setIsFinishMenuOpen((value) => !value)}
                  >
                    Завершить
                  </button>
                  {isFinishMenuOpen && (
                    <div className="finish-options-menu" role="menu" aria-label="Выберите знак завершения">
                      {[
                        ['.', 'Точка'],
                        ['...', 'Троеточие'],
                        ['!', 'Восклицание'],
                        ['?', 'Вопрос'],
                        ['?!', 'Риторический вопрос'],
                      ].map(([ending, label]) => (
                        <button
                          key={ending}
                          type="button"
                          onClick={() => finishPhraseGame(ending)}
                        >
                          <span>{label}</span>
                          <b>{ending}</b>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              </div>
          </div>
          {phraseGamesError ? (
            <p className="phrase-games-message">{phraseGamesError}</p>
          ) : phraseGameRows.length > 0 ? (
            <div className="phrase-game-list">
              {phraseGameRows.map((game) => (
                <article
                  className={`phrase-game-card ${game.id === activePhraseGameId ? 'is-selected' : ''}`}
                  key={game.id}
                >
                  <span className={`phrase-status-dot ${game.canMove ? 'is-open' : 'is-locked'}`} />
                  <div>
                    <strong>{formatPhraseDisplayText(game.fullText || game.startText)}</strong>
                    <span>
                      Автор: <b className="phrase-author-name">{game.authorName}</b>
                      {` · ходов: ${game.moveCount}`}
                      {game.lastMoveAuthorName && (
                        <> · пх: <b className="phrase-lastmove-name">{game.lastMoveAuthorName}</b></>
                      )}
                    </span>
                  </div>
                  <div className="phrase-game-actions">
                    <b>{game.moveStatusText}</b>
                    {game.id !== activePhraseGameId && (
                    <button
                      type="button"
                      onClick={() => {
                        setActivePhraseGameId(game.id);
                        setIsWatchingPhrase(!game.canMove);
                        setIsComposingPhrase(true);
                        setIsFinishMenuOpen(false);
                        setPhraseGamesError('');
                        setSentenceFromPhraseMoves(game.moves || []);
                      }}
                    >
                      {game.id === activePhraseGameId ? 'Открыта' : game.canMove ? 'Выбрать' : 'Смотреть'}
                    </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : null}
          {finishedPhraseTexts.length > 0 && (
            <button
              type="button"
              className={isFinishedPhrasesOpen ? 'finished-phrases-link is-active' : 'finished-phrases-link'}
              onClick={() => setIsFinishedPhrasesOpen((value) => !value)}
            >
              Завершённые фразы
            </button>
          )}
          {isFinishedPhrasesOpen && finishedPhraseTexts.length > 0 && (
            <section className="finished-phrases-panel" aria-label="Завершённые фразы">
              <div className="finished-phrases-list">
                {finishedPhraseTexts.map((phrase) => (
                  <article className="finished-phrase-card" key={phrase.gameId}>
                    <div>
                      <span className="finished-phrase-number">
                        {phrase.id.replace('phrase ', '')}
                      </span>
                      <strong>{phrase.text}</strong>
                    </div>
                    <p>
                      Автор: {phrase.authorName}
                      {` · участников: ${phrase.participants.join(', ') || 'нет'}`}
                      {` · ходов: ${phrase.moveCount}`}
                    </p>
                  </article>
                ))}
              </div>
            </section>
          )}
        </section>
      )}
      {((isDictionarySectionActive && !mobileSearchMode && !(databaseSearch && databaseSearch.trim())) || (isConstructorOpen && isComposingPhrase)) && (
        <button
          className={isPartsMenuOpen ? 'parts-menu-toggle is-open' : 'parts-menu-toggle'}
          type="button"
          onClick={() => setIsPartsMenuOpen((value) => !value)}
          aria-expanded={isPartsMenuOpen}
        >
          Части речи
        </button>
      )}

      <div className={[
        isConstructorOpen ? 'constructor-category-controls' : 'dictionary-category-controls',
        isConstructorOpen && !isComposingPhrase ? 'mobile-section-hidden desktop-hidden' : '',
        !isConstructorOpen && !isDictionarySectionActive ? 'mobile-section-hidden' : '',
        !isConstructorOpen && !deskDict ? 'desktop-hidden' : '',
        isPartsMenuOpen ? 'is-open' : '',
      ].filter(Boolean).join(' ')}>
      <CategoryControls
        roles={orderedCategories}
        roleLabels={{}}
        categoryColors={categoryColorByRole}
        sectionTargets={isConstructorOpen ? [] : sectionTargets}
        onSelectCategory={(category) => {
          setIsAddingCategory(false);
          setIsMobileMenuOpen(false);
          setActiveCategory(category);
          setActiveSubcategory(null);
          setIsPartsMenuOpen(false);
        }}
        allowAddCategory={!isConstructorOpen}
        onToggleNewCategory={() => setIsAddingCategory((value) => !value)}
        onChangeCategoryColor={isConstructorOpen ? null : changeCategoryColor}
        categoryTextColors={categoryTextColorByRole}
        categoryTextThemes={categoryTextThemeByRole}
        onChangeCategoryTextTheme={isConstructorOpen ? null : setCategoryTextTheme}
        onRenameCategory={isConstructorOpen ? renameConstructorGroup : renameCategory}
        onDeleteCategory={isConstructorOpen ? deleteConstructorGroup : deleteCategory}
        onMarkCategoryAsDeck={isConstructorOpen ? null : markCategoryAsDeck}
        onMoveOrCopyCategory={isConstructorOpen ? () => {} : copyOrMoveCategorySection}
        onReorderCategories={isConstructorOpen ? reorderConstructorGroups : reorderCategories}
        allowColorChange={!isConstructorOpen}
        allowSectionTransfer={!isConstructorOpen}
        isSkeletonCategory={isSkeletonCategory}
        isCategoryDuplicate={isCategoryDuplicate}
      />
      </div>

      {isAddingCategory && isConstructorOpen && (
        <form
          className="new-category-form constructor-group-form"
          onSubmit={(event) => {
            event.preventDefault();
            addConstructorGroup();
          }}
        >
          <input
            value={constructorGroupDraft.label}
            onChange={(event) => setConstructorGroupDraft((draft) => ({
              ...draft,
              label: event.target.value,
            }))}
            placeholder="Название в конструкторе..."
            autoFocus
          />
          <select
            value={constructorGroupDraft.sourceKey}
            onChange={(event) => setConstructorGroupDraft((draft) => ({
              ...draft,
              sourceKey: event.target.value,
            }))}
          >
            <option value="">Источник из базы...</option>
            {constructorSourceTargets.map((target) => {
              const key = `${target.category}${PATH_SEPARATOR}${target.path}`;
              return (
                <option key={key} value={key}>
                  {target.label}
                </option>
              );
            })}
          </select>
          <button className="secondary-button" type="submit">
            Создать
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={() => {
              setConstructorGroupDraft({ label: '', sourceKey: '' });
              setIsAddingCategory(false);
            }}
          >
            Отмена
          </button>
        </form>
      )}

      {isAddingCategory && !isConstructorOpen && (
        <NewCategoryForm
          onAddCategory={addMainCategory}
          onCancel={() => setIsAddingCategory(false)}
        />
      )}

      <div className={[
        !isConstructorOpen && !isDictionarySectionActive ? 'mobile-section-hidden' : '',
        !isConstructorOpen && !deskDict ? 'desktop-hidden' : '',
      ].filter(Boolean).join(' ')}>
        <CategoryPanel
          activeCategory={activeCategory}
          activeSubcategory={activeSubcategory}
          db={db}
          categoryColors={categoryColors}
          categoryMeta={categoryMeta}
          description={
            categoryMeta[
              activeSubcategory
                ? getSubcategoryMetaKey(activeCategory, activeSubcategory)
                : getCategoryMetaKey(activeCategory)
            ]?.description || ''
          }
          grammarIconCatalog={grammarIconCatalog}
          grammarIconParts={grammarIconParts}
          savedTexts={exampleTexts}
          searchHighlight={searchHighlight}
          newWord={newWord}
          newWordCapitalized={newWordCapitalized}
          onBackToSubcategories={() => setActiveSubcategory(null)}
          onClose={() => setActiveCategory(null)}
          onSelectSubcategory={setActiveSubcategory}
          onRenameSubcategory={renameSubcategory}
          onDeleteSubcategory={deleteSubcategory}
          onOpenAddRowModal={(subPath) => setAddRowModal({ subcategoryPath: subPath, draft: "" })}
          onOpenRenameRowModal={(subPath, rowName) => setRenameRowModal({ subcategoryPath: subPath, oldName: rowName, draft: rowName, error: "" })}
          activeSubcategoryRowsMeta={activeCategory ? getSubcategoryRowsMeta(activeCategory, activeSubcategory || '') : { rows: [], rowOf: {} }}
          onMoveSubcategoryRow={moveSubcategoryRow}
          onSetWordRow={setWordRow}
          onOpenWordLinkModal={(wordIndex, current) => setWordLinkModal({ wordIndex, draft: current || "", error: "", hadLink: !!current })}
          onOpenWordStoryModal={(wordIndex, current) => setStoryModal({ wordIndex, draft: current || "", had: !!current })}
          getWordTexts={getWordTexts}
          onOpenStoryInAnalyzer={openWordStoryInAnalyzer}
          onRenameSubcategoryRow={renameSubcategoryRow}
          onAssignSubcategoryRow={(childName, rowName) => setSubcategoryRow(activeSubcategory || "", childName, rowName)}
          requestConfirm={requestConfirm}
          onOpenDeleteRowModal={(subPath) => setDeleteRowModal({ subcategoryPath: subPath, selected: "" })}
          onMarkSubcategoryAsDeck={markSubcategoryAsDeck}
          onChangeCategoryColor={isConstructorOpen ? null : changeCategoryColor}
          onRenameCategory={isConstructorOpen ? renameConstructorGroup : renameCategory}
          onDeleteCategory={isConstructorOpen ? deleteConstructorGroup : deleteCategory}
        onMarkCategoryAsDeck={isConstructorOpen ? null : markCategoryAsDeck}
          onPinSubcategoryToConstructor={isConstructorOpen ? pinSubcategoryToConstructor : null}
          sectionTargets={sectionTargets}
          onMoveOrCopySubcategory={copyOrMoveSubcategorySection}
          onSetSubcategoryIcon={setSubcategoryIcon}
          onSetDescription={setActivePanelDescription}
          onReorderSubcategories={reorderSubcategories}
          onSelectWord={isConstructorOpen ? selectWord : () => {}}
          onSelectWordForm={isConstructorOpen ? selectWordForm : () => {}}
          onDeleteWord={deleteWord}
          onChangeWordLevel={changeWordLevel}
          onSetLevelShared={setWordLevelShared}
          onSetWordGender={setWordGender}
          onRequestGenderBackfill={requestGenderBackfill}
          onRequestFormsBackfill={requestFormsBackfill}
          onReorderWords={reorderWords}
          onAddTranslation={addWordTranslation}
          onDeleteTranslation={deleteWordTranslation}
          onAddRelation={addWordRelation}
          onDeleteRelation={deleteWordRelation}
          onSetPhoto={setWordPhoto}
          onSetWordPhrase={setWordPhrase}
          onOpenOrdskjonnhet={openOrdskjonnhet}
          onViewPublic={(word) => setPublicPage({ wordNorm: String((word && word.text) || "").trim().toLowerCase(), wordText: (word && word.text) || "", openedAt: Date.now(), preferOwnerId: userProfile.id })}
          isConstructorOpen={isConstructorOpen}
          phraseDatabases={phraseDatabasesForPanel}
          foreignPhotosCache={foreignPhotosCache}
          onLoadForeignPhoto={loadForeignPhoto}
          ownPublicThumbs={ownPublicThumbs}
          mySubscriptions={mySubscriptions}
          myUserId={userProfile.id}
          onToggleSubscription={toggleSubscription}
          onToggleSubscriptionStar={toggleSubscriptionStar}
          onSetWordForms={setWordForms}
          onSetWordRoot={setWordRoot}
          onDeleteWordRoot={deleteWordRoot}
          onMoveOrCopyWord={moveOrCopyWord}
          onEditWordText={editWordText}
          onOpenEditWordModal={(wordIndex, current) => setEditWordModal({ wordIndex, draft: current || "" })}
          onRequestPhoto={(key) => loadOnePhotoLazy(key, userProfile.id)}
          currentCategoryType={getTypeForCategory(activeCategory)}
          isPhraseContext={getInferredCanonicalCategoryId(activeCategory) === 'Frase'}
          categoryOrder={categoryOrder}
          onNewWordChange={setNewWord}
          onNewWordCapitalizedChange={setNewWordCapitalized}
          onAddNode={addNode}
          onAddSubcategory={addSubcategory}
        />
      </div>

      <div id="analyzer-section-anchor" className={[
        isAnalyzerSectionActive ? 'mobile-analyzer-section is-active' : 'mobile-analyzer-section',
        !deskAnalyzer ? 'desktop-hidden' : '',
      ].filter(Boolean).join(' ')}>
        <TextAnalyzer
          text={analyzerText}
          maxChars={analyzerMaxChars}
          analyzerMap={analyzerMap}
          analyzerRootEntries={analyzerRootEntries}
          analyzerMaxPhraseLength={analyzerMaxPhraseLength}
          savedTexts={analysisLibrary.texts}
          nextSaveNumber={nextAnalysisTextNumber}
          onTextChange={setAnalyzerText}
          onSaveText={saveAnalysisText}
          canListenText={['admin', 'moderator'].includes(userProfile?.role)}
          onOpenInDictionary={(r) => openSearchResult(r)}
          onShowEvikWord={showEvikWordUnderSearch}
          onCreateDeckFromText={createDeckFromAnalyzerText}
          analyzerAliases={analyzerAliases}
          onSetAlias={setAnalyzerAlias}
          onRemoveAlias={removeAnalyzerAlias}
        />
      </div>

      <footer className="app-footer">Design by City_108</footer>
    </main>
  );
}
